import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'
import { useProducts } from '../context/ProductsContext'
import MobileHome from '../components/MobileHome'
import StepsCard from '../components/StepsCard'
import SleepCard from '../components/SleepCard'
import { syncReminderNotifications, initReminderNotificationTap, consumeOpenRemindersFlag, speakReminder, voiceLineForNow, isVoiceOn, setVoiceOn, getReminderFreq, setReminderFreq, checkExactAlarms, requestExactAlarms, reminderDebugInfo, ensureNotifPermission, sendTestNotification } from '../utils/reminderNotifications'
import { Capacitor as _Cap2 } from '@capacitor/core'
import { AlertTriangle } from 'lucide-react'
import { fetchSubscriptionStatus, subscribePremium } from '../utils/subscription'
import { photoUrl } from '../utils/photoUrl'

let _Capacitor = null
try { _Capacitor = require('@capacitor/core').Capacitor } catch (e) {}
const isNativeApp = () => !!(_Capacitor && _Capacitor.isNativePlatform && _Capacitor.isNativePlatform())

const MED_STOP_WORDS = new Set(['face','skin','hair','body','nail','scalp','acid','salt','base','plus','with','from','each','both','also','this','that','type','care','anti','free','rich','mild','deep','soft','pure','oral','eyes','once','only','used','acts','help','keep','give','feel','stay'])
function medKeywords(medName) {
  return medName.toLowerCase()
    .replace(/[\d.]+\s*(%|mg|ml|mcg|g\b|w\/v|w\/w)/gi, '')
    .replace(/\b(tab|tablet|cap|capsule|gel|cream|lotion|serum|wash|solution|spray|ointment|foam|drops?|syrup|liquid|topical|oral|mist|lacquer|patch)\b/gi, '')
    .replace(/[()[\]]/g, ' ')
    .split(/[\s+&,/]+/)
    .map(w => w.trim())
    .filter(w => w.length >= 6 && !MED_STOP_WORDS.has(w))
}
function matchesMedication(product, keywords) {
  if (!keywords.length) return false
  const haystack = (product.name + ' ' + (product.desc || '') + ' ' + (product.tags || []).join(' ')).toLowerCase()
  return keywords.some(kw => haystack.includes(kw))
}

const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42',
  g500:'#3D8A57', g400:'#5AAE73', g300:'#85C99A', g100:'#E8F5EE', g50:'#F2FAF5',
  white:'#FFFFFF', text:'#1A2820', muted:'#5A7065', border:'#C8DDD2', error:'#C0392B',
  headerGrad:'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)',
}
const STATUS_COLORS = {
  pending:   { bg:'#FEF9F0', color:'#B7770D', border:'#FCEDC7' },
  confirmed: { bg:'#EBF8F0', color:'#1A6B3C', border:'#A3DFB8' },
  completed: { bg:'#EDF2FF', color:'#3451B2', border:'#BAC8FF' },
  cancelled: { bg:'#FFF0F0', color:'#C92A2A', border:'#FFC9C9' },
}

const WINDOWS = {
  morning:   { openH:9,  openM:0,  closeH:12, closeM:0,  missedH:12, missedM:0,  nextDay:false },
  afternoon: { openH:13, openM:0,  closeH:15, closeM:0,  missedH:16, missedM:0,  nextDay:false },
  night:     { openH:21, openM:0,  closeH:23, closeM:59, missedH:0,  missedM:1,  nextDay:true  },
}

const pad = n => String(n).padStart(2,'0')
const todayISO = () => { const d=new Date(); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}` }
const addDays = (iso,n) => { const d=new Date(iso+'T00:00:00'); d.setDate(d.getDate()+n); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}` }
const fmtDate = d => {
  if (!d) return '—'
  try { const s=typeof d==='string'?(d.includes('T')?d.split('T')[0]:d.split(' ')[0]):new Date(d).toISOString().slice(0,10); const [y,m,day]=s.split('-'); return `${day}/${m}/${y}` }
  catch { return String(d) }
}
const _MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const fmtNice = d => {
  if (!d) return '—'
  try { const s=String(d).slice(0,10); const [y,m,day]=s.split('-'); return `${day} ${_MON[parseInt(m,10)-1]} ${y}` }
  catch { return fmtDate(d) }
}

function getSlotStatus(date, type, taken) {
  if (taken) return 'taken'
  const today = todayISO()
  const win = WINDOWS[type]
  if (!win) return 'future'
  if (date < today) return 'missed'
  if (date > today) return 'future'
  const now = new Date()
  const nowM = now.getHours()*60+now.getMinutes()
  const openM  = win.openH*60+win.openM
  const closeM = win.closeH*60+win.closeM
  const missedM = win.nextDay ? 24*60 : win.missedH*60+win.missedM
  if (type==='night') { return nowM>=openM ? 'open' : 'future' }
  if (nowM < openM)    return 'future'
  if (nowM <= closeM)  return 'open'
  if (nowM >= missedM) return 'missed'
  return 'future'
}

function parseMedication(text) {
  if (!text) return []
  const results = []
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
  const isNewMed = line => /^\d+[\)\.]\s+\S/.test(line)
  const isSectionHeader = line => /^(ointment|tablets?|capsules?|syrup|drops?|cream|gel|lotion)s?:?\s*$/i.test(line)
  const isInstruction = line => {
    if (isNewMed(line)) return false
    if (/^(first|next|later|then|after|apply|use|remove|avoid|take|do not|note|warning|rinse|wash|continue|repeat|increase|decrease|stop|once|twice|thrice|every|alternate|weekly|daily|nightly|overnight|morning|evening)/i.test(line)) return true
    return false
  }
  const blocks = []
  let currentBlock = null
  for (const line of lines) {
    if (isSectionHeader(line)) { if (currentBlock) blocks.push(currentBlock); currentBlock = null; continue }
    if (isNewMed(line)) { if (currentBlock) blocks.push(currentBlock); currentBlock = { headerLine: line, subLines: [] }; continue }
    if (isInstruction(line) && currentBlock) { currentBlock.subLines.push(line); continue }
    if (!currentBlock) { currentBlock = { headerLine: line, subLines: [] } } else { currentBlock.subLines.push(line) }
  }
  if (currentBlock) blocks.push(currentBlock)
  for (const block of blocks) {
    const line  = block.headerLine
    const lower = line.toLowerCase()
    const cleanName = line.replace(/^\d+[\)\.]\s*/,'').trim()
    const fullText = [line, ...block.subLines].join(' ').toLowerCase()
    // Pull out a "start after N weeks/days" phrase first — both to offset the
    // medicine's start AND so its number isn't mistaken for the duration.
    let startOffsetDays = 0
    let durText = fullText
    const soM = fullText.match(/start\s+after\s+(\d+)\s*(day|week|month)/i)
    if (soM) { const n = parseInt(soM[1]), u = soM[2].toLowerCase(); startOffsetDays = u==='month'?n*30:u==='week'?n*7:n; durText = fullText.replace(soM[0], ' ') }
    durText = durText.replace(/\d\s*[-–]\s*\d\s*[-–]\s*\d/g, ' ')  // drop dose patterns (0-0-1) so their digits aren't mistaken for a duration
    let durationDays = 30
    const mM = durText.match(/(\d+)\s*month/); if (mM) durationDays = parseInt(mM[1])*30
    const wM = durText.match(/(\d+)\s*week/);  if (wM && !mM) durationDays = parseInt(wM[1])*7
    const dM = durText.match(/(\d+)\s*day/);   if (dM && !mM && !wM) durationDays = parseInt(dM[1])
    const dosage = line.match(/(\d)\s*[-–]\s*(\d)\s*[-–]\s*(\d)/)
    if (dosage) {
      const mo = parseInt(dosage[1]), af = parseInt(dosage[2]), ni = parseInt(dosage[3])
      const namePart = cleanName.slice(0, cleanName.search(/\d\s*[-–]\s*\d\s*[-–]\s*\d/)).trim()
      if (mo) results.push({ name: namePart||cleanName, type:'morning',   durationDays, startOffsetDays, doses:mo })
      if (af) results.push({ name: namePart||cleanName, type:'afternoon', durationDays, startOffsetDays, doses:af })
      if (ni) results.push({ name: namePart||cleanName, type:'night',     durationDays, startOffsetDays, doses:ni })
      continue
    }
    const hasNight = /\b(night|9\s*pm|evening|overnight|nightly)\b/i.test(lower)
    const hasMorn  = /\b(morning|10\s*am)\b/i.test(lower)
    const hasAfter = /\b(afternoon|2\s*pm)\b/i.test(lower)
    if (hasNight || hasMorn || hasAfter) {
      const namePart = cleanName.replace(/\s+(night|9\s*pm|evening|overnight|nightly|morning|10\s*am|afternoon|2\s*pm).*/i,'').replace(/\s+\d+\s*(month|week|day)s?.*/i,'').trim()
      if (hasMorn)  results.push({ name: namePart||cleanName, type:'morning',   durationDays, startOffsetDays, doses:1 })
      if (hasAfter) results.push({ name: namePart||cleanName, type:'afternoon', durationDays, startOffsetDays, doses:1 })
      if (hasNight) results.push({ name: namePart||cleanName, type:'night',     durationDays, startOffsetDays, doses:1 })
    }
  }
  return results
}

function generateReminders(appointment) {
  if (!appointment) return []
  const combined = [appointment.medication||'', appointment.prescription||''].join('\n')
  const meds = parseMedication(combined)
  if (!meds.length) return []
  const raw = appointment.date||todayISO()
  const startDate = raw.includes('T')?raw.split('T')[0]:raw.split(' ')[0]
  const reminders = []
  for (const med of meds) {
    for (let i=0; i<med.durationDays; i++) {
      reminders.push({ id:`${appointment.id}__${med.name}__${med.type}__${i}`, date:addDays(startDate,(med.startOffsetDays||0)+i), medicationName:med.name, medicationType:med.type, doses:med.doses, durationDays:med.durationDays, dayIndex:i })
    }
  }
  // Medication + prescription text often duplicate the same meds → dedupe by id.
  const seen = new Set()
  const deduped = reminders.filter(r => (seen.has(r.id) ? false : (seen.add(r.id), true)))
  const typeOrder = { morning:0, afternoon:1, night:2 }
  deduped.sort((a,b) => a.date!==b.date?a.date.localeCompare(b.date):(typeOrder[a.medicationType]??3)-(typeOrder[b.medicationType]??3))
  return deduped
}

// Consult marked complete but plan days / follow-up still remaining → treatment is active
function planIsActive(a) {
  if (!a || a.status !== 'completed') return false
  try {
    const startISO = String(a.date || '').slice(0, 10)
    const elapsed = Math.floor((Date.now() - new Date(startISO + 'T00:00:00')) / 86400000) + 1
    const parsed = parseMedication([a.medication || '', a.prescription || ''].join('\n'))
    const dur = parsed.length ? Math.max(...parsed.map(m => m.durationDays)) : null
    const planDays = (dur && dur >= 3 && dur <= 180) ? dur : null
    const fuPending = a.followUpDate && String(a.followUpDate).slice(0, 10) >= todayISO()
    return (planDays != null && elapsed >= 1 && elapsed <= planDays) || !!fuPending
  } catch { return false }
}

const lsKey  = id => `rem-state-${id}`
const loadState = id => { try { return JSON.parse(localStorage.getItem(lsKey(id))||'{}') } catch { return {} } }
const saveState = (id,s) => { try { localStorage.setItem(lsKey(id),JSON.stringify(s)) } catch {} }

// Group parsed medicines by name into Topical / Oral for the Medications screen
const TOPICAL_RE = /\b(cream|gel|lotion|ointment|serum|wash|cleanser|solution|foam|sunscreen|spf|moisturi|toner|mask|apply|topical|face)\b/i
function structuredMeds(appt) {
  const text = [appt?.medication || '', appt?.prescription || ''].join('\n')
  const parsed = parseMedication(text)
  const byName = {}
  parsed.forEach(m => {
    const key = (m.name || '').trim()
    if (!key) return
    if (!byName[key]) byName[key] = { name: key, types: [], durationDays: 0, doses: m.doses }
    if (!byName[key].types.includes(m.type)) byName[key].types.push(m.type)
    byName[key].durationDays = Math.max(byName[key].durationDays, m.durationDays)
  })
  const schedIcon = t => t.includes('night') ? '🌙' : t.includes('afternoon') ? '☀️' : t.includes('morning') ? '🌅' : '💊'
  const schedLabel = t => {
    const p = []
    if (t.includes('morning')) p.push('Morning')
    if (t.includes('afternoon')) p.push('Afternoon')
    if (t.includes('night')) p.push('Night')
    return p.join(' & ') || 'As needed'
  }
  const when = t => t.includes('night') ? 'at night' : t.includes('morning') ? 'in the morning' : t.includes('afternoon') ? 'in the afternoon' : 'as directed'
  const topical = [], oral = []
  Object.values(byName).forEach(m => {
    const isTopical = TOPICAL_RE.test(m.name)
    const entry = {
      name: m.name, icon: schedIcon(m.types), schedule: schedLabel(m.types), durationDays: m.durationDays,
      instruction: isTopical
        ? `Apply ${when(m.types)}`
        : `${m.doses || 1} ${/cap/i.test(m.name) ? 'capsule' : 'tablet'}${(m.doses || 1) > 1 ? 's' : ''} ${when(m.types)}`
    }
    ;(isTopical ? topical : oral).push(entry)
  })
  return { topical, oral, count: Object.keys(byName).length }
}

// Downscale/compress an image client-side so uploads are fast (camera photos are multi-MB)
function compressImage(file, maxDim = 1400, quality = 0.8) {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/')) return resolve(file)
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      let { width, height } = img
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim }
        else { width = Math.round(width * maxDim / height); height = maxDim }
      }
      try {
        const canvas = document.createElement('canvas')
        canvas.width = width; canvas.height = height
        canvas.getContext('2d').drawImage(img, 0, 0, width, height)
        canvas.toBlob(b => resolve(b ? new File([b], (file.name || 'photo').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file), 'image/jpeg', quality)
      } catch { resolve(file) }
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
    img.src = url
  })
}

// ─── MY CARE SCREEN ──────────────────────────────────────────────────────────
const G = { g900:'#0D2218',g800:'#1A3A28',g700:'#235233',g600:'#2D6B42',g500:'#3D8A57',g300:'#85C99A',g100:'#E8F5EE',g50:'#F5FAF7',mute:'#8a9a92',text:'#1A2820',border:'#EAF1ED',headerGrad:'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)' }
const MF = "'DM Sans',sans-serif"
const MS = "'DM Serif Display',serif"

// Meds that increase sun sensitivity — detected from the patient's prescription
const PHOTO_MEDS = [
  { re: /tretinoin|retinoid|\bretinol\b|adapalene|isotretinoin|tazaroten|differin|retino[\s-]?a/i, label: 'a retinoid (like tretinoin)' },
  { re: /doxycyclin|tetracyclin|minocyclin/i, label: 'doxycycline' },
  { re: /hydroquinone/i, label: 'hydroquinone' },
  { re: /glycolic|lactic|salicylic|mandelic|\baha\b|\bbha\b/i, label: 'an exfoliating acid' },
]
// Daily skin tip from REAL UV + weather + medication. All advice is guideline-based
// (AAD / WHO sun-care) — no generative AI writing medical claims, no fake numbers.
function buildSkinTip(uv, temp, hum, photoMed) {
  const uvr = (uv == null || isNaN(uv)) ? null : Math.round(uv)
  const medNote = photoMed ? `Because you're using ${photoMed.label}, your skin is more sun-sensitive — SPF matters more today.` : null
  let icon, headline, body, source
  if (uvr != null) {
    source = 'AAD / WHO sun-care guidance'
    if (uvr >= 11)     { icon = '🔥'; headline = `UV Index ${uvr} · Extreme`;   body = 'Avoid the sun 10am–4pm. SPF 50+, reapply every 2 hours, and cover up — hat, sunglasses & long sleeves.' }
    else if (uvr >= 8) { icon = '🔆'; headline = `UV Index ${uvr} · Very High`; body = 'Apply SPF 50+ and reapply every 2 hours. Avoid direct sun 11am–3pm.' }
    else if (uvr >= 6) { icon = '☀️'; headline = `UV Index ${uvr} · High`;      body = 'Apply SPF 50 and reapply every 3 hours when outdoors.' }
    else if (uvr >= 3) { icon = '🌤️'; headline = `UV Index ${uvr} · Moderate`;  body = 'Apply SPF 30+ before going outdoors.' }
    else               { icon = '☁️'; headline = `UV Index ${uvr} · Low`;       body = 'UV is low, but daily SPF still protects against premature ageing.' }
    if (hum != null && hum >= 78) body += ' It’s humid — keep products lightweight and non-comedogenic.'
    else if (hum != null && hum < 35) body += ' Air is dry — layer a hydrating serum under moisturiser.'
  } else {
    // No live UV (location off) — honest fallback: real guidance, no invented number
    const warm = (() => { const m = new Date().getMonth(); return m >= 2 && m <= 9 })()
    source = 'Daily skincare basics'
    icon = warm ? '🌞' : '🧴'
    headline = 'Daily skin care'
    body = warm
      ? 'Cleanse gently and apply SPF 30+ every morning — reapply if you’re out in the sun.'
      : 'Hydrate well and apply SPF 30+ — UV still reaches your skin on cool, cloudy days.'
  }
  return { uv: uvr, icon, headline, body, medNote, source }
}

function MyCareScreen({ user, treatment, patientState, allScans, appointments, fetchWithAuth, navigate, onOpenDetail, onReload }) {
  const [healthLogs, setHealthLogs] = React.useState([])
  const [scoreOpen, setScoreOpen] = React.useState(false)
  const [healthView, setHealthView] = React.useState(false)
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false)
  const [pane, setPane] = React.useState('home')  // home | photos | history | review
  const [preview, setPreview] = React.useState(null)  // filename shown full-screen
  const photoInputRef = React.useRef(null)
  const camRef = React.useRef(null)                    // single-shot camera for slots
  const [slots, setSlots] = React.useState([])         // staged this-week photos: {file,url} up to 3

  const fetchHealthLogs = React.useCallback(() => {
    if (!fetchWithAuth) return
    fetchWithAuth(`${API_BASE}/api/health/logs?days=30`)
      .then(r => r.ok ? r.json() : { logs: [] })
      .then(data => setHealthLogs(Array.isArray(data?.logs) ? data.logs : []))
      .catch(() => {})
  }, [fetchWithAuth])

  React.useEffect(() => {
    fetchHealthLogs()
  }, [fetchHealthLogs])

  // Steps live in the phone's native step counter (not health_logs) — pull the
  // same 14-day history the Lifestyle screen uses so the preview tile has a value.
  const [stepHist, setStepHist] = React.useState([])
  React.useEffect(() => {
    try {
      const { StepCounter } = require('../native/stepCounter')
      StepCounter && StepCounter.getHistory && StepCounter.getHistory({ days: 14 })
        .then(r => setStepHist(r?.history || [])).catch(() => {})
    } catch {}
  }, [])
  const avgSteps = React.useMemo(() => {
    const v = stepHist.map(h => h.steps).filter(x => typeof x === 'number' && x > 0)
    return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null
  }, [stepHist])

  // Gather all treatment progress photos across appointments, newest first.
  // Filenames start with an upload timestamp (e.g. "1781801987770-name.png").
  const progressPhotos = React.useMemo(() => {
    const all = []
    appointments.forEach(a => {
      let pp = []
      try { pp = typeof a.progressPhotos === 'string' ? JSON.parse(a.progressPhotos || '[]') : (Array.isArray(a.progressPhotos) ? a.progressPhotos : []) } catch {}
      pp.forEach(f => { const ts = parseInt(String(f).split('-')[0]); all.push({ file: f, ts: isNaN(ts) ? 0 : ts }) })
    })
    return all.sort((x, y) => y.ts - x.ts)
  }, [appointments])

  const lastPhotoTs = progressPhotos.length ? progressPhotos[0].ts : null
  const daysSincePhoto = lastPhotoTs ? Math.floor((Date.now() - lastPhotoTs) / 86400000) : null
  const photoDue = daysSincePhoto === null || daysSincePhoto >= 7  // weekly cadence

  // Appointment to attach new weekly photos to (active treatment, else most recent).
  const photoTarget = React.useMemo(
    () => treatment?.appt || appointments.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null,
    [treatment, appointments]
  )

  // Doctor's verdict(s) on the uploaded progress photos
  const [reviews, setReviews] = React.useState([])
  React.useEffect(() => {
    if (!fetchWithAuth || !photoTarget?.id) { setReviews([]); return }
    fetchWithAuth(`${API_BASE}/api/appointments/${photoTarget.id}/photo-reviews`)
      .then(r => r.ok ? r.json() : { reviews: [] })
      .then(d => setReviews(Array.isArray(d?.reviews) ? d.reviews : []))
      .catch(() => {})
  }, [fetchWithAuth, photoTarget])
  const lastReview = reviews[0] || null

  // ── Today's Skin Tip: real GPS → real UV (open-meteo, no keys) + medication ──
  const [skinTip, setSkinTip] = React.useState(null)
  React.useEffect(() => {
    let cancelled = false
    const medsText = [treatment?.appt?.medication, treatment?.appt?.prescription].filter(Boolean).join(' ')
    const photoMed = PHOTO_MEDS.find(m => m.re.test(medsText)) || null
    ;(async () => {
      let lat = null, lon = null
      // 1) Accurate GPS
      try {
        const { Geolocation } = require('@capacitor/geolocation')
        const perm = await Geolocation.requestPermissions()
        if (perm.location === 'granted' || perm.coarseLocation === 'granted') {
          const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
          lat = pos.coords.latitude; lon = pos.coords.longitude
        }
      } catch (e) {}
      // 2) Fallback: geocode the patient's saved locality
      if (lat == null) {
        const locality = appointments.find(a => a.locality)?.locality
        if (locality) {
          try {
            const gr = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(locality)}&count=1`)
            const gd = await gr.json()
            if (gd.results?.length) { lat = gd.results[0].latitude; lon = gd.results[0].longitude }
          } catch {}
        }
      }
      // No location → still show a real tip (medication/season), just no UV number
      if (lat == null) { if (!cancelled) setSkinTip(buildSkinTip(null, null, null, photoMed)); return }
      try {
        const wr = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=uv_index,temperature_2m,relative_humidity_2m&daily=uv_index_max&timezone=auto`)
        const wd = await wr.json()
        if (cancelled) return
        // Use TODAY'S PEAK UV (daily max) so a morning/evening open is still meaningful,
        // not the instantaneous value (which is 0 at night).
        const uv = wd?.daily?.uv_index_max?.[0] != null ? wd.daily.uv_index_max[0] : wd?.current?.uv_index
        setSkinTip(buildSkinTip(uv, wd?.current?.temperature_2m, wd?.current?.relative_humidity_2m, photoMed))
      } catch { if (!cancelled) setSkinTip(buildSkinTip(null, null, null, photoMed)) }
    })()
    return () => { cancelled = true }
  }, [treatment, appointments])

  const uploadWeeklyPhoto = async (files) => {
    if (!files?.length || !photoTarget) return
    setUploadingPhoto(true)
    try {
      const fd = new FormData()
      const arr = Array.from(files).slice(0, 3)
      for (let i = 0; i < arr.length; i++) { const c = await compressImage(arr[i]); fd.append(`photo_${i}`, c) }
      const res = await fetchWithAuth(`${API_BASE}/api/appointments/${photoTarget.id}/progress-photos`, { method: 'POST', body: fd })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { alert(data.message || 'Upload failed'); return }
      onReload && onReload()
    } catch (e) { alert('Upload failed: ' + e.message) }
    finally { setUploadingPhoto(false) }
  }

  // 3-slot camera capture: each tap opens the camera for one shot (front/left/right)
  const addSlot = (files) => {
    const f = files && files[0]
    if (!f) return
    setSlots(s => s.length >= 3 ? s : [...s, { file: f, url: URL.createObjectURL(f) }])
  }
  const removeSlot = (i) => setSlots(s => {
    const t = s[i]; if (t) { try { URL.revokeObjectURL(t.url) } catch {} }
    return s.filter((_, j) => j !== i)
  })
  const submitSlots = async () => {
    if (!slots.length || uploadingPhoto) return
    await uploadWeeklyPhoto(slots.map(s => s.file))
    slots.forEach(s => { try { URL.revokeObjectURL(s.url) } catch {} })
    setSlots([])
  }

  // No adherence here — it only applies during an active course (handled on the active-treatment page).
  // Score = Progress Photos (45) + Lifestyle (30) + Doctor review (25), rescaled to 100.
  const score = React.useMemo(() => {
    const photos = !progressPhotos.length ? 0 : daysSincePhoto <= 7 ? 45 : daysSincePhoto <= 14 ? 36 : daysSincePhoto <= 30 ? 27 : 18
    const lifestyle = healthLogs.length >= 7 ? 30 : healthLogs.length >= 3 ? 21 : healthLogs.length >= 1 ? 12 : 0
    const doctor = (appointments.some(a => a.status === 'completed') || lastReview) ? 25 : 0
    return { total: photos + lifestyle + doctor, photos, lifestyle, doctor }
  }, [progressPhotos, daysSincePhoto, healthLogs, appointments, lastReview])

  const scoreLabel = score.total >= 85 ? 'Excellent' : score.total >= 70 ? 'Good' : score.total >= 50 ? 'Fair' : 'Getting Started'
  const scoreColor = score.total >= 85 ? G.g500 : score.total >= 70 ? '#5AAE73' : score.total >= 50 ? '#B7770D' : G.mute
  const insight = score.total >= 85 ? 'Skin is stable and improving. Keep adding a weekly progress photo.'
    : score.total >= 70 ? 'Good progress. Add a weekly photo and stay consistent.'
    : score.total >= 50 ? 'Building your score. Add a weekly progress photo and log daily.'
    : 'Start your journey. Add a progress photo each week to track your skin.'

  const lifeAvg = React.useMemo(() => {
    if (!healthLogs.length) return null
    const r = healthLogs.slice(0, 14)
    const avg = key => { const v = r.map(l => parseFloat(l[key])).filter(x => !isNaN(x) && x > 0); return v.length ? Math.round(v.reduce((a,b)=>a+b,0)/v.length) : null }
    const stresses = r.map(l => l.stressLevel).filter(Boolean)
    const sMap = { low:1, medium:2, high:3 }
    const sAvg = stresses.length ? stresses.map(s=>sMap[s]||2).reduce((a,b)=>a+b,0)/stresses.length : null
    const stress = sAvg === null ? null : sAvg <= 1.5 ? 'Low' : sAvg <= 2.2 ? 'Medium' : 'High'
    return { sleep: avg('sleepHours'), water: avg('waterGlasses'), steps: avg('steps'), stress }
  }, [healthLogs])

  const nextReview = React.useMemo(() => {
    const future = appointments.filter(a => ['pending','confirmed'].includes(a.status) && new Date(a.date) > new Date())
    if (future.length) {
      const next = future.sort((a,b) => new Date(a.date)-new Date(b.date))[0]
      return { date: next.date, daysLeft: Math.ceil((new Date(next.date)-Date.now())/86400000), isBooked: true, doctor: next.doctor||'Your dermatologist' }
    }
    const done = appointments.filter(a => a.status === 'completed')
    if (done.length) {
      const last = done.sort((a,b)=>new Date(b.date)-new Date(a.date))[0]
      const d = new Date(last.date); d.setDate(d.getDate()+90)
      return { date: d.toISOString().slice(0,10), daysLeft: Math.ceil((d-Date.now())/86400000), isBooked: false, doctor: last.doctor||'Your dermatologist' }
    }
    return null
  }, [appointments])

  const Card = ({ children, style={} }) => (
    <div style={{ background:'#fff', borderRadius:20, border:`1px solid ${G.border}`, padding:'1.2rem', ...style }}>{children}</div>
  )
  const SecTitle = ({ children, action }) => (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.85rem' }}>
      <div style={{ fontWeight:800, fontSize:'1rem', color:G.text }}>{children}</div>
      {action}
    </div>
  )

  // Circular score ring
  const Ring = ({ value, color, size=94, stroke=9 }) => {
    const r = (size - stroke) / 2
    const c = 2 * Math.PI * r
    const off = c - (Math.max(0, Math.min(100, value)) / 100) * c
    return (
      <svg width={size} height={size} style={{ transform:'rotate(-90deg)', flexShrink:0 }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={G.g100} strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition:'stroke-dashoffset .5s' }} />
      </svg>
    )
  }

  // Navigation list row
  const Row = ({ icon, title, subtitle, onClick, last }) => (
    <button onClick={onClick} style={{ display:'flex', alignItems:'center', gap:14, width:'100%', background:'none', border:'none', borderBottom:last?'none':`1px solid ${G.border}`, padding:'.95rem .2rem', cursor:'pointer', fontFamily:MF, textAlign:'left' }}>
      <div style={{ width:40, height:40, borderRadius:12, background:G.g50, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem', flexShrink:0 }}>{icon}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:700, color:G.text, fontSize:'.92rem' }}>{title}</div>
        <div style={{ fontSize:'.72rem', color:G.mute, marginTop:2 }}>{subtitle}</div>
      </div>
      <span style={{ color:G.mute, fontSize:'1.15rem' }}>›</span>
    </button>
  )

  // Sub-screen header with back button
  const SubHeader = ({ title, subtitle }) => (
    <div style={{ background:'#fff', padding:'3rem 1.2rem 1rem', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', gap:12 }}>
      <button onClick={() => setPane('home')} style={{ background:'none', border:'none', fontSize:'1.4rem', color:G.g900, cursor:'pointer', padding:0, lineHeight:1 }}>‹</button>
      <div>
        <div style={{ fontFamily:MS, fontSize:'1.3rem', color:G.g900 }}>{title}</div>
        {subtitle && <div style={{ fontSize:'.74rem', color:G.mute, marginTop:2 }}>{subtitle}</div>}
      </div>
    </div>
  )

  // Adaptive status banner (top of My Care home)
  const firstName = (() => {
    const n = (user?.name || '').trim()
    if (n) return n.split(/\s+/)[0]
    const e = user?.email || ''
    return (e && e.includes('@')) ? e.split('@')[0] : 'there'
  })()
  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening' })()

  // The single most relevant next action — factual, drives behaviour
  const todayLogged = healthLogs.some(l => l.date === todayISO())
  const nextStep = (() => {
    if (daysSincePhoto == null) return 'Upload your first progress photos (up to 3)'
    if (daysSincePhoto >= 7) return "Upload this week's progress photos (up to 3)"
    if (!todayLogged) return "Log today's check-in"
    const d = 7 - daysSincePhoto
    return `Upload your next photo in ${d} day${d === 1 ? '' : 's'}`
  })()

  const status = (() => {
    if (patientState === 'TREATMENT_COMPLETED') {
      const done = appointments.filter(a => a.status === 'completed').sort((a,b)=>new Date(b.date)-new Date(a.date))[0]
      const days = done ? Math.max(0, Math.floor((Date.now() - new Date(done.date)) / 86400000)) : 0
      // Celebrate for the first week, then settle into a living, actionable greeting.
      if (days <= 7) return { emoji:'', title:'Congratulations!', body:`Your treatment is complete. You've maintained healthy skin for ${days} day${days===1?'':'s'}.`, nextStep }
      // Only claim "improving" when the doctor's verdict actually says so
      const improving = lastReview?.verdict === 'improving'
      return improving
        ? { emoji:'', title:`Great progress, ${firstName}`, body:'Your dermatologist sees your skin improving.', nextStep }
        : { emoji:'', title:`${greeting}, ${firstName} 👋`, body:'Maintaining your results — nice work.', nextStep }
    }
    if (treatment && ['ACTIVE_TREATMENT','CARE_PLAN_CREATED','FOLLOW_UP_DUE'].includes(patientState)) {
      return { emoji:'🌿', title:treatment.name, body:`Day ${treatment.dayNum} of ${treatment.planDays} · ${treatment.pct}% complete. Keep going!` }
    }
    if (patientState === 'APPT_BOOKED') return { emoji:'📅', title:'Consultation booked', body:'Your appointment is confirmed. Prepare your photos and questions.' }
    return { emoji:'👋', title:`${greeting}, ${firstName} 👋`, body:'Book a consultation to start your personalised skin journey.' }
  })()

  // Photos oldest-first with week labels (relative to treatment start / first photo)
  const weekPhotos = React.useMemo(() => {
    const asc = progressPhotos.slice().sort((a,b) => a.ts - b.ts)
    let startTs = null
    if (treatment?.appt?.date) { const t = new Date(treatment.appt.date).getTime(); if (!isNaN(t)) startTs = t }
    if (!startTs && asc.length) startTs = asc[0].ts
    return asc.map(p => {
      const wk = (startTs && p.ts) ? Math.max(0, Math.round((p.ts - startTs) / (7*86400000))) : null
      return { ...p, week: wk }
    })
  }, [progressPhotos, treatment])

  // ── PROGRESS PHOTOS grid sub-screen ──
  if (pane === 'photos') return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <input ref={photoInputRef} type="file" accept="image/*" multiple style={{ display:'none' }}
        onChange={e => { uploadWeeklyPhoto(e.target.files); e.target.value = '' }} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" style={{ display:'none' }}
        onChange={e => { addSlot(e.target.files); e.target.value = '' }} />
      <SubHeader title="Progress Photos" subtitle="Track your skin improvement" />
      <div style={{ padding:'1.1rem' }}>
        {/* Doctor's verdict on the photos */}
        {lastReview && (() => {
          const vm = { improving:['✓','Improving',G.g600,G.g100,G.g300], same:['👍','No major change','#B7770D','#FFF8E1','#FFE082'], worse:['⚠️','Needs attention','#C0392B','#FFF0F0','#F5C6C6'] }[lastReview.verdict] || ['💬','Reviewed',G.g600,G.g100,G.g300]
          return (
            <div style={{ background:vm[3], border:`1px solid ${vm[4]}`, borderRadius:14, padding:'.9rem 1rem', marginBottom:'1rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:'1.1rem' }}>{vm[0]}</span>
                <div style={{ fontWeight:800, color:vm[2], fontSize:'.9rem' }}>{vm[1]}</div>
              </div>
              <div style={{ fontSize:'.75rem', color:G.text, marginTop:5, lineHeight:1.5 }}>
                Reviewed by {lastReview.doctorName || 'your doctor'} · {fmtDate(lastReview.createdAt)}
              </div>
              {lastReview.note && <div style={{ fontSize:'.8rem', color:G.text, marginTop:6, lineHeight:1.55, fontStyle:'italic' }}>"{lastReview.note}"</div>}
              {lastReview.verdict === 'worse' && (
                <button onClick={() => navigate('/book-appointment')} style={{ marginTop:'.7rem', background:'#C0392B', color:'#fff', border:'none', borderRadius:10, padding:'.55rem 1rem', fontWeight:700, fontSize:'.8rem', cursor:'pointer', fontFamily:MF }}>Book a review →</button>
              )}
            </div>
          )
        })()}
        {photoDue ? (
          <div style={{ background:'#fff', border:`1px solid ${G.border}`, borderRadius:16, padding:'1rem', marginBottom:'1rem' }}>
            <div style={{ fontWeight:800, color:G.text, fontSize:'.92rem' }}>📅 Add this week's photos</div>
            <div style={{ fontSize:'.72rem', color:G.mute, margin:'.25rem 0 .85rem' }}>Tap a slot to open the camera. Add up to 3 (front, left, right).</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {[0,1,2].map(i => {
                const s = slots[i]; const label = ['Front','Left','Right'][i]; const isNext = slots.length === i
                return (
                  <div key={i}>
                    <div onClick={() => { if (!s && isNext && !uploadingPhoto) camRef.current && camRef.current.click() }}
                      style={{ position:'relative', width:'100%', paddingBottom:'100%', borderRadius:12, overflow:'hidden', border: s ? `1px solid ${G.border}` : `2px dashed ${G.g300}`, background: s ? '#000' : G.g50, cursor: (!s && isNext) ? 'pointer' : 'default', opacity: (s || isNext) ? 1 : .45 }}>
                      {s ? (<>
                        <img src={s.url} alt={label} style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }} />
                        <button onClick={(ev) => { ev.stopPropagation(); removeSlot(i) }} style={{ position:'absolute', top:4, right:4, width:22, height:22, borderRadius:'50%', background:'rgba(0,0,0,.6)', color:'#fff', border:'none', fontSize:'.85rem', lineHeight:1, cursor:'pointer' }}>×</button>
                      </>) : (
                        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color:G.g600, fontSize:'1.5rem' }}>📷</div>
                      )}
                    </div>
                    <div style={{ fontSize:'.64rem', color: s ? G.text : G.mute, fontWeight: s ? 700 : 500, textAlign:'center', marginTop:4 }}>{label}</div>
                  </div>
                )
              })}
            </div>
            <button onClick={submitSlots} disabled={!slots.length || uploadingPhoto}
              style={{ width:'100%', marginTop:'.9rem', background: (slots.length && !uploadingPhoto) ? G.g700 : '#CBDCD2', color:'#fff', border:'none', borderRadius:12, padding:'.85rem', fontWeight:800, fontSize:'.9rem', cursor:(slots.length && !uploadingPhoto)?'pointer':'default', fontFamily:MF }}>
              {uploadingPhoto ? 'Uploading…' : slots.length ? `Submit ${slots.length} photo${slots.length===1?'':'s'}` : 'Add at least 1 photo'}
            </button>
          </div>
        ) : (
          <div style={{ background:G.g100, border:`1px solid ${G.g300}`, borderRadius:12, padding:'.7rem .9rem', fontSize:'.76rem', color:G.g700, fontWeight:600, marginBottom:'1rem' }}>
            ✓ This week's photos are in. Next window in {Math.max(1, 7 - (daysSincePhoto || 0))} day{Math.max(1, 7 - (daysSincePhoto || 0)) === 1 ? '' : 's'}.
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          {weekPhotos.map((p, i) => (
            <div key={p.file} onClick={() => setPreview(p.file)} style={{ cursor:'pointer' }}>
              <div style={{ position:'relative', width:'100%', paddingBottom:'100%', borderRadius:14, overflow:'hidden', border:`1px solid ${G.border}`, background:'#fff' }}>
                <img src={photoUrl(p.file)} alt={`week-${i}`} style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.style.display='none' }} />
              </div>
              <div style={{ fontWeight:700, color:G.text, fontSize:'.82rem', marginTop:6 }}>{p.week !== null ? `Week ${p.week}` : `Photo ${i+1}`}</div>
              <div style={{ fontSize:'.68rem', color:G.mute }}>{p.ts ? fmtDate(new Date(p.ts).toISOString().slice(0,10)) : ''}</div>
            </div>
          ))}
        </div>
        <div style={{ background:'#fff', border:`1px solid ${G.border}`, borderRadius:12, padding:'.8rem .95rem', fontSize:'.72rem', color:G.mute, lineHeight:1.55, marginTop:'1rem', display:'flex', gap:8 }}>
          <span>ℹ️</span><span>Consistent weekly photos help your doctor track improvement better and keep your Skin Health Score high.</span>
        </div>
      </div>

      {/* Full-screen in-app preview */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.92)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <img src={photoUrl(preview)} alt="preview" style={{ maxWidth:'100%', maxHeight:'85%', borderRadius:12, objectFit:'contain' }} />
          <button onClick={() => setPreview(null)} style={{ position:'absolute', top:'3rem', right:'1.2rem', width:40, height:40, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.3)', color:'#fff', fontSize:'1.3rem', cursor:'pointer' }}>✕</button>
        </div>
      )}
    </div>
  )

  // ── TREATMENT HISTORY list sub-screen ──
  if (pane === 'history') return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <SubHeader title="Treatment History" subtitle="All your past consultations" />
      <div style={{ padding:'1.1rem', display:'flex', flexDirection:'column', gap:'1rem' }}>
        {appointments.length === 0
          ? <div style={{ fontSize:'.82rem', color:G.mute, textAlign:'center', padding:'2rem 0' }}>No treatment history yet.</div>
          : appointments.slice().sort((a,b)=>new Date(b.date)-new Date(a.date)).map((a,i) => {
              let pp = []
              try { pp = typeof a.progressPhotos === 'string' ? JSON.parse(a.progressPhotos||'[]') : (Array.isArray(a.progressPhotos)?a.progressPhotos:[]) } catch {}
              const meds = parseMedication(a.medication || a.prescription || '')
              const dur = meds.length ? Math.max(...meds.map(m=>m.durationDays)) : null
              // Consult marked complete but plan days / follow-up still remaining → treatment is active
              const startI = String(a.date||'').slice(0,10)
              const elapsedD = (() => { try { return Math.floor((Date.now() - new Date(startI+'T00:00:00'))/86400000) + 1 } catch { return null } })()
              const fuPend = a.followUpDate && String(a.followUpDate).slice(0,10) >= todayISO()
              const planActive = a.status==='completed' && ((dur && dur >= 3 && dur <= 180 && elapsedD != null && elapsedD <= dur) || fuPend)
              const sColor = a.status==='completed'?G.g500:a.status==='confirmed'?'#3451B2':a.status==='cancelled'?'#C0392B':'#B7770D'
              const sLabel = a.status==='completed'?(planActive?'Active plan':'Completed'):a.status==='confirmed'?'Confirmed':a.status==='cancelled'?'Cancelled':'Pending'
              let adh = null
              try {
                const st = JSON.parse(localStorage.getItem(`rem-state-${a.id}`)||'{}')
                const taken = Object.values(st).filter(Boolean).length
                const rems = generateReminders(a)
                const elapsed = rems.filter(r=>r.date<=todayISO()).length
                if (elapsed) adh = Math.min(100, Math.round(taken/elapsed*100))
              } catch {}
              const outcome = a.status==='completed'?(planActive?'Ongoing':'Completed'):a.status==='cancelled'?'—':'Ongoing'
              const Stat = ({ label, value, color }) => (
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'.62rem', color:G.mute, fontWeight:600 }}>{label}</div>
                  <div style={{ fontWeight:800, color:color||G.text, fontSize:'.85rem', marginTop:2 }}>{value}</div>
                </div>
              )
              return (
                <div key={a._id||i} onClick={() => onOpenDetail && onOpenDetail(a,'details')} style={{ background:'#fff', borderRadius:18, border:`1px solid ${G.border}`, padding:'1.1rem', cursor:'pointer' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                    <div style={{ fontWeight:800, color:G.text, fontSize:'1rem' }}>{a.service||'Consultation'}</div>
                    <span style={{ fontSize:'.66rem', fontWeight:700, color:sColor, background:`${sColor}18`, padding:'.2rem .6rem', borderRadius:999 }}>{sLabel}</span>
                  </div>
                  <div style={{ fontSize:'.72rem', color:G.mute, marginBottom:'.85rem' }}>{fmtDate(a.date)}{a.doctor?` · ${a.doctor}`:''}</div>
                  <div style={{ display:'flex', gap:12, paddingTop:'.7rem', borderTop:`1px solid ${G.border}` }}>
                    <Stat label="Duration" value={dur?`${dur} days`:'—'} />
                    <Stat label="Adherence" value={adh!==null?`${adh}%`:'—'} />
                    <Stat label="Outcome" value={outcome} color={a.status==='completed'?G.g600:G.text} />
                  </div>
                  {pp.length>0 && (
                    <div style={{ display:'flex', gap:6, marginTop:'.85rem' }}>
                      {pp.slice(0,4).map((f,j)=>(
                        <div key={j} style={{ width:52, height:52, borderRadius:10, overflow:'hidden', border:`1px solid ${G.border}` }}>
                          <img src={photoUrl(f)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>{e.target.style.display='none'}} />
                        </div>
                      ))}
                      {pp.length>4 && <div style={{ width:52, height:52, borderRadius:10, background:G.g50, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.72rem', fontWeight:700, color:G.g600 }}>+{pp.length-4}</div>}
                    </div>
                  )}
                </div>
              )
            })
        }
      </div>
    </div>
  )

  // ── NEXT REVIEW sub-screen ──
  if (pane === 'review') {
    const dx = (appointments.find(a => a.diagnosis)?.diagnosis || '').toLowerCase()
    const reviewReason = /acne/.test(dx) ? 'Acne treatments should be reviewed every 3 months to adjust your plan and prevent scarring.'
      : /(pigment|melasma)/.test(dx) ? 'Pigmentation needs periodic review to track fading and prevent it coming back.'
      : /(psorias|eczema|dermatit)/.test(dx) ? 'Chronic skin conditions should be reviewed regularly to keep flare-ups under control.'
      : /(hair|alopecia|dandruff)/.test(dx) ? 'Hair treatments take months to show results — regular reviews keep the plan on track.'
      : 'Dermatologists recommend a skin review every 3–6 months to catch changes early.'
    const RecBox = () => (
      <div style={{ background:G.g50, borderRadius:12, padding:'.85rem 1rem', marginTop:'1rem' }}>
        <div style={{ fontSize:'.64rem', color:G.mute, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700, marginBottom:6 }}>Recommended because</div>
        <div style={{ display:'flex', gap:8, fontSize:'.82rem', color:G.text, lineHeight:1.55 }}>
          <span style={{ color:G.g500, fontWeight:800 }}>✓</span><span>{reviewReason}</span>
        </div>
      </div>
    )
    return (
      <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
        <SubHeader title="Next Skin Review" subtitle="Stay on top of your skin health" />
        <div style={{ padding:'1.1rem' }}>
          <Card>
            {nextReview ? (
              <>
                <div style={{ textAlign:'center', paddingBottom:'1rem' }}>
                  <div style={{ fontSize:'.72rem', color:G.mute, fontWeight:600 }}>Next Skin Review</div>
                  <div style={{ fontFamily:MS, fontSize:'1.6rem', color:G.g900, margin:'6px 0 2px' }}>{fmtDate(nextReview.date)}</div>
                  <div style={{ display:'inline-block', background: nextReview.daysLeft<=7?'#FFF8E1':G.g100, color: nextReview.daysLeft<=7?'#B7770D':G.g700, fontWeight:800, fontSize:'.82rem', padding:'.3rem .9rem', borderRadius:999, marginTop:6 }}>{Math.max(0,nextReview.daysLeft)} days left</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'.7rem 0', borderTop:`1px solid ${G.border}` }}>
                  <span style={{ fontSize:'1.1rem' }}>👩‍⚕️</span>
                  <span style={{ flex:1, fontSize:'.85rem', color:G.text, fontWeight:600 }}>{nextReview.doctor}</span>
                  {nextReview.isBooked && <span style={{ fontSize:'.7rem', color:G.g600, fontWeight:700 }}>✅ Confirmed</span>}
                </div>
                <RecBox />
                <button onClick={() => navigate('/book-appointment')} style={{ width:'100%', marginTop:'1rem', background:nextReview.isBooked?G.g100:G.g700, color:nextReview.isBooked?G.g700:'#fff', border:'none', borderRadius:12, padding:'.85rem', fontWeight:800, fontSize:'.9rem', cursor:'pointer', fontFamily:MF }}>
                  {nextReview.isBooked ? 'View Appointment' : 'Book Earlier →'}
                </button>
              </>
            ) : (
              <>
                <div style={{ textAlign:'center', padding:'.5rem 0' }}>
                  <div style={{ fontSize:'2rem' }}>🗓️</div>
                  <div style={{ fontWeight:800, color:G.text, marginTop:6 }}>No review scheduled yet</div>
                </div>
                <RecBox />
                <button onClick={() => navigate('/book-appointment')} style={{ width:'100%', marginTop:'1rem', background:G.g700, color:'#fff', border:'none', borderRadius:12, padding:'.85rem', fontWeight:800, fontSize:'.9rem', cursor:'pointer', fontFamily:MF }}>
                  Book a Review →
                </button>
              </>
            )}
          </Card>
        </div>
      </div>
    )
  }

  if (healthView) return (
    <LifestyleScreen
      healthLogs={healthLogs}
      fetchWithAuth={fetchWithAuth}
      onReloadLogs={fetchHealthLogs}
      onBack={() => { setHealthView(false); fetchHealthLogs() }}
    />
  )

  return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <input ref={photoInputRef} type="file" accept="image/*" multiple style={{ display:'none' }}
        onChange={e => { uploadWeeklyPhoto(e.target.files); e.target.value = '' }} />
      <div style={{ background:'#fff', padding:'3rem 1.2rem 1rem', borderBottom:`1px solid ${G.border}` }}>
        <div style={{ fontFamily:MS, fontSize:'1.4rem', color:G.g900 }}>My Care</div>
        <div style={{ fontSize:'.78rem', color:G.mute, marginTop:3 }}>Your long-term skin health</div>
      </div>

      <div style={{ padding:'1rem 1.1rem', display:'flex', flexDirection:'column', gap:'1rem' }}>

        {/* ── Adaptive status banner ── */}
        <div style={{ background:'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)', borderRadius:20, padding:'1.3rem 1.4rem', color:'#fff', position:'relative', overflow:'hidden', boxShadow:'0 8px 28px rgba(15,61,36,.28)' }}>
          {status.emoji && <div style={{ position:'absolute', top:-20, right:-15, fontSize:'5rem', opacity:.18 }}>{status.emoji}</div>}
          <div style={{ fontSize:'1.15rem', fontWeight:800, position:'relative' }}>{status.emoji ? `${status.emoji} ` : ''}{status.title}</div>
          <div style={{ fontSize:'.8rem', color:'rgba(255,255,255,.95)', marginTop:6, lineHeight:1.5, position:'relative', maxWidth:'88%' }}>{status.body}</div>
          {status.sub && <div style={{ fontSize:'.78rem', color:'rgba(255,255,255,.88)', marginTop:4, lineHeight:1.5, position:'relative', maxWidth:'88%', fontStyle:'italic' }}>{status.sub}</div>}
          {status.nextStep && (
            <div style={{ display:'inline-flex', alignItems:'center', gap:7, marginTop:12, background:'rgba(255,255,255,.22)', borderRadius:999, padding:'.4rem .85rem', fontSize:'.76rem', fontWeight:700, position:'relative' }}>
              👉 Next: {status.nextStep}
            </div>
          )}
        </div>

        {/* ── Skin Health Score (ring) ── */}
        <Card>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:'.72rem', color:G.mute, fontWeight:600 }}>Skin Health Score</div>
              <div style={{ display:'flex', alignItems:'baseline', gap:4, margin:'2px 0' }}>
                <span style={{ fontSize:'2.2rem', fontWeight:900, color:scoreColor, lineHeight:1 }}>{score.total}</span>
                <span style={{ fontSize:'.7rem', color:G.mute }}>/ 100</span>
              </div>
              <div style={{ fontWeight:800, fontSize:'1rem', color:scoreColor }}>{scoreLabel}</div>
              <div style={{ fontSize:'.68rem', color:G.mute, marginTop:2 }}>
                {lastPhotoTs ? `Updated ${daysSincePhoto === 0 ? 'today' : `${daysSincePhoto} day${daysSincePhoto===1?'':'s'} ago`}` : 'Add your first progress photo'}
              </div>
            </div>
            <div style={{ position:'relative', width:94, height:94, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Ring value={score.total} color={scoreColor} />
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.3rem' }}>✨</div>
            </div>
          </div>

          <button onClick={() => setScoreOpen(o=>!o)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', fontSize:'.75rem', color:G.g600, fontWeight:700, fontFamily:MF, marginTop:'1rem' }}>
            {scoreOpen ? '▲' : '▼'} How it's calculated
          </button>
          {scoreOpen && (
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:'.75rem' }}>
              {[['📸 Progress Photos',score.photos,45],['🏃 Lifestyle',score.lifestyle,30],['🩺 Doctor review',score.doctor,25]].map(([label,val,max]) => (
                <div key={label}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.72rem', color:G.text, marginBottom:3 }}>
                    <span>{label}</span><span style={{ color:G.mute }}>{val}/{max}</span>
                  </div>
                  <div style={{ height:5, background:G.g100, borderRadius:999 }}>
                    <div style={{ height:'100%', width:`${(val/max)*100}%`, background:G.g300, borderRadius:999 }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ background:G.g50, borderRadius:12, padding:'.75rem .9rem', fontSize:'.78rem', color:G.text, lineHeight:1.55, borderLeft:`3px solid ${G.g500}`, marginTop:'.85rem' }}>
            💚 {insight}
          </div>
        </Card>

        {/* ── Today's Skin Tip (real UV + medication) ── */}
        {skinTip && (
          <Card>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.75rem' }}>
              <div style={{ fontWeight:800, fontSize:'1rem', color:G.text }}>Today's Skin Tip</div>
              <span style={{ fontSize:'.6rem', fontWeight:700, color:G.g600, background:G.g100, padding:'.15rem .5rem', borderRadius:999 }}>🩺 Dermatology-guided</span>
            </div>
            <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
              <span style={{ fontSize:'1.9rem', lineHeight:1 }}>{skinTip.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:800, color:G.text, fontSize:'.95rem' }}>{skinTip.headline}</div>
                <div style={{ fontSize:'.82rem', color:G.text, marginTop:4, lineHeight:1.55 }}>{skinTip.body}</div>
                {skinTip.medNote && <div style={{ fontSize:'.8rem', color:G.g700, marginTop:8, lineHeight:1.55, background:G.g50, borderRadius:10, padding:'.6rem .75rem', borderLeft:`3px solid ${G.g500}` }}>💊 {skinTip.medNote}</div>}
              </div>
            </div>
            <div style={{ fontSize:'.64rem', color:G.mute, marginTop:'.85rem', lineHeight:1.5, borderTop:`1px solid ${G.border}`, paddingTop:'.6rem' }}>
              Based on {skinTip.source}. General guidance — check with your doctor for concerns.
            </div>
          </Card>
        )}

        {/* ── Progress Photos preview ── */}
        {(() => {
          const thumbs = weekPhotos.length <= 3 ? weekPhotos : [weekPhotos[0], weekPhotos[Math.floor(weekPhotos.length/2)], weekPhotos[weekPhotos.length-1]]
          // Only call it before/after when the oldest & newest are a real time gap apart (≥7 days)
          const spanDays = (thumbs.length > 1 && thumbs[0].ts && thumbs[thumbs.length-1].ts) ? Math.floor((thumbs[thumbs.length-1].ts - thumbs[0].ts) / 86400000) : 0
          const showBeforeAfter = spanDays >= 7
          const nextDue = lastPhotoTs == null ? 'Add now' : (7 - daysSincePhoto) <= 0 ? 'Due now' : (7 - daysSincePhoto) === 1 ? 'Tomorrow' : `In ${7 - daysSincePhoto} days`
          const dueColor = lastPhotoTs == null || (7 - daysSincePhoto) <= 1 ? '#B7770D' : G.g600
          return (
            <div onClick={() => setPane('photos')} style={{ background:'#fff', borderRadius:20, border:`1px solid ${G.border}`, padding:'1.2rem', cursor:'pointer' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: progressPhotos.length ? '.9rem' : 0 }}>
                <div style={{ fontWeight:800, fontSize:'1rem', color:G.text }}>📸 Progress Photos</div>
                <span style={{ color:G.mute, fontSize:'1.15rem' }}>›</span>
              </div>

              {progressPhotos.length === 0 ? (
                <div style={{ fontSize:'.78rem', color:G.mute, lineHeight:1.5 }}>Add your first photo to start tracking your skin improvement.</div>
              ) : (
                <>
                  <div style={{ display:'flex', gap:10, marginBottom:'1rem' }}>
                    {thumbs.map((p, i) => (
                      <div key={p.file} style={{ flex:1 }}>
                        <div style={{ position:'relative', width:'100%', paddingBottom:'100%', borderRadius:12, overflow:'hidden', border:`1px solid ${G.border}`, background:G.g50 }}>
                          <img src={photoUrl(p.file)} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.style.display='none' }} />
                          {showBeforeAfter && i === 0 && <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,.5)', color:'#fff', fontSize:'.55rem', fontWeight:700, textAlign:'center', padding:'2px 0' }}>BEFORE</div>}
                          {showBeforeAfter && i === thumbs.length-1 && <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(45,107,66,.75)', color:'#fff', fontSize:'.55rem', fontWeight:700, textAlign:'center', padding:'2px 0' }}>AFTER</div>}
                        </div>
                        <div style={{ fontSize:'.62rem', color:G.mute, textAlign:'center', marginTop:4, fontWeight:600 }}>{p.week !== null ? `Week ${p.week}` : `#${i+1}`}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:'flex', gap:12 }}>
                    <div style={{ flex:1, background:G.g50, borderRadius:12, padding:'.65rem .8rem' }}>
                      <div style={{ fontSize:'.6rem', color:G.mute, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:600 }}>Last photo</div>
                      <div style={{ fontWeight:800, color:G.text, fontSize:'.85rem', marginTop:2 }}>{daysSincePhoto === 0 ? 'Today' : `${daysSincePhoto} day${daysSincePhoto===1?'':'s'} ago`}</div>
                    </div>
                    <div style={{ flex:1, background:G.g50, borderRadius:12, padding:'.65rem .8rem' }}>
                      <div style={{ fontSize:'.6rem', color:G.mute, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:600 }}>Next photo due</div>
                      <div style={{ fontWeight:800, color:dueColor, fontSize:'.85rem', marginTop:2 }}>{nextDue}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        })()}

        {/* ── Treatment plan card ── */}
        {appointments.length > 0 && (() => {
          const featured = appointments.slice().sort((a,b)=>new Date(b.date)-new Date(a.date))[0]
          const dxWord = (featured.diagnosis||'').trim().split(/[\s,]+/)[0]
          const planName = dxWord ? `${dxWord.charAt(0).toUpperCase()+dxWord.slice(1)} Recovery Plan` : (featured.service || 'Care Plan')
          const consultDone = featured.status === 'completed'
          const cancelled = featured.status === 'cancelled'
          const startISO = String(featured.date||'').slice(0,10)
          const elapsed = (() => { try { return Math.floor((Date.now() - new Date(startISO+'T00:00:00'))/86400000) + 1 } catch { return null } })()
          // Validate plan length — ignore absurd parses (the old "1350 days" bug)
          const parsedMax = (() => { const m = parseMedication(featured.medication || featured.prescription || ''); return m.length ? Math.max(...m.map(x=>x.durationDays)) : null })()
          const planDays = (parsedMax && parsedMax >= 3 && parsedMax <= 180) ? parsedMax : null
          // Doctor completing the consult ≠ plan finished — stay Active while doses/follow-up remain
          const fuPending = featured.followUpDate && String(featured.followUpDate).slice(0,10) >= todayISO()
          const planActive = consultDone && ((planDays != null && elapsed != null && elapsed <= planDays) || fuPending)
          const done = consultDone && !planActive
          const sColor = (done || planActive) ? G.g500 : featured.status==='confirmed' ? '#3451B2' : cancelled ? '#C0392B' : '#B7770D'
          const sLabel = done ? 'Completed' : cancelled ? 'Cancelled' : 'Active'
          const completedISO = featured.followUpDate ? String(featured.followUpDate).slice(0,10) : (planDays ? addDays(startISO, planDays) : null)
          const InfoRow = ({ label, value, color }) => (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'.6rem 0', borderTop:`1px solid ${G.border}` }}>
              <span style={{ fontSize:'.78rem', color:G.mute }}>{label}</span>
              <span style={{ fontSize:'.85rem', fontWeight:700, color:color||G.text }}>{value}</span>
            </div>
          )
          return (
            <div style={{ background:'#fff', borderRadius:20, border:`1px solid ${G.border}`, overflow:'hidden' }}>
              <div onClick={() => onOpenDetail(featured, 'details')} style={{ padding:'1.2rem', cursor:'pointer' }}>
                <div style={{ fontSize:'.66rem', color:G.mute, textTransform:'uppercase', letterSpacing:'.06em', fontWeight:700 }}>{done ? 'Treatment' : 'Current Plan'}</div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginTop:4, marginBottom: done ? '.4rem' : '.2rem' }}>
                  <div style={{ fontWeight:800, fontSize:'1.15rem', color:G.text }}>{planName}</div>
                  <span style={{ fontSize:'.66rem', fontWeight:700, color:sColor, background:`${sColor}18`, padding:'.2rem .6rem', borderRadius:999, whiteSpace:'nowrap' }}>{sLabel}</span>
                </div>

                {!done && !cancelled && elapsed != null && elapsed >= 1 && (
                  <div style={{ margin:'.3rem 0 .5rem' }}>
                    <span style={{ fontSize:'1.7rem', fontWeight:900, color:G.g700 }}>Day {planDays ? Math.min(elapsed, planDays) : elapsed}</span>
                    {planDays && <span style={{ fontSize:'.95rem', color:G.mute, fontWeight:600 }}> of {planDays}</span>}
                  </div>
                )}

                {done ? (
                  <>
                    <InfoRow label="Started" value={fmtDate(startISO)} />
                    <InfoRow label="Completed" value={completedISO ? fmtDate(completedISO) : '—'} />
                    <InfoRow label="Doctor" value={featured.doctor || 'Your dermatologist'} />
                  </>
                ) : (
                  <>
                    <InfoRow label="Status" value={cancelled ? 'Cancelled' : 'Active'} color={cancelled ? '#C0392B' : G.g600} />
                    <InfoRow label="Started" value={fmtDate(startISO)} />
                    {fuPending && <InfoRow label="Next follow-up" value={fmtDate(featured.followUpDate)} color={G.g600} />}
                    <InfoRow label="Doctor" value={featured.doctor || 'Your dermatologist'} />
                  </>
                )}

                <div style={{ marginTop:'.9rem', color:G.g700, fontWeight:800, fontSize:'.85rem' }}>View Report →</div>
              </div>
              {appointments.length > 1 && (
                <button onClick={() => setPane('history')} style={{ width:'100%', background:G.g50, border:'none', borderTop:`1px solid ${G.border}`, padding:'.75rem', fontWeight:700, fontSize:'.8rem', color:G.g700, cursor:'pointer', fontFamily:MF }}>
                  View all {appointments.length} consultations
                </button>
              )}
            </div>
          )
        })()}

        {/* ── Lifestyle Insights preview ── */}
        <div onClick={() => setHealthView(true)} style={{ background:'#fff', borderRadius:20, border:`1px solid ${G.border}`, padding:'1.2rem', cursor:'pointer' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: lifeAvg ? '.9rem' : 0 }}>
            <div style={{ fontWeight:800, fontSize:'1rem', color:G.text }}>📊 Lifestyle Insights</div>
            <span style={{ color:G.mute, fontSize:'1.15rem' }}>›</span>
          </div>

          {!lifeAvg ? (
            <div style={{ fontSize:'.78rem', color:G.mute, lineHeight:1.5 }}>Log your daily habits — sleep, water, stress &amp; sun — to see how they affect your skin.</div>
          ) : (
            <>
              <div style={{ display:'flex', gap:8 }}>
                {[
                  ['🚶', avgSteps != null ? avgSteps.toLocaleString() : '—', 'avg steps'],
                  ['😴', lifeAvg.sleep != null ? `${lifeAvg.sleep}h` : '—', 'avg sleep'],
                  ['💧', lifeAvg.water != null ? `${lifeAvg.water}` : '—', 'glasses'],
                  ['😌', lifeAvg.stress || '—', 'stress'],
                ].map(([icon, val, label]) => (
                  <div key={label} style={{ flex:1, background:G.g50, borderRadius:12, padding:'.6rem .25rem', textAlign:'center' }}>
                    <div style={{ fontSize:'1rem' }}>{icon}</div>
                    <div style={{ fontWeight:800, color:G.text, fontSize:'.88rem', marginTop:2 }}>{val}</div>
                    <div style={{ fontSize:'.54rem', color:G.mute, textTransform:'uppercase', letterSpacing:'.03em', fontWeight:600, marginTop:1 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginTop:'.9rem', fontSize:'.74rem', fontWeight:700, color: todayLogged ? G.g600 : '#B7770D' }}>
                {todayLogged ? '✓ Today’s check-in logged' : '● Log today’s check-in →'}
              </div>
            </>
          )}
        </div>

        {/* ── Next Review card (calm when far, escalates within 14 days) ── */}
        {(() => {
          const dx = (appointments.find(a => a.diagnosis)?.diagnosis || '').toLowerCase()
          const cond = /acne/.test(dx) ? 'acne treatments should be reviewed every 3 months to adjust your plan and prevent scarring'
            : /(pigment|melasma)/.test(dx) ? 'pigmentation needs periodic review to track fading and prevent recurrence'
            : /(psorias|eczema|dermatit)/.test(dx) ? 'chronic skin conditions should be reviewed regularly to control flare-ups'
            : /(hair|alopecia|dandruff)/.test(dx) ? 'hair treatments need regular reviews to keep the plan on track'
            : 'a review every 3–6 months keeps your skin healthy'

          // Dynamic recommendation, driven automatically by the doctor's latest weekly verdict
          const rec = (() => {
            const future = appointments.filter(a => ['pending','confirmed'].includes(a.status) && new Date(a.date) > new Date())
            if (future.length) { const n = future.sort((a,b)=>new Date(a.date)-new Date(b.date))[0]; return { date:n.date, tone:'booked', doctor:n.doctor||'Your dermatologist' } }
            // Plan still active with a doctor-set follow-up → show that date, not a "plan complete" recommendation
            // (a 'worse'/'same' verdict below still wins, since the doctor wants an earlier review)
            const latestDone = appointments.filter(a => a.status==='completed').sort((a,b)=>new Date(b.date)-new Date(a.date))[0]
            const fuISO = latestDone?.followUpDate ? String(latestDone.followUpDate).slice(0,10) : null
            if (fuISO && fuISO >= todayISO() && planIsActive(latestDone) && (!lastReview || lastReview.verdict === 'improving')) {
              return { date: fuISO, tone:'followup', doctor: latestDone.doctor||'Your dermatologist' }
            }
            if (lastReview) {
              const days = { worse:7, same:28, improving:90 }[lastReview.verdict] ?? 90
              const d = new Date(lastReview.createdAt); d.setDate(d.getDate()+days)
              return { date: d.toISOString().slice(0,10), tone:lastReview.verdict, doctor:lastReview.doctorName||'Your dermatologist' }
            }
            const done = appointments.filter(a => a.status==='completed')
            if (done.length) { const l = done.sort((a,b)=>new Date(b.date)-new Date(a.date))[0]; const d=new Date(l.date); d.setDate(d.getDate()+90); return { date:d.toISOString().slice(0,10), tone:'default', doctor:l.doctor||'Your dermatologist' } }
            return null
          })()
          if (!rec) return null
          const dl = Math.max(0, Math.ceil((new Date(rec.date+'T00:00:00') - Date.now())/86400000))
          const T = {
            worse:     { color:'#C0392B', bg:'#FFF0F0', border:'#F5C6C6', dot:'#C0392B', chip:'Review Soon', reason:`Your dermatologist flagged that your skin needs attention in the latest review — an earlier check-up is recommended.`, cta:'Book Review Now', filled:true, urgent:true },
            same:      { color:'#B7770D', bg:'#FFF8E1', border:'#FFE082', dot:'#E0A400', chip:'Recommended', reason:`Your progress is steady. A review keeps your plan on track and catches any change early.`, cta:'Book Review →', filled:true, urgent:true },
            improving: { color:G.g600, bg:G.g50, border:G.border, dot:G.g500, chip:'On Track', reason:`Your skin is improving. A routine review every 3 months helps maintain results, detect early recurrence, and adjust your skincare if needed.`, cta:'Book Review →', filled:false, urgent:false },
            default:   { color:G.g600, bg:G.g50, border:G.border, dot:'#E0A400', chip:'Recommended', reason:`Your treatment is complete — ${cond}.`, cta:'Book Review →', filled:false, urgent:false },
            booked:    { color:G.g700, bg:G.g100, border:G.border, dot:G.g500, chip:'Booked', reason:`Your review is booked. ${cond.charAt(0).toUpperCase()+cond.slice(1)}.`, cta:'View Appointment', filled:dl<=7, urgent:dl<=7 },
            followup:  { color:G.g600, bg:G.g50, border:G.border, dot:G.g500, chip:'Scheduled', reason:`Your plan is active. Your doctor set this follow-up to check progress and adjust the treatment if needed.`, cta:'Book Follow-up →', filled:dl<=7, urgent:dl<=7 },
          }
          const t = T[rec.tone] || T.default
          return (
            <div style={{ background:'#fff', borderRadius:20, border:`1px solid ${t.border}`, padding:'1.2rem', boxShadow: t.urgent ? '0 2px 12px rgba(0,0,0,.06)' : 'none' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.85rem' }}>
                <div style={{ fontWeight:800, fontSize:'1rem', color:G.text }}>{rec.tone==='followup' ? '🩺 Upcoming Follow-up' : '🩺 Care Recommendation'}</div>
                <span style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:'.7rem', fontWeight:800, color:t.color, background:t.bg, border:`1px solid ${t.border}`, padding:'.25rem .7rem', borderRadius:999 }}>
                  <span style={{ width:7, height:7, borderRadius:'50%', background:t.dot, display:'inline-block' }} />{t.chip}
                </span>
              </div>
              <div style={{ fontSize:'.62rem', color:G.mute, textTransform:'uppercase', letterSpacing:'.06em', fontWeight:700 }}>{rec.tone==='followup' ? 'Next Follow-up' : 'Recommended Review'}</div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginTop:2 }}>
                <span style={{ fontFamily:MS, fontSize:'1.5rem', color:G.g900 }}>{fmtNice(rec.date)}</span>
                <span style={{ fontSize:'.78rem', color:G.mute }}>🧑‍⚕️ {rec.doctor}</span>
              </div>
              <div style={{ background:t.bg, borderRadius:12, padding:'.8rem .9rem', marginTop:'.9rem' }}>
                <div style={{ fontSize:'.7rem', color:G.text, fontWeight:800, marginBottom:6 }}>{rec.tone==='followup' ? 'Why this follow-up?' : 'Why this recommendation?'}</div>
                <div style={{ display:'flex', gap:8, fontSize:'.8rem', color:G.text, lineHeight:1.55 }}><span style={{ color:t.color, fontWeight:800 }}>{rec.tone==='worse'?'⚠':'✓'}</span><span>{t.reason}</span></div>
              </div>
              <button onClick={() => navigate('/book-appointment')} style={{ width:'100%', marginTop:'.9rem', background: t.filled?(rec.tone==='worse'?'#C0392B':G.g700):'#fff', color: t.filled?'#fff':G.g700, border:`1.5px solid ${rec.tone==='worse'?'#C0392B':G.g500}`, borderRadius:12, padding:'.8rem', fontWeight:800, fontSize:'.88rem', cursor:'pointer', fontFamily:MF }}>
                {t.cta}
              </button>
              {lastReview && (
                <div style={{ fontSize:'.64rem', color:G.mute, marginTop:'.7rem', textAlign:'center' }}>
                  Based on your doctor's latest review · {fmtNice(lastReview.createdAt)}
                </div>
              )}
            </div>
          )
        })()}

      </div>
    </div>
  )
}

// ─── LIFESTYLE INSIGHTS (My Care → Lifestyle Insights) ────────────────────────
const MOOD_OPTS   = [['great','😄'],['good','🙂'],['ok','😐'],['low','🙁'],['bad','😣']]
const STRESS_OPTS = [['low','😄'],['mild','🙂'],['medium','😐'],['high','🙁'],['severe','😣']]
const SUN_OPTS    = ['low','medium','high']
const moodLabel   = m => ({great:'Great',good:'Good',ok:'Okay',low:'Low',bad:'Bad'}[m] || '—')
const stressLabel = s => ({low:'Low',mild:'Mild',medium:'Medium',high:'High',severe:'Severe'}[s] || '—')
const sunLabel    = s => s ? s.charAt(0).toUpperCase()+s.slice(1) : '—'

function LifestyleRing({ value, color, size=96, stroke=10 }) {
  const r = (size-stroke)/2, c = 2*Math.PI*r
  const off = c - (Math.max(0,Math.min(100,value))/100)*c
  return (
    <svg width={size} height={size} style={{ transform:'rotate(-90deg)', flexShrink:0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={G.g100} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition:'stroke-dashoffset .5s' }} />
    </svg>
  )
}

function LifestyleScreen({ healthLogs, fetchWithAuth, onBack, onReloadLogs }) {
  const [pane, setPane] = React.useState('overview')  // overview | steps | checkin
  const today = todayISO()
  const todayLog = React.useMemo(() => healthLogs.find(l => l.date === today) || null, [healthLogs, today])
  const [form, setForm] = React.useState({ waterGlasses:0, mood:'', stressLevel:'', sunExposure:'' })
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)

  React.useEffect(() => {
    if (todayLog) setForm({ waterGlasses: todayLog.waterGlasses||0, mood: todayLog.mood||'', stressLevel: todayLog.stressLevel||'', sunExposure: todayLog.sunExposure||'' })
  }, [todayLog])

  const set = (k,v) => setForm(p => ({ ...p, [k]: v }))
  const save = async () => {
    setSaving(true); setSaved(false)
    try {
      await fetchWithAuth(`${API_BASE}/api/health/log`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ date: today, ...form }) })
      setSaved(true); setTimeout(()=>setSaved(false), 2200); onReloadLogs && onReloadLogs()
    } catch {} finally { setSaving(false) }
  }

  const wellness = React.useMemo(() => {
    let s = 0
    s += Math.min(30, Math.round((form.waterGlasses/8)*30))
    s += ({great:30,good:24,ok:16,low:8,bad:4}[form.mood] ?? 0)
    s += ({low:30,mild:24,medium:16,high:8,severe:4}[form.stressLevel] ?? 0)
    return Math.min(100, s)
  }, [form])

  // Step history from the phone (for the walking week-over-week insight)
  const [stepHist, setStepHist] = React.useState([])
  React.useEffect(() => {
    try {
      const { StepCounter } = require('../native/stepCounter')
      StepCounter && StepCounter.getHistory && StepCounter.getHistory({ days: 14 }).then(r => setStepHist(r?.history || [])).catch(() => {})
    } catch (e) {}
  }, [])

  // Week-over-week lifestyle analysis
  const weekly = React.useMemo(() => {
    const dAgo = d => { try { return Math.floor((Date.now() - new Date(String(d).slice(0,10)+'T00:00:00')) / 86400000) } catch { return 999 } }
    const thisW = healthLogs.filter(l => dAgo(l.date) <= 6)
    const lastW = healthLogs.filter(l => { const x = dAgo(l.date); return x >= 7 && x <= 13 })
    const moodNum = m => ({ great:5, good:4, ok:3, low:2, bad:1 }[m] ?? null)
    const stressNum = s => ({ low:5, mild:4, medium:3, high:2, severe:1 }[s] ?? null) // higher = calmer
    const avg = (logs, fn) => { const v = logs.map(fn).filter(x => x != null && !isNaN(x)); return v.length ? v.reduce((a,b)=>a+b,0)/v.length : null }
    const sThisW = stepHist.filter(h => dAgo(h.date) <= 6)
    const sLastW = stepHist.filter(h => { const x = dAgo(h.date); return x >= 7 && x <= 13 })

    const metrics = [
      { key:'Sleep',   now: avg(thisW, l=>parseFloat(l.sleepHours)),      prev: avg(lastW, l=>parseFloat(l.sleepHours)) },
      { key:'Water',   now: avg(thisW, l=>parseFloat(l.waterGlasses)),    prev: avg(lastW, l=>parseFloat(l.waterGlasses)) },
      { key:'Walking', now: avg(sThisW, h=>h.steps),                       prev: avg(sLastW, h=>h.steps) },
      { key:'Mood',    now: avg(thisW, l=>moodNum(l.mood)),               prev: avg(lastW, l=>moodNum(l.mood)) },
      { key:'Stress',  now: avg(thisW, l=>stressNum(l.stressLevel)),      prev: avg(lastW, l=>stressNum(l.stressLevel)) },
    ]
    const bullets = metrics.map(m => {
      if (m.now == null || m.prev == null || !m.prev) return null // need both weeks to compare
      const rel = (m.now - m.prev) / m.prev
      if (Math.abs(rel) < 0.08) return { ok: true, text: `${m.key} steady` }
      const improved = m.now > m.prev
      const verb = m.key === 'Water' ? (improved ? 'intake up' : 'intake down')
                 : m.key === 'Walking' ? (improved ? 'up this week' : 'reduced this week')
                 : m.key === 'Stress' ? (improved ? 'calmer this week' : 'higher this week')
                 : (improved ? 'improved' : 'dropped')
      return { ok: improved, text: `${m.key} ${verb}` }
    }).filter(Boolean)

    // Weekly lifestyle score from this week's logged habits
    const w = avg(thisW, l=>parseFloat(l.waterGlasses)), mo = avg(thisW, l=>moodNum(l.mood)), st = avg(thisW, l=>stressNum(l.stressLevel))
    let s = 0, parts = 0
    if (w != null)  { s += Math.min(40, Math.round(w/8*40)); parts++ }
    if (mo != null) { s += Math.round((mo/5)*30); parts++ }
    if (st != null) { s += Math.round((st/5)*30); parts++ }
    const score = parts ? Math.min(100, s) : null
    return { bullets, score }
  }, [healthLogs, stepHist])
  const wLabel = wellness >= 80 ? 'Excellent' : wellness >= 60 ? 'Good' : wellness >= 40 ? 'Fair' : 'Getting Started'
  const wColor = wellness >= 80 ? G.g500 : wellness >= 60 ? '#5AAE73' : wellness >= 40 ? '#B7770D' : G.mute

  const Header = ({ title, subtitle, back }) => (
    <div style={{ background:'#fff', padding:'3rem 1.2rem 1rem', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', gap:12 }}>
      <button onClick={back} style={{ background:'none', border:'none', fontSize:'1.4rem', color:G.g900, cursor:'pointer', padding:0, lineHeight:1 }}>‹</button>
      <div style={{ flex:1 }}>
        <div style={{ fontFamily:MS, fontSize:'1.3rem', color:G.g900 }}>{title}</div>
        {subtitle && <div style={{ fontSize:'.74rem', color:G.mute, marginTop:2 }}>{subtitle}</div>}
      </div>
    </div>
  )
  const LsRow = ({ icon, title, value, onClick }) => (
    <button onClick={onClick} style={{ display:'flex', alignItems:'center', gap:12, width:'100%', background:'#fff', border:`1px solid ${G.border}`, borderRadius:14, padding:'.95rem 1.1rem', cursor:'pointer', fontFamily:MF, textAlign:'left', marginBottom:10 }}>
      <span style={{ fontSize:'1.2rem' }}>{icon}</span>
      <span style={{ flex:1, fontWeight:700, color:G.text, fontSize:'.9rem' }}>{title}</span>
      <span style={{ fontWeight:700, color:G.g600, fontSize:'.82rem' }}>{value}</span>
      <span style={{ color:G.mute, marginLeft:4 }}>›</span>
    </button>
  )

  // ── STEPS (live device card) ──
  if (pane === 'steps') return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <Header title="Steps" subtitle="Live from your phone" back={() => setPane('overview')} />
      <div style={{ margin:'0 1rem' }}>
        <StepsCard />
      </div>
    </div>
  )

  // ── SLEEP (live device card) ──
  if (pane === 'sleep') return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <Header title="Sleep" subtitle="Estimated from phone movement" back={() => setPane('overview')} />
      <div style={{ margin:'0 1rem' }}>
        <SleepCard />
      </div>
    </div>
  )

  // ── DAILY CHECK-IN (Water + Stress + Mood + Sun in one) ──
  if (pane === 'checkin') {
    const insight = (() => {
      if (form.waterGlasses >= 6 && ['low','mild'].includes(form.stressLevel)) return 'Your hydration and stress levels look great. Keep it up!'
      if (form.waterGlasses < 4) return 'Try to drink more water — hydration helps your skin barrier stay healthy.'
      if (['high','severe'].includes(form.stressLevel)) return 'High stress can trigger breakouts. A short walk or good sleep can help.'
      return 'Log daily to see how your habits affect your skin over time.'
    })()
    const EmojiRow = ({ opts, value, onPick }) => (
      <div style={{ display:'flex', gap:8, justifyContent:'space-between' }}>
        {opts.map(([val,emo]) => (
          <button key={val} onClick={() => onPick(val)} style={{ flex:1, aspectRatio:'1', maxWidth:60, borderRadius:12, border:`2px solid ${value===val?G.g500:G.border}`, background:value===val?G.g100:'#fff', fontSize:'1.6rem', cursor:'pointer' }}>{emo}</button>
        ))}
      </div>
    )
    const secLbl = { fontSize:'.82rem', fontWeight:700, color:G.text, margin:'1.3rem 0 .6rem' }
    return (
      <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:120 }}>
        <Header title="Daily Check-in" subtitle="Log habits that affect your skin" back={() => setPane('overview')} />
        <div style={{ padding:'1.1rem' }}>
          <div style={{ fontSize:'.72rem', color:G.mute }}>Today · {fmtDate(today)}</div>

          <div style={{ ...secLbl, marginTop:'.6rem' }}>💧 Glasses of Water</div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#fff', border:`1px solid ${G.border}`, borderRadius:14, padding:'.7rem 1rem' }}>
            <span style={{ fontSize:'.9rem', color:G.text, fontWeight:700 }}>{form.waterGlasses} glasses</span>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <button onClick={() => set('waterGlasses', Math.max(0, form.waterGlasses-1))} style={{ width:36, height:36, borderRadius:'50%', border:`1.5px solid ${G.border}`, background:'#fff', fontSize:'1.3rem', cursor:'pointer', color:G.g700 }}>−</button>
              <button onClick={() => set('waterGlasses', form.waterGlasses+1)} style={{ width:36, height:36, borderRadius:'50%', border:'none', background:G.g700, color:'#fff', fontSize:'1.3rem', cursor:'pointer' }}>+</button>
            </div>
          </div>

          <div style={secLbl}>🌿 Stress Level</div>
          <EmojiRow opts={STRESS_OPTS} value={form.stressLevel} onPick={v => set('stressLevel', v)} />

          <div style={secLbl}>🙂 Mood</div>
          <EmojiRow opts={MOOD_OPTS} value={form.mood} onPick={v => set('mood', v)} />

          <div style={secLbl}>☀️ Sun Exposure</div>
          <div style={{ display:'flex', gap:8 }}>
            {SUN_OPTS.map(o => (
              <button key={o} onClick={() => set('sunExposure', o)} style={{ flex:1, padding:'.85rem', borderRadius:12, border:`1.5px solid ${form.sunExposure===o?G.g500:G.border}`, background:form.sunExposure===o?G.g700:'#fff', color:form.sunExposure===o?'#fff':G.text, fontWeight:700, fontSize:'.85rem', cursor:'pointer', fontFamily:MF }}>{sunLabel(o)}</button>
            ))}
          </div>

          <div style={{ background:G.g100, border:`1px solid ${G.g300}`, borderRadius:14, padding:'1rem', marginTop:'1.4rem' }}>
            <div style={{ fontWeight:800, color:G.g800, fontSize:'.82rem', marginBottom:5 }}>🤖 AI Insights</div>
            <div style={{ fontSize:'.8rem', color:G.text, lineHeight:1.6 }}>{insight}</div>
          </div>

          <button onClick={save} disabled={saving} style={{ width:'100%', marginTop:'1.2rem', background:saved?G.g500:G.g700, color:'#fff', border:'none', borderRadius:12, padding:'.9rem', fontWeight:800, fontSize:'.9rem', cursor:'pointer', fontFamily:MF }}>
            {saving ? 'Saving…' : saved ? '✓ Saved for today' : "Save today's check-in"}
          </button>
        </div>
      </div>
    )
  }

  // ── OVERVIEW (screen 6) ──
  return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <div style={{ background:'#fff', padding:'3rem 1.2rem 1rem', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', gap:12 }}>
        <button onClick={onBack} style={{ background:'none', border:'none', fontSize:'1.4rem', color:G.g900, cursor:'pointer', padding:0, lineHeight:1 }}>‹</button>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:MS, fontSize:'1.3rem', color:G.g900 }}>Lifestyle & Skin</div>
          <div style={{ fontSize:'.74rem', color:G.mute, marginTop:2 }}>See how your daily habits affect your skin</div>
        </div>
        <span style={{ fontSize:'1.8rem' }}>🧘‍♀️</span>
      </div>

      <div style={{ padding:'1.1rem' }}>
        {(() => {
          const lScore = weekly.score != null ? weekly.score : wellness
          const lColor = lScore >= 80 ? G.g500 : lScore >= 60 ? '#5AAE73' : lScore >= 40 ? '#B7770D' : G.mute
          const lLabel = lScore >= 80 ? 'Excellent' : lScore >= 60 ? 'Good' : lScore >= 40 ? 'Fair' : 'Getting Started'
          const aiInsight = (() => {
            const ups = weekly.bullets.filter(b => b.ok && !b.text.includes('steady')).length
            const downs = weekly.bullets.filter(b => !b.ok).length
            if (weekly.bullets.length === 0) return 'Log daily for a week to unlock personalised insights on how your habits affect your skin.'
            if (ups >= 2 && downs === 0) return 'Your routine is trending up. Consistent habits like these are linked to fewer breakouts. Keep it going.'
            if (downs >= 1) return 'A small dip this week. Steady sleep, water and lower stress are the habits most linked to calmer skin — an easy win to focus on.'
            return 'Nice consistency. Patients who keep steady habits tend to see fewer flare-ups over time.'
          })()
          return (
            <>
              {/* Lifestyle Score */}
              <div style={{ background:'#fff', border:`1px solid ${G.border}`, borderRadius:20, padding:'1.2rem', display:'flex', alignItems:'center', gap:16, marginBottom:'1rem' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'.72rem', color:G.mute, fontWeight:600 }}>Lifestyle Score · this week</div>
                  <div style={{ display:'flex', alignItems:'baseline', gap:4, margin:'2px 0' }}>
                    <span style={{ fontSize:'2.2rem', fontWeight:900, color:lColor, lineHeight:1 }}>{lScore}</span>
                    <span style={{ fontSize:'.7rem', color:G.mute }}>/ 100</span>
                  </div>
                  <div style={{ fontWeight:800, fontSize:'1rem', color:lColor }}>{lLabel}</div>
                </div>
                <div style={{ position:'relative', width:96, height:96, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <LifestyleRing value={lScore} color={lColor} />
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem' }}>💚</div>
                </div>
              </div>

              {/* This week's changes */}
              {weekly.bullets.length > 0 && (
                <div style={{ background:'#fff', border:`1px solid ${G.border}`, borderRadius:16, padding:'1rem 1.1rem', marginBottom:'1rem' }}>
                  <div style={{ fontSize:'.7rem', color:G.mute, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700, marginBottom:'.65rem' }}>This week vs last</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'.55rem' }}>
                    {weekly.bullets.map((b,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:'.85rem', color:G.text }}>
                        <span style={{ color: b.ok ? G.g500 : '#B7770D', fontWeight:800 }}>{b.ok ? '✓' : '⚠'}</span>
                        <span>{b.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Insight */}
              <div style={{ background:G.g100, border:`1px solid ${G.g300}`, borderRadius:16, padding:'1rem 1.1rem', marginBottom:'1.2rem' }}>
                <div style={{ fontWeight:800, color:G.g800, fontSize:'.82rem', marginBottom:5 }}>🤖 AI Insight</div>
                <div style={{ fontSize:'.82rem', color:G.text, lineHeight:1.6 }}>{aiInsight}</div>
              </div>
            </>
          )
        })()}

        <LsRow icon="🚶" title="Steps" value="View" onClick={() => setPane('steps')} />
        <LsRow icon="😴" title="Sleep" value="View" onClick={() => setPane('sleep')} />

        {/* Daily Check-in (Water · Stress · Mood · Sun in one) */}
        <button onClick={() => setPane('checkin')} style={{ display:'flex', alignItems:'center', gap:12, width:'100%', background:'#fff', border:`1px solid ${G.border}`, borderRadius:14, padding:'.95rem 1.1rem', cursor:'pointer', fontFamily:MF, textAlign:'left', marginBottom:10 }}>
          <span style={{ fontSize:'1.2rem' }}>📝</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:700, color:G.text, fontSize:'.9rem' }}>Daily Check-in</div>
            <div style={{ fontSize:'.72rem', color:G.mute, marginTop:2 }}>
              💧 {form.waterGlasses}/8 · 🌿 {stressLabel(form.stressLevel)} · 🙂 {moodLabel(form.mood)} · ☀️ {sunLabel(form.sunExposure)}
            </div>
          </div>
          <span style={{ color:G.mute, marginLeft:4 }}>›</span>
        </button>
      </div>
    </div>
  )
}

// ─── TREATMENT DETAIL SCREEN (opened from My Care → Treatment History) ─────────
function TreatmentDetailScreen({ appt, onBack, backLabel, onPhotosUpdated, onProgressPhotosUpdated }) {
  const [view, setView] = React.useState('summary')  // summary | meds | photos
  const consultDone = appt.status === 'completed'
  const dxWord = (appt.diagnosis||'').trim().split(/[\s,]+/)[0]
  const planName = dxWord ? `${dxWord.charAt(0).toUpperCase()+dxWord.slice(1)} Recovery Plan` : (appt.service || 'Care Plan')

  const meds = React.useMemo(() => structuredMeds(appt), [appt])

  const durationDays = React.useMemo(() => {
    const parsed = parseMedication([appt.medication||'', appt.prescription||''].join('\n'))
    const max = parsed.length ? Math.max(...parsed.map(m => m.durationDays)) : null
    // Ignore absurd parses (the "1350 days" bug); prefer real dates when possible
    if (appt.status === 'completed' && appt.followUpDate) {
      try { const d = Math.floor((new Date(String(appt.followUpDate).slice(0,10)) - new Date(String(appt.date).slice(0,10))) / 86400000); if (d >= 3 && d <= 365) return d } catch {}
    }
    return (max && max >= 3 && max <= 180) ? max : null
  }, [appt])

  const photoCount = React.useMemo(() => {
    const cnt = arr => { try { const v = typeof arr==='string'?JSON.parse(arr||'[]'):(Array.isArray(arr)?arr:[]); return v.length } catch { return 0 } }
    return cnt(appt.progressPhotos) + cnt(appt.photos)
  }, [appt])

  const adherence = React.useMemo(() => {
    try {
      const st = JSON.parse(localStorage.getItem(`rem-state-${appt.id}`) || '{}')
      const taken = Object.values(st).filter(Boolean).length
      const rems = generateReminders(appt)
      const today = todayISO()
      const elapsed = rems.filter(r => r.date <= today).length
      if (!elapsed) return null
      return Math.min(100, Math.round((taken / elapsed) * 100))
    } catch { return null }
  }, [appt])

  const startISO = String(appt.date || '').slice(0,10)
  // Consult marked complete but plan days / follow-up still remaining → treatment is active
  const elapsedDay = (() => { try { return Math.floor((Date.now() - new Date(startISO+'T00:00:00'))/86400000) + 1 } catch { return null } })()
  const fuPending = appt.followUpDate && String(appt.followUpDate).slice(0,10) >= todayISO()
  const planActive = consultDone && ((durationDays != null && elapsedDay != null && elapsedDay <= durationDays) || fuPending)
  const completed = consultDone && !planActive
  const sColor = (completed || planActive) ? G.g500 : appt.status==='confirmed' ? '#3451B2' : appt.status==='cancelled' ? '#C0392B' : '#B7770D'
  const sLabel = completed ? 'Completed' : planActive ? 'Active' : (appt.status ? appt.status.charAt(0).toUpperCase()+appt.status.slice(1) : 'Pending')
  const completedISO = completed ? (appt.followUpDate ? String(appt.followUpDate).slice(0,10) : (durationDays ? addDays(startISO, durationDays) : null)) : null
  const outcome = completed ? 'Completed' : appt.status === 'cancelled' ? '—' : 'In progress'

  const aiSummary = completed
    ? `Your treatment plan is complete. ${adherence !== null && adherence >= 80 ? 'You stayed consistent with your medication — great work.' : 'Keep up the healthy habits you built.'} Book a review if your skin needs attention.`
    : `Treatment is ongoing. Keep taking your medicines${adherence !== null ? ` — you're at ${adherence}% adherence` : ''} and add weekly photos to track progress.`

  const Stat = ({ label, value, color }) => (
    <div style={{ flex:1, textAlign:'center' }}>
      <div style={{ fontSize:'.62rem', color:'rgba(255,255,255,.55)', fontWeight:600, marginBottom:3 }}>{label}</div>
      <div style={{ fontWeight:800, color:color||'#fff', fontSize:'.92rem' }}>{value}</div>
    </div>
  )
  const SumRow = ({ icon, label, value, onClick, valueColor }) => (
    <button onClick={onClick} disabled={!onClick} style={{ display:'flex', alignItems:'center', gap:10, width:'100%', background:'none', border:'none', borderBottom:`1px solid ${G.border}`, padding:'.8rem 0', cursor:onClick?'pointer':'default', fontFamily:MF, textAlign:'left' }}>
      <span style={{ fontSize:'1rem' }}>{icon}</span>
      <span style={{ flex:1, fontSize:'.85rem', color:G.text }}>{label}</span>
      <span style={{ fontWeight:800, fontSize:'.85rem', color:valueColor||G.text }}>{value}</span>
      {onClick && <span style={{ color:G.mute, marginLeft:6 }}>›</span>}
    </button>
  )
  const MedCard = ({ m }) => (
    <div style={{ background:'#fff', border:`1px solid ${G.border}`, borderRadius:16, padding:'.95rem 1.1rem', marginBottom:10 }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
        <div style={{ width:36, height:36, borderRadius:10, background:G.g50, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', flexShrink:0 }}>{m.icon}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:800, color:G.text, fontSize:'.92rem' }}>{m.name}</div>
          <div style={{ fontSize:'.72rem', color:G.mute, marginTop:2 }}>{m.schedule}{m.durationDays?` · ${m.durationDays} days`:''}</div>
          <div style={{ fontSize:'.75rem', color:G.g700, marginTop:6, fontWeight:600 }}>{m.instruction}</div>
        </div>
      </div>
    </div>
  )

  // ── MEDICATIONS sub-view (screen 4) ──
  if (view === 'meds') return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <div style={{ background:'#fff', padding:'3rem 1.2rem 1rem', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', gap:12 }}>
        <button onClick={() => setView('summary')} style={{ background:'none', border:'none', fontSize:'1.4rem', color:G.g900, cursor:'pointer', padding:0, lineHeight:1 }}>‹</button>
        <div>
          <div style={{ fontFamily:MS, fontSize:'1.3rem', color:G.g900 }}>Medications</div>
          <div style={{ fontSize:'.74rem', color:G.mute, marginTop:2 }}>Prescribed medicines and usage</div>
        </div>
      </div>
      <div style={{ padding:'1.1rem' }}>
        {meds.count === 0 && <div style={{ textAlign:'center', color:G.mute, fontSize:'.82rem', padding:'2rem 0' }}>No medicines recorded for this treatment.</div>}
        {meds.topical.length > 0 && <>
          <div style={{ fontSize:'.7rem', fontWeight:700, color:G.mute, textTransform:'uppercase', letterSpacing:'.06em', margin:'0 0 .6rem' }}>Topical</div>
          {meds.topical.map((m,i) => <MedCard key={i} m={m} />)}
        </>}
        {meds.oral.length > 0 && <>
          <div style={{ fontSize:'.7rem', fontWeight:700, color:G.mute, textTransform:'uppercase', letterSpacing:'.06em', margin:'1rem 0 .6rem' }}>Oral Medicines</div>
          {meds.oral.map((m,i) => <MedCard key={i} m={m} />)}
        </>}
      </div>
    </div>
  )

  // ── PHOTOS sub-view ──
  if (view === 'photos') return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <div style={{ background:'#fff', padding:'3rem 1.2rem 1rem', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', gap:12 }}>
        <button onClick={() => setView('summary')} style={{ background:'none', border:'none', fontSize:'1.4rem', color:G.g900, cursor:'pointer', padding:0, lineHeight:1 }}>‹</button>
        <div style={{ fontFamily:MS, fontSize:'1.3rem', color:G.g900 }}>Photos</div>
      </div>
      <div style={{ padding:'1.1rem' }}>
        <DetailsTab appt={appt} hideDiagnosis onPhotosUpdated={onPhotosUpdated} onProgressPhotosUpdated={onProgressPhotosUpdated} />
      </div>
    </div>
  )

  // ── SUMMARY (screen 3) ──
  return (
    <div style={{ minHeight:'100vh', background:G.g50, fontFamily:MF, paddingBottom:100 }}>
      <div style={{ background:`linear-gradient(135deg,#1A3A28,#235233)`, padding:'3rem 1.2rem 1.3rem', color:'#fff' }}>
        <button onClick={onBack} style={{ background:'none', border:'none', fontSize:'1.5rem', color:'#fff', cursor:'pointer', padding:0, lineHeight:1, marginBottom:8 }}>‹</button>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ fontFamily:MS, fontSize:'1.4rem', lineHeight:1.15 }}>{planName}</div>
          <span style={{ fontSize:'.66rem', fontWeight:700, color:'#fff', background:sColor, padding:'.2rem .65rem', borderRadius:999 }}>{sLabel}</span>
        </div>
        <div style={{ fontSize:'.76rem', color:'rgba(255,255,255,.7)', marginTop:6 }}>📅 {fmtDate(appt.date)}{appt.doctor?` · ${appt.doctor}`:''}</div>
        {/* Stats strip */}
        <div style={{ display:'flex', gap:6, marginTop:16, borderTop:'1px solid rgba(255,255,255,.12)', paddingTop:14 }}>
          <Stat label="Duration" value={durationDays?`${durationDays}d`:'—'} />
          <Stat label="Adherence" value={adherence!==null?`${adherence}%`:'—'} />
          <Stat label="Photos" value={photoCount} />
          <Stat label="Outcome" value={outcome} color={completed?G.g300:'#fff'} />
        </div>
      </div>

      <div style={{ padding:'1.1rem', display:'flex', flexDirection:'column', gap:'1rem' }}>
        {appt.diagnosis && (
          <div>
            <div style={{ fontSize:'.82rem', fontWeight:800, color:G.text, marginBottom:8 }}>Diagnosis</div>
            <div style={{ background:'#fff', border:`1px solid ${G.border}`, borderRadius:14, padding:'.95rem 1.1rem', fontSize:'.88rem', color:G.text, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{appt.diagnosis}</div>
          </div>
        )}

        <div>
          <div style={{ fontSize:'.82rem', fontWeight:800, color:G.text, marginBottom:8 }}>Treatment Summary</div>
          <div style={{ background:'#fff', border:`1px solid ${G.border}`, borderRadius:16, padding:'.2rem 1.1rem' }}>
            <SumRow icon="💊" label="Medicines" value={meds.count} onClick={meds.count ? () => setView('meds') : undefined} />
            <SumRow icon="📅" label="Started on" value={fmtDate(startISO)} />
            {completedISO && <SumRow icon="✅" label="Completed on" value={fmtDate(completedISO)} />}
            {planActive && fuPending && <SumRow icon="📅" label="Next follow-up" value={fmtDate(appt.followUpDate)} valueColor={G.g600} />}
            {photoCount > 0 && <SumRow icon="📸" label="Photos" value={`${photoCount} uploaded`} onClick={() => setView('photos')} />}
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'.8rem 0' }}>
              <span style={{ fontSize:'1rem' }}>🎯</span>
              <span style={{ flex:1, fontSize:'.85rem', color:G.text }}>Outcome</span>
              <span style={{ fontWeight:800, fontSize:'.85rem', color:completed?G.g600:G.text }}>{outcome}</span>
            </div>
          </div>
        </div>

        {(appt.prescription || appt.medication || completed) && (
          <div style={{ background:G.g100, border:`1px solid ${G.g300}`, borderRadius:16, padding:'1.1rem' }}>
            <div style={{ fontWeight:800, color:G.g800, fontSize:'.88rem', marginBottom:6 }}>🤖 AI Treatment Summary</div>
            <div style={{ fontSize:'.82rem', color:G.text, lineHeight:1.6 }}>{aiSummary}</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function PatientDashboard({ onAdd }) {
  const { user, fetchWithAuth } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [activeTab, setActiveTab] = useState('details')
  const [unbookedScan, setUnbookedScan] = useState(null)
  const [allScans, setAllScans]         = useState([])
  const [patientNotifs, setPatientNotifs] = useState([])
  const [searchParams] = useSearchParams()
  const [mobileView, setMobileView] = useState(() => searchParams.get('view') === 'care' ? 'list' : 'home')
  const [fromTile, setFromTile] = useState(false) // opened via a home tile → show only that section
  const [premium, setPremium] = useState(false)   // voice reminders subscription
  const [premiumPrice, setPremiumPrice] = useState(99)
  const premiumRef = useRef(false)
  const nativeApp = isNativeApp()

  // Reset to correct view on every tab-bar navigation, even if the URL hasn't changed
  useEffect(() => {
    setMobileView(searchParams.get('view') === 'care' ? 'list' : 'home')
    setSelected(null)
    setFromTile(false)
  }, [location.key])

  // Derive the "Current Treatment" summary for the mobile home screen.
  const treatment = useMemo(() => {
    const withRx = appointments.filter(a => a.prescription || a.medication)
    if (!withRx.length) return null
    const appt = withRx.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0]
    const meds = parseMedication(appt.medication || appt.prescription || '')
    const planDays = meds.length ? Math.max(...meds.map(m => m.durationDays)) : 20
    const start = new Date(String(appt.date || todayISO()).slice(0, 10) + 'T00:00:00')
    const dayNum = Math.min(planDays, Math.max(1, Math.floor((Date.now() - start.getTime()) / 86400000) + 1))
    return { appt, name: appt.service || 'Treatment', planDays, dayNum, pct: Math.round((dayNum / planDays) * 100) }
  }, [appointments])

  // Web dashboard: if the patient is on active medication, open that appointment's
  // detail view by default instead of the empty reminders panel. Runs once per visit
  // (per location.key) so it never fights a manual selection or the reminders view
  // (opened via the "Upload Prescription" button, which sets selected=null).
  const autoSelectedFor = useRef(null)
  useEffect(() => {
    if (nativeApp) return                                   // native app has its own home
    if (autoSelectedFor.current === location.key) return    // already auto-selected this visit
    // "In medication" = has an ACTIVE plan (same signal as the "Active Plan" badge),
    // not merely any past prescription. Open the most recent active-plan appointment.
    const activeAppt = appointments.filter(planIsActive).sort((a, b) => new Date(b.date) - new Date(a.date))[0]
    if (!activeAppt) return                                 // not in medication → keep reminders default
    autoSelectedFor.current = location.key
    setSelected(activeAppt)
    setActiveTab('details')
    setFromTile(false)
  }, [nativeApp, appointments, location.key])

  // ── Premium (voice reminders) subscription status ──
  useEffect(() => {
    let alive = true
    fetchSubscriptionStatus(fetchWithAuth).then(s => {
      if (!alive) return
      setPremium(!!s.premium); premiumRef.current = !!s.premium
      if (s.priceInr) setPremiumPrice(s.priceInr)
    })
    return () => { alive = false }
  }, [fetchWithAuth])

  const startSubscribe = useCallback(async () => {
    try {
      const r = await subscribePremium(fetchWithAuth, user)
      if (r?.premium) { setPremium(true); premiumRef.current = true; return true }
    } catch (e) { if (e?.message && e.message !== 'cancelled') alert(e.message) }
    return false
  }, [fetchWithAuth, user])

  // ── Local dose notifications (native app): schedule 9AM/1PM/9PM for the active plan ──
  useEffect(() => {
    if (!nativeApp || !treatment?.appt) return
    initReminderNotificationTap()
    syncReminderNotifications(generateReminders(treatment.appt), loadState(treatment.appt.id))
  }, [nativeApp, treatment?.appt?.id])

  // Notification tap → open the Reminders screen
  useEffect(() => {
    if (!nativeApp) return
    const openRem = () => {
      const appt = treatment?.appt || appointments[0]
      if (!appt) return
      setSelected(appt); setActiveTab('reminders'); setFromTile(true); setMobileView('list')
      // Speak the due medicines out loud — premium (voice) subscribers only
      if (premiumRef.current) { try { speakReminder(voiceLineForNow(generateReminders(appt), loadState(appt.id), appt.name)) } catch {} }
    }
    if (consumeOpenRemindersFlag()) openRem()
    const handler = () => { consumeOpenRemindersFlag(); openRem() }
    window.addEventListener('revive-open-reminders', handler)
    return () => window.removeEventListener('revive-open-reminders', handler)
  }, [nativeApp, treatment?.appt?.id, appointments.length])

  // Patient journey state — drives which home screen renders in the native app.
  // NEW_USER → APPT_BOOKED → CARE_PLAN_CREATED (1st week after consult)
  // → ACTIVE_TREATMENT (photo uploads) → FOLLOW_UP_DUE → TREATMENT_COMPLETED
  // (maintenance: follow-up consult done and next follow-up 3+ months out or none)
  const { patientState, latestAppt } = useMemo(() => {
    const DAY = 86400000
    const sorted = appointments.slice().sort((a, b) => new Date(b.date) - new Date(a.date))
    if (!sorted.length) return { patientState: 'NEW_USER', latestAppt: null }
    const booked = sorted.find(a => ['pending', 'confirmed'].includes(a.status))
    if (booked) return { patientState: 'APPT_BOOKED', latestAppt: booked }
    const completed = sorted.filter(a => a.status === 'completed')
    if (!completed.length) return { patientState: 'NEW_USER', latestAppt: null }
    const lastDone = completed[0]
    const doneTs = new Date(String(lastDone.date).slice(0, 10) + 'T00:00:00').getTime()
    const fuTs = lastDone.followUpDate ? new Date(String(lastDone.followUpDate).slice(0, 10) + 'T00:00:00').getTime() : null
    // Maintenance: a follow-up consult is done AND the next follow-up is 3+ months away (or none)
    if (completed.length >= 2 && (!fuTs || fuTs - Date.now() > 90 * DAY)) {
      return { patientState: 'TREATMENT_COMPLETED', latestAppt: lastDone }
    }
    // Follow-up due: doctor's follow-up date is within a week (or passed) and nothing booked yet
    if (fuTs && fuTs - Date.now() <= 7 * DAY) return { patientState: 'FOLLOW_UP_DUE', latestAppt: lastDone }
    // First week after the consult: care plan just created
    if (Date.now() - doneTs <= 7 * DAY) return { patientState: 'CARE_PLAN_CREATED', latestAppt: lastDone }
    return { patientState: 'ACTIVE_TREATMENT', latestAppt: lastDone }
  }, [appointments])

  useEffect(() => {
    if (!user?.token) { navigate('/login'); return }
    load()
    loadScans()
    loadNotifs()
  }, [user])

  const loadNotifs = async () => {
    try {
      const { API_BASE } = await import('../config')
      const res = await fetchWithAuth(`${API_BASE}/api/patient/notifications`)
      if (!res.ok) return
      const j = await res.json()
      setPatientNotifs((j.notifications || []).filter(n => !n.read))
    } catch {}
  }

  const dismissNotif = async (id) => {
    setPatientNotifs(prev => prev.filter(n => n.id !== id))
    try {
      const { API_BASE } = await import('../config')
      await fetchWithAuth(`${API_BASE}/api/patient/notifications/${id}/read`, { method: 'PUT' })
    } catch {}
  }

  const load = async () => {
    setLoading(true)
    try {
      const { API_BASE } = await import('../config')
      const res = await fetchWithAuth(`${API_BASE}/api/followups?email=${encodeURIComponent(user.email)}`)
      if (!res.ok) throw new Error()
      const j = await res.json()
      setAppointments(j.followups||[])
    } catch(e) { console.error(e); setAppointments([]) }
    finally { setLoading(false) }
  }

  const loadScans = async () => {
    try {
      const { API_BASE } = await import('../config')
      const res = await fetchWithAuth(`${API_BASE}/api/skin-scans?email=${encodeURIComponent(user.email)}`)
      if (!res.ok) return
      const j = await res.json()
      const scans = j.scans || []
      setAllScans(scans)
      const latest = scans[0]
      if (!latest || latest.booked) return
      const ageDays = (Date.now() - new Date(latest.created_at).getTime()) / 86400000
      if (ageDays < 3) setUnbookedScan(latest)
    } catch {}
  }

  const openAppt = a => { setSelected(a); setActiveTab('details'); setFromTile(false) }
  if (!user?.token) return null

  // ── NATIVE APP: mockup-style mobile home screen ──
  const openDetail = (appt, tab) => {
    if (appt) { setSelected(appt); setActiveTab(tab); setFromTile(true) }
    else { setSelected(null) }
    setMobileView('list')
  }
  if (nativeApp && mobileView === 'home' && !selected) {
    return (
      <MobileHome
        user={user}
        treatment={treatment}
        patientState={patientState}
        latestAppt={latestAppt}
        appointments={appointments}
        fetchWithAuth={fetchWithAuth}
        navigate={navigate}
        notifs={patientNotifs}
        onDismissNotif={dismissNotif}
        onOpenPrescription={() => openDetail(treatment?.appt || appointments[0], 'prescription')}
        onOpenPlan={() => openDetail(treatment?.appt || appointments[0], 'plan')}
        onOpenReminders={() => openDetail(treatment?.appt || appointments[0], 'reminders')}
        onOpenProgress={() => (allScans.length ? navigate('/skin-analysis') : openDetail(treatment?.appt || appointments[0], 'details'))}
        onOpenAppointments={() => navigate('/book-appointment')}
        onMessages={() => navigate('/contact')}
        onNavHome={() => setMobileView('home')}
        onNavProgress={() => (allScans.length ? navigate('/skin-analysis') : openDetail(treatment?.appt || appointments[0], 'details'))}
        onNavShop={() => navigate('/shop')}
        onSkinScan={() => navigate('/skin-quiz')}
        onExplore={(path) => navigate(path)}
        onOpenDetail={(tab) => openDetail(latestAppt, tab)}
      />
    )
  }

  // ── NATIVE APP: My Care screen ──
  if (nativeApp && mobileView === 'list' && !selected) {
    return (
      <MyCareScreen
        user={user}
        treatment={treatment}
        patientState={patientState}
        allScans={allScans}
        appointments={appointments}
        fetchWithAuth={fetchWithAuth}
        navigate={navigate}
        onReload={load}
        onOpenDetail={(appt, tab) => { setSelected(appt); setActiveTab(tab); setFromTile(true) }}
      />
    )
  }

  // ── NATIVE APP: clean treatment detail (opened from My Care → Treatment History) ──
  if (nativeApp && selected && fromTile && activeTab === 'details') {
    return (
      <TreatmentDetailScreen
        appt={selected}
        backLabel={searchParams.get('view') === 'care' ? 'My Care' : 'Home'}
        onBack={() => { setSelected(null); setFromTile(false) }}
        onPhotosUpdated={(id, photos) => { setAppointments(prev => prev.map(a => a.id===id ? {...a, photos: JSON.stringify(photos)} : a)); setSelected(prev => prev?.id===id ? {...prev, photos: JSON.stringify(photos)} : prev) }}
        onProgressPhotosUpdated={(id, pp) => { setAppointments(prev => prev.map(a => a.id===id ? {...a, progressPhotos: JSON.stringify(pp)} : a)); setSelected(prev => prev?.id===id ? {...prev, progressPhotos: JSON.stringify(pp)} : prev) }}
      />
    )
  }

  // ── NATIVE APP: clean reminders / prescription screen (opened from a home tile) ──
  if (nativeApp && selected && fromTile && (activeTab === 'reminders' || activeTab === 'prescription' || activeTab === 'plan')) {
    return (
      <div style={{ minHeight: '100vh', background: '#F4F8F5', fontFamily: "'DM Sans',sans-serif", paddingBottom: 96 }}>
        <div style={{ background: 'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)', padding: '3.1rem 1.1rem 1.2rem', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -70, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle,rgba(133,201,154,.28),transparent 70%)' }}/>
          <div style={{ position: 'absolute', bottom: -90, left: -40, width: 190, height: 190, borderRadius: '50%', background: 'radial-gradient(circle,rgba(255,255,255,.08),transparent 70%)' }}/>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
            <button onClick={() => { setSelected(null); setFromTile(false); setMobileView(searchParams.get('view') === 'care' ? 'list' : 'home') }}
              style={{ width: 38, height: 38, borderRadius: 13, background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.2)', color: '#fff', fontSize: '1.15rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: "'DM Sans',sans-serif", paddingBottom: 2 }}>‹</button>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.35rem', color: '#fff', lineHeight: 1.15 }}>{activeTab === 'reminders' ? 'Medicine Reminders' : activeTab === 'plan' ? 'Treatment Plan' : 'Prescription'}</div>
              <div style={{ fontSize: '.68rem', color: 'rgba(255,255,255,.6)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.service} · {fmtDate(selected.date)}</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '1rem 1rem 0' }}>
          {activeTab === 'reminders' ? <ReminderTab appointment={selected} premium={premium} premiumPrice={premiumPrice} onSubscribe={startSubscribe}/>
            : activeTab === 'plan' ? <PlanTab appt={selected} onTrackDoses={() => setActiveTab('reminders')} onViewPrescription={() => setActiveTab('prescription')}/>
            : <PrescriptionTab appt={selected}/>}
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight:'100vh', background:C.g50, fontFamily:"'DM Sans',sans-serif" }}>

      {/* ── NATIVE APP: back-to-home bar ── */}
      {nativeApp && (
        <div style={{ position:'sticky', top:0, zIndex:30, background:'linear-gradient(150deg,#0F3D24,#1E6B3C)', padding:'2.7rem 1rem .8rem', display:'flex', alignItems:'center', gap:11 }}>
          <button onClick={()=>{ setSelected(null); setFromTile(false); setMobileView(searchParams.get('view') === 'care' ? 'list' : 'home') }}
            style={{ width:34, height:34, borderRadius:12, background:'rgba(255,255,255,.14)', border:'1px solid rgba(255,255,255,.2)', color:'#fff', fontSize:'1.05rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontFamily:"'DM Sans',sans-serif", paddingBottom:2 }}>‹</button>
          <span style={{ color:'#fff', fontSize:'.92rem', fontWeight:700, fontFamily:"'DM Serif Display',serif", letterSpacing:'.01em' }}>
            {selected ? (fromTile ? ({ prescription:'Prescription', reminders:'Reminders', details:'Details' }[activeTab] || selected.service) : selected.service) : 'My Appointments'}
          </span>
        </div>
      )}

      {/* ── COMPACT HERO ── */}
      <div style={{ background:'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)', padding:'1rem 0', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-60, right:-40, width:190, height:190, borderRadius:'50%', background:'radial-gradient(circle,rgba(133,201,154,.22),transparent 70%)', pointerEvents:'none' }} />
        <div className="pd-hero">
          {/* Title + welcome */}
          <div>
            <h1 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.3rem', color:'white', margin:0, lineHeight:1.2, fontWeight:400 }}>My Health Dashboard</h1>
            <p style={{ fontSize:'.72rem', color:'rgba(255,255,255,.5)', fontWeight:300, margin:'.15rem 0 0' }}>Welcome back, {user?.name||user?.email} 👋</p>
          </div>
          {/* Stat pills — single row */}
          <div className="pd-stats">
            {[
              ['📋', appointments.length,                                           'Total'],
              ['✅', appointments.filter(a=>a.status==='completed').length,         'Done'],
              ['⏳', appointments.filter(a=>a.status==='pending').length,           'Pending'],
              ['💊', appointments.filter(a=>a.prescription||a.medication).length,  'With Rx'],
            ].map(([icon,count,label])=>(
              <div key={label} className="pd-stat">
                <span style={{ fontSize:'.85rem' }}>{icon}</span>
                <div>
                  <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1rem', color:'white', lineHeight:1 }}>{count}</div>
                  <div style={{ fontSize:'.5rem', color:'rgba(255,255,255,.45)', textTransform:'uppercase', letterSpacing:'.06em', marginTop:1, whiteSpace:'nowrap' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MISSED DOSE NOTIFICATIONS ── */}
      {patientNotifs.length > 0 && (
        <div style={{ maxWidth:1600, margin:'0 auto', padding:'.75rem 2rem 0', display:'flex', flexDirection:'column', gap:'.5rem' }}>
          {patientNotifs.map(n => (
            <div key={n.id} style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:12, padding:'.85rem 1.1rem', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'1rem' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:'.65rem' }}>
                <span style={{ fontSize:'1.2rem', flexShrink:0 }}>💊</span>
                <div>
                  <div style={{ fontWeight:700, color:'#B91C1C', fontSize:'.85rem' }}>{n.title}</div>
                  <div style={{ fontSize:'.78rem', color:'#7F1D1D', marginTop:2, lineHeight:1.5 }}>{n.body}</div>
                </div>
              </div>
              <button onClick={() => dismissNotif(n.id)}
                style={{ background:'none', border:'none', color:'#B91C1C', cursor:'pointer', fontSize:'1.1rem', flexShrink:0, padding:0, lineHeight:1 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* ── UNBOOKED SCAN BANNER ── */}
      {unbookedScan && (
        <div style={{ maxWidth:1600, margin:'0 auto', padding:'.75rem 2rem 0' }}>
          <div style={{ background:'linear-gradient(135deg,#1A3A28,#235233)', border:'1px solid #3D8A57', borderRadius:12, padding:'.9rem 1.25rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'.75rem' }}>
              <span style={{ fontSize:'1.4rem' }}>🔬</span>
              <div>
                <div style={{ color:'white', fontWeight:600, fontSize:'.88rem' }}>Your skin scan is ready for review</div>
                <div style={{ color:'rgba(255,255,255,.55)', fontSize:'.75rem', marginTop:2 }}>Book an appointment so a doctor can review your results</div>
              </div>
            </div>
            <button onClick={() => navigate('/book-appointment')}
              style={{ background:'#5AAE73', color:'white', border:'none', borderRadius:8, padding:'.5rem 1.25rem', fontWeight:600, fontSize:'.82rem', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap' }}>
              Book Appointment
            </button>
          </div>
        </div>
      )}

      {/* ── GRID ── */}
      <div className="pd-grid">

        {/* LEFT LIST (hidden when a home tile opened a specific section) */}
        <div style={{ display:(nativeApp && fromTile)?'none':undefined, background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 10px rgba(13,34,24,.05)' }}>
          <div style={{ padding:'.75rem 1rem', borderBottom:`1px solid ${C.border}`, background:C.g50, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontWeight:700, color:C.g900, fontSize:'.85rem' }}>My Appointments</div>
            <div style={{ display:'flex', gap:5 }}>
              <button onClick={()=>setSelected(null)} title="Upload Prescription"
                style={{ fontSize:'.68rem', color:!selected?C.g700:C.muted, background:!selected?C.g100:'none', border:`1px solid ${!selected?C.g400:C.border}`, borderRadius:6, padding:'.2rem .55rem', cursor:'pointer', fontWeight:!selected?700:400 }}>💊 Rx</button>
              <button onClick={load} style={{ fontSize:'.68rem', color:C.muted, background:'none', border:`1px solid ${C.border}`, borderRadius:6, padding:'.2rem .5rem', cursor:'pointer' }}>🔄</button>
            </div>
          </div>

          {loading ? (
            <div style={{ padding:'2rem', textAlign:'center', color:C.muted }}>
              <div style={{ fontSize:'1.3rem', opacity:.3 }}>⏳</div>
              <div style={{ fontSize:'.8rem', marginTop:6 }}>Loading…</div>
            </div>
          ) : appointments.length===0 ? (
            <div style={{ padding:'2rem', textAlign:'center', color:C.muted }}>
              <div style={{ fontSize:'1.8rem', opacity:.2 }}>📋</div>
              <div style={{ fontSize:'.82rem', fontWeight:500, color:C.text, margin:'6px 0 12px' }}>No appointments yet</div>
              <button onClick={()=>navigate('/book-appointment')} style={{ padding:'.45rem 1rem', borderRadius:8, background:C.g700, color:'white', border:'none', fontSize:'.78rem', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Book Appointment</button>
            </div>
          ) : (
            <div style={{ overflowY:'auto', maxHeight:'calc(100vh - 160px)' }}>
              {appointments.map(a=>{
                const planLive = planIsActive(a)
                const sc = planLive ? {bg:C.g100,color:C.g600,border:C.g300} : STATUS_COLORS[a.status]||STATUS_COLORS.pending
                const isActive = selected?.id===a.id
                return (
                  <div key={a.id} onClick={()=>openAppt(a)}
                    style={{ padding:'.75rem 1rem', borderBottom:`1px solid ${C.border}`, cursor:'pointer', background:isActive?C.g100:'white', borderLeft:isActive?`3px solid ${C.g500}`:'3px solid transparent', transition:'all .15s' }}
                    onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=C.g50}}
                    onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background='white'}}>
                    <div style={{ display:'flex', justifyContent:'space-between', gap:6 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:600, color:C.g900, fontSize:'.83rem', marginBottom:'.15rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.service||'—'}</div>
                        <div style={{ fontSize:'.68rem', color:C.muted }}>📅 {fmtDate(a.date)}{a.timeSlot?` · ${a.timeSlot}`:''}</div>
                        <div style={{ display:'flex', gap:3, marginTop:'.25rem', flexWrap:'wrap' }}>
                          {a.diagnosis    && <span style={{ fontSize:'.58rem', background:'#EDF2FF', color:'#3451B2', padding:'.1rem .35rem', borderRadius:4 }}>Dx ✓</span>}
                          {a.prescription && <span style={{ fontSize:'.58rem', background:C.g100,   color:C.g600,   padding:'.1rem .35rem', borderRadius:4 }}>Rx ✓</span>}
                          {a.medication   && <span style={{ fontSize:'.58rem', background:'#FFF3E0', color:'#E65100', padding:'.1rem .35rem', borderRadius:4 }}>💊 Med</span>}
                        </div>
                      </div>
                      <span style={{ fontSize:'.6rem', fontWeight:600, padding:'.15rem .45rem', borderRadius:999, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, textTransform:'capitalize', whiteSpace:'nowrap', flexShrink:0, alignSelf:'flex-start' }}>{planLive?'Active plan':a.status}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* RIGHT DETAIL */}
        <div style={{ minWidth:0, display:'flex', flexDirection:'column' }}>
        {selected ? (
          <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem', minWidth:0 }}>
          <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 10px rgba(13,34,24,.05)' }}>
            {/* Detail header */}
            <div style={{ background:C.headerGrad, padding:'1rem 1.25rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', color:'white', marginBottom:'.2rem' }}>{selected.service}</div>
                  <div style={{ fontSize:'.72rem', color:'rgba(255,255,255,.5)' }}>📅 {fmtDate(selected.date)}{selected.timeSlot?` · ${selected.timeSlot}`:''}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  {(()=>{ const active=planIsActive(selected); const sc=active?{bg:C.g100,color:C.g600,border:C.g300}:STATUS_COLORS[selected.status]||STATUS_COLORS.pending; return <span style={{ fontSize:'.68rem', fontWeight:600, padding:'.25rem .65rem', borderRadius:999, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, textTransform:'capitalize' }}>{active?'Active plan':selected.status}</span> })()}
                  <button onClick={()=>setSelected(null)} style={{ width:28, height:28, borderRadius:6, background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', color:'rgba(255,255,255,.8)', cursor:'pointer', fontSize:'1.1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
                </div>
              </div>
              {(selected.phone||selected.mode||selected.followUpDate) && (
                <div style={{ display:'flex', gap:6, marginTop:'.65rem', flexWrap:'wrap' }}>
                  {selected.phone        && <div style={{ background:'rgba(255,255,255,.1)', borderRadius:7, padding:'.25rem .6rem', fontSize:'.7rem', color:'rgba(255,255,255,.7)' }}>📞 {selected.phone}</div>}
                  {selected.mode         && <div style={{ background:'rgba(255,255,255,.1)', borderRadius:7, padding:'.25rem .6rem', fontSize:'.7rem', color:'rgba(255,255,255,.7)' }}>💻 {selected.mode}</div>}
                  {selected.followUpDate && <div style={{ background:'rgba(255,255,255,.1)', borderRadius:7, padding:'.25rem .6rem', fontSize:'.7rem', color:'rgba(255,255,255,.7)' }}>📅 Follow-up: {fmtDate(selected.followUpDate)}</div>}
                  {selected.mode === 'online' && selected.status !== 'completed' && String(selected.date).slice(0,10) >= todayISO() && (
                    <a href={selected.meetLink || `https://meet.jit.si/revive-${selected.id}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#1A73E8', color:'white', borderRadius:7, padding:'.3rem .85rem', fontSize:'.75rem', fontWeight:700, textDecoration:'none' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z"/></svg>
                      Join Video Call
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Tabs (hidden when opened from a home tile — that tile IS the section) */}
            <div style={{ display:(nativeApp && fromTile)?'none':'flex', borderBottom:`1px solid ${C.border}`, background:C.g50 }}>
              {[['details','📋 Details'],['prescription','💊 Prescription'],['reminders','⏰ Reminders']].map(([key,label])=>(
                <button key={key} onClick={()=>setActiveTab(key)}
                  style={{ flex:1, padding:'.65rem .3rem', fontSize:'.72rem', fontWeight:600, border:'none', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", background:'none', borderBottom:activeTab===key?`2.5px solid ${C.g600}`:'2.5px solid transparent', color:activeTab===key?C.g700:C.muted, transition:'all .15s' }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div style={{ padding:'1.25rem', overflowY:'auto', maxHeight:'calc(100vh - 300px)' }}>
              {activeTab==='details'      && <DetailsTab appt={selected}
                onPhotosUpdated={(id, photos) => setAppointments(prev => prev.map(a => a.id===id ? {...a, photos: JSON.stringify(photos)} : a))}
                onProgressPhotosUpdated={(id, pp) => { setAppointments(prev => prev.map(a => a.id===id ? {...a, progressPhotos: JSON.stringify(pp)} : a)); setSelected(prev => prev?.id===id ? {...prev, progressPhotos: JSON.stringify(pp)} : prev) }}
              />}
              {activeTab==='prescription' && <PrescriptionTab appt={selected}/>}
              {activeTab==='reminders'    && <ReminderTab appointment={selected} premium={premium} premiumPrice={premiumPrice} onSubscribe={startSubscribe}/>}
            </div>
          </div>
          {allScans.length >= 2 && <SkinProgressCard scans={allScans} navigate={navigate} />}
          {allScans.length === 1 && (
            <div style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:14, padding:'1rem 1.25rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'.75rem' }}>
                <span style={{ fontSize:'1.2rem' }}>🔬</span>
                <div>
                  <div style={{ fontWeight:700, color:C.g900, fontSize:'.82rem' }}>Skin Progress Tracking</div>
                  <div style={{ color:C.muted, fontSize:'.72rem', marginTop:2 }}>Take a second scan to see how your skin is changing over time</div>
                </div>
              </div>
              <button onClick={() => navigate('/skin-quiz')}
                style={{ background:C.g700, color:'white', border:'none', borderRadius:8, padding:'.4rem 1rem', fontWeight:600, fontSize:'.75rem', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", flexShrink:0 }}>
                New Scan
              </button>
            </div>
          )}
          </div>
        ) : (
          /* No appointment selected — show Rx Upload prominently */
          <div style={{ background:'white', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 10px rgba(13,34,24,.05)' }}>
            <div style={{ background:C.headerGrad, padding:'1rem 1.25rem' }}>
              <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', color:'white', marginBottom:'.15rem' }}>My Reminders</div>
              <div style={{ fontSize:'.72rem', color:'rgba(255,255,255,.5)' }}>Your daily medication reminders — tap “My Prescription” to add a new one</div>
            </div>
            <div style={{ padding:'1.25rem', overflowY:'auto', maxHeight:'calc(100vh - 220px)' }}>
              <RxUploadTab onAdd={onAdd} />
            </div>
          </div>
        )}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing:border-box; }
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px}
        body { overflow-x: hidden; }
        .pd-grid { display:grid; grid-template-columns:340px minmax(0,1fr); gap:1.5rem; align-items:start; max-width:1600px; margin:0 auto; padding:1.25rem 2rem; }
        .pd-hero { max-width:1600px; margin:0 auto; padding:0 2rem; position:relative; z-index:1; display:flex; justify-content:space-between; align-items:center; gap:1rem; flex-wrap:wrap; }
        .pd-stats { display:flex; gap:.5rem; flex-wrap:nowrap; }
        .pd-stat { background:rgba(255,255,255,.1); border:1px solid rgba(255,255,255,.14); border-radius:12px; padding:.45rem .8rem; display:flex; align-items:center; gap:7px; min-width:0; text-align:center; }
        @media (max-width: 900px) {
          .pd-grid { grid-template-columns: 1fr; padding:1.25rem 1.5rem; }
        }
        @media (max-width: 640px) {
          .pd-hero { padding:0 1rem; gap:.7rem; }
          .pd-stats { width:100%; gap:.4rem; }
          .pd-stat { flex:1; flex-direction:column; gap:2px; padding:.45rem .3rem; }
        }
      `}</style>
    </div>
  )
}

// ─── Skin Progress Card ───────────────────────────────────────────────────────
function SkinProgressCard({ scans, navigate }) {
  const latest = scans[0]
  const prev   = scans[1]

  const latestConds = (latest?.analysis?.conditions || []).map(c => c.name?.toLowerCase())
  const prevConds   = (prev?.analysis?.conditions   || []).map(c => c.name?.toLowerCase())

  // Build comparison: new / resolved / persisting
  const allNames = [...new Set([...latestConds, ...prevConds])]
  const rows = allNames.map(name => {
    const inLatest = latestConds.includes(name)
    const inPrev   = prevConds.includes(name)
    if (inLatest && inPrev)   return { name, status: 'same',     icon: '→', color: '#6B7280' }
    if (inLatest && !inPrev)  return { name, status: 'new',      icon: '↑', color: '#DC2626' }
    return                           { name, status: 'resolved', icon: '↓', color: '#16A34A' }
  })

  const fmt = d => { try { const s = (d||'').replace(' ','T').split('T')[0]; const [y,m,day]=s.split('-'); return `${day}/${m}/${y}` } catch { return d } }

  const resolved  = rows.filter(r => r.status === 'resolved').length
  const newConds  = rows.filter(r => r.status === 'new').length
  const persisting= rows.filter(r => r.status === 'same').length

  return (
    <div style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:14, overflow:'hidden', boxShadow:'0 2px 10px rgba(13,34,24,.05)' }}>
      {/* Header */}
      <div style={{ background:C.headerGrad, padding:'1rem 1.5rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', color:'white' }}>Skin Progress</div>
          <div style={{ fontSize:'.72rem', color:'rgba(255,255,255,.5)', marginTop:2 }}>
            {fmt(prev.created_at)} → {fmt(latest.created_at)}
          </div>
        </div>
        <div style={{ display:'flex', gap:'.6rem' }}>
          {[['↓ Resolved', resolved, '#16A34A'], ['↑ New', newConds, '#DC2626'], ['→ Ongoing', persisting, '#6B7280']].map(([label, n, color]) => (
            <div key={label} style={{ background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.15)', borderRadius:8, padding:'.4rem .8rem', textAlign:'center' }}>
              <div style={{ color, fontWeight:700, fontSize:'.9rem' }}>{n}</div>
              <div style={{ color:'rgba(255,255,255,.45)', fontSize:'.6rem' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Condition rows */}
      <div style={{ padding:'1rem 1.5rem', display:'flex', flexWrap:'wrap', gap:'.5rem' }}>
        {rows.map(r => (
          <div key={r.name} style={{ display:'flex', alignItems:'center', gap:'.4rem', background: r.status==='resolved'?'#F0FDF4': r.status==='new'?'#FEF2F2':'#F9FAFB', border:`1px solid ${r.status==='resolved'?'#BBF7D0':r.status==='new'?'#FECACA':'#E5E7EB'}`, borderRadius:999, padding:'.3rem .8rem', fontSize:'.75rem' }}>
            <span style={{ fontWeight:700, color:r.color }}>{r.icon}</span>
            <span style={{ color:'#374151', textTransform:'capitalize' }}>{r.name}</span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ borderTop:`1px solid ${C.border}`, padding:'.75rem 1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontSize:'.72rem', color:C.muted }}>{scans.length} scan{scans.length>1?'s':''} on file</div>
        <button onClick={() => navigate('/skin-quiz')}
          style={{ background:C.g700, color:'white', border:'none', borderRadius:8, padding:'.4rem 1rem', fontWeight:600, fontSize:'.75rem', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
          + New Scan
        </button>
      </div>
    </div>
  )
}

// ─── Details Tab ──────────────────────────────────────────────────────────────
function DetailsTab({ appt, onPhotosUpdated, onProgressPhotosUpdated, hideDiagnosis }) {
  const { fetchWithAuth } = useAuth()
  const fileInputRef = React.useRef(null)
  const progressFileInputRef = React.useRef(null)
  const canEditPhotos = !['completed', 'cancelled'].includes(appt.status)

  // Days since treatment started (appt.date = day 0) — drives the weekly progress cadence
  const daysSinceAppt = React.useMemo(() => {
    try {
      const raw = appt.date || ''
      const d = new Date(raw.includes('T') ? raw : raw + 'T00:00:00')
      return Math.floor((Date.now() - d.getTime()) / 86400000)
    } catch { return 999 }
  }, [appt.date])
  // Progress photos: one batch per treatment-week, every week from day 1 (up to 180 days)
  const showProgressWindow = appt.status === 'completed'
  const currentWeek = Math.floor(daysSinceAppt / 7)  // week 0 = days 0–6, week 1 = days 7–13…
  const weekLabel = currentWeek + 1                   // human-friendly (Week 1, Week 2…)

  const [photos, setPhotos] = React.useState(() => {
    try { return typeof appt.photos === 'string' ? JSON.parse(appt.photos) : (Array.isArray(appt.photos) ? appt.photos : []) } catch { return [] }
  })
  const [progressPhotos, setProgressPhotos] = React.useState(() => {
    try { return typeof appt.progressPhotos === 'string' ? JSON.parse(appt.progressPhotos) : (Array.isArray(appt.progressPhotos) ? appt.progressPhotos : []) } catch { return [] }
  })
  const [progressWeeks, setProgressWeeks] = React.useState(() => {
    try { return typeof appt.progressPhotoWeeks === 'string' ? JSON.parse(appt.progressPhotoWeeks) : (Array.isArray(appt.progressPhotoWeeks) ? appt.progressPhotoWeeks : []) } catch { return [] }
  })
  const addedThisWeek = progressWeeks.includes(currentWeek)
  const canAddThisWeek = showProgressWindow && !addedThisWeek
  const daysToNextWeek = Math.max(1, (currentWeek + 1) * 7 - daysSinceAppt)
  const [uploading, setUploading] = React.useState(false)
  const [deleting, setDeleting] = React.useState(null)
  const [progUploading, setProgUploading] = React.useState(false)
  const [progDeleting, setProgDeleting] = React.useState(null)

  const addPhotos = async (files) => {
    if (!files?.length) return
    setUploading(true)
    try {
      const fd = new FormData()
      Array.from(files).slice(0, 10 - photos.length).forEach((f, i) => fd.append(`photo_${i}`, f))
      const res = await fetchWithAuth(`${API_BASE}/api/appointments/${appt.id}/photos`, { method:'POST', body: fd })
      const data = await res.json()
      if (res.ok) { setPhotos(data.photos); onPhotosUpdated && onPhotosUpdated(appt.id, data.photos) }
      else { alert('Upload failed: ' + (data.message || res.status)) }
    } catch(e) { alert('Upload failed: ' + e.message) }
    finally { setUploading(false) }
  }

  const deletePhoto = async (filename) => {
    if (!window.confirm('Remove this photo?')) return
    setDeleting(filename)
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/appointments/${appt.id}/photos/${encodeURIComponent(filename)}`, { method:'DELETE' })
      const data = await res.json()
      if (res.ok) { setPhotos(data.photos); onPhotosUpdated && onPhotosUpdated(appt.id, data.photos) }
    } catch(e) { alert('Delete failed: ' + e.message) }
    finally { setDeleting(null) }
  }

  const addProgressPhotos = async (files) => {
    if (!files?.length) return
    setProgUploading(true)
    try {
      const fd = new FormData()
      Array.from(files).slice(0, 10 - progressPhotos.length).forEach((f, i) => fd.append(`photo_${i}`, f))
      const res = await fetchWithAuth(`${API_BASE}/api/appointments/${appt.id}/progress-photos`, { method:'POST', body: fd })
      const data = await res.json()
      if (res.ok) { setProgressPhotos(data.progressPhotos); if (Array.isArray(data.progressPhotoWeeks)) setProgressWeeks(data.progressPhotoWeeks); onProgressPhotosUpdated && onProgressPhotosUpdated(appt.id, data.progressPhotos) }
      else alert(data.message || 'Upload failed')
    } catch(e) { alert('Upload failed: ' + e.message) }
    finally { setProgUploading(false) }
  }

  const deleteProgressPhoto = async (filename) => {
    if (!window.confirm('Remove this progress photo?')) return
    setProgDeleting(filename)
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/appointments/${appt.id}/progress-photos/${encodeURIComponent(filename)}`, { method:'DELETE' })
      const data = await res.json()
      if (res.ok) { setProgressPhotos(data.progressPhotos); onProgressPhotosUpdated && onProgressPhotosUpdated(appt.id, data.progressPhotos) }
    } catch(e) { alert('Delete failed: ' + e.message) }
    finally { setProgDeleting(null) }
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'.85rem' }}>
      {/* Photos section */}
      <div style={{ background:C.g50, border:`1px solid ${C.border}`, borderRadius:10, padding:'.85rem 1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.6rem' }}>
          <div style={{ fontSize:'.65rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em' }}>📷 Your Photos</div>
          {canEditPhotos && photos.length < 10 && (
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              style={{ padding:'.25rem .75rem', borderRadius:7, background:C.g700, color:'white', border:'none', fontSize:'.7rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
              {uploading ? '⏳ Uploading…' : '+ Add Photos'}
            </button>
          )}
          {!canEditPhotos && <span style={{ fontSize:'.65rem', color:C.muted, fontStyle:'italic' }}>Consultation closed</span>}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display:'none' }}
          onChange={e => { addPhotos(e.target.files); e.target.value = '' }} />
        {photos.length === 0 ? (
          canEditPhotos
            ? <div onClick={() => fileInputRef.current?.click()}
                style={{ border:`2px dashed ${C.border}`, borderRadius:9, padding:'1.25rem', textAlign:'center', cursor:'pointer', color:C.muted, fontSize:'.8rem' }}>
                📷 Tap to add skin/hair photos for your doctor
              </div>
            : <div style={{ color:C.muted, fontSize:'.8rem' }}>No photos uploaded.</div>
        ) : (
          <div style={{ display:'flex', gap:'.5rem', flexWrap:'wrap' }}>
            {photos.map((filename, i) => (
              <div key={i} style={{ position:'relative', width:72, height:72, flexShrink:0 }}>
                <a href={photoUrl(filename)} target="_blank" rel="noopener noreferrer"
                  style={{ display:'block', width:72, height:72, borderRadius:8, overflow:'hidden', border:`1.5px solid ${C.border}` }}>
                  <img src={photoUrl(filename)} alt={`photo-${i+1}`}
                    style={{ width:'100%', height:'100%', objectFit:'cover' }}
                    onError={e => { e.target.style.display='none' }} />
                </a>
                {canEditPhotos && (
                  <button onClick={e => { e.preventDefault(); deletePhoto(filename) }} disabled={deleting===filename}
                    style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', background:'#C0392B', color:'white', border:'2px solid white', cursor:'pointer', fontSize:'.6rem', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1, padding:0 }}>
                    {deleting===filename ? '…' : '✕'}
                  </button>
                )}
              </div>
            ))}
            {canEditPhotos && photos.length < 10 && (
              <div onClick={() => fileInputRef.current?.click()}
                style={{ width:72, height:72, borderRadius:8, border:`2px dashed ${C.border}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:C.muted, fontSize:'1.4rem', flexShrink:0 }}>
                +
              </div>
            )}
          </div>
        )}
      </div>

      {/* Progress Photos section — one batch per treatment-week, every week */}
      {appt.status === 'completed' && (
        <div style={{ background:'#F0F4FF', border:'1.5px solid #BAC8FF', borderRadius:10, padding:'.85rem 1rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.5rem' }}>
            <div>
              <div style={{ fontSize:'.65rem', fontWeight:700, color:'#3451B2', textTransform:'uppercase', letterSpacing:'.08em' }}>📸 Progress Photos</div>
              {canAddThisWeek && (
                <div style={{ fontSize:'.65rem', color:'#5A72CC', marginTop:'.15rem' }}>
                  Week {weekLabel} · Add this week's progress photos for the doctor
                </div>
              )}
              {addedThisWeek && (
                <div style={{ fontSize:'.65rem', color:'#2F9E44', marginTop:'.15rem' }}>
                  ✓ Week {weekLabel} photos received · next window in {daysToNextWeek} day{daysToNextWeek !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            {canAddThisWeek && progressPhotos.length < 10 && (
              <button onClick={() => progressFileInputRef.current?.click()} disabled={progUploading}
                style={{ padding:'.25rem .75rem', borderRadius:7, background:'#3451B2', color:'white', border:'none', fontSize:'.7rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
                {progUploading ? '⏳ Uploading…' : '+ Add Photos'}
              </button>
            )}
          </div>
          <input ref={progressFileInputRef} type="file" accept="image/*" multiple style={{ display:'none' }}
            onChange={e => { addProgressPhotos(e.target.files); e.target.value = '' }} />
          {progressPhotos.length === 0 ? (
            canAddThisWeek
              ? <div onClick={() => progressFileInputRef.current?.click()}
                  style={{ border:'2px dashed #BAC8FF', borderRadius:9, padding:'1.25rem', textAlign:'center', cursor:'pointer', color:'#5A72CC', fontSize:'.8rem' }}>
                  📸 Tap to upload this week's progress photos
                </div>
              : <div style={{ color:C.muted, fontSize:'.8rem' }}>✓ This week's photos are in. Come back in {daysToNextWeek} day{daysToNextWeek !== 1 ? 's' : ''} for the next set.</div>
          ) : (
            <div style={{ display:'flex', gap:'.5rem', flexWrap:'wrap' }}>
              {progressPhotos.map((filename, i) => (
                <div key={i} style={{ position:'relative', width:72, height:72, flexShrink:0 }}>
                  <a href={photoUrl(filename)} target="_blank" rel="noopener noreferrer"
                    style={{ display:'block', width:72, height:72, borderRadius:8, overflow:'hidden', border:'1.5px solid #BAC8FF' }}>
                    <img src={photoUrl(filename)} alt={`progress-${i+1}`}
                      style={{ width:'100%', height:'100%', objectFit:'cover' }}
                      onError={e => { e.target.style.display='none' }} />
                  </a>
                  {showProgressWindow && (
                    <button onClick={e => { e.preventDefault(); deleteProgressPhoto(filename) }} disabled={progDeleting===filename}
                      style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', background:'#C0392B', color:'white', border:'2px solid white', cursor:'pointer', fontSize:'.6rem', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1, padding:0 }}>
                      {progDeleting===filename ? '…' : '✕'}
                    </button>
                  )}
                </div>
              ))}
              {canAddThisWeek && progressPhotos.length < 10 && (
                <div onClick={() => progressFileInputRef.current?.click()}
                  style={{ width:72, height:72, borderRadius:8, border:'2px dashed #BAC8FF', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#5A72CC', fontSize:'1.4rem', flexShrink:0 }}>
                  +
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {appt.message && (
        <div style={{ background:C.g50, border:`1px solid ${C.border}`, borderRadius:10, padding:'.85rem 1rem' }}>
          <div style={{ fontSize:'.65rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>Your Notes</div>
          <div style={{ fontSize:'.85rem', color:C.text, lineHeight:1.7 }}>{appt.message}</div>
        </div>
      )}
      {!hideDiagnosis && appt.diagnosis && (
        <div style={{ background:'#EDF2FF', border:'1px solid #BAC8FF', borderRadius:10, padding:'.85rem 1rem' }}>
          <div style={{ fontSize:'.65rem', fontWeight:700, color:'#3451B2', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>🔬 Diagnosis</div>
          <div style={{ fontSize:'.87rem', color:'#1A2155', lineHeight:1.8, whiteSpace:'pre-wrap' }}>{appt.diagnosis}</div>
        </div>
      )}
      {!hideDiagnosis && !appt.diagnosis && !appt.message && (
        <div style={{ textAlign:'center', padding:'1rem', color:C.muted }}>
          <div style={{ fontSize:'.82rem' }}>No diagnosis yet. Check back after your consultation.</div>
        </div>
      )}
    </div>
  )
}

// ─── Prescription Tab ─────────────────────────────────────────────────────────
function PrescriptionTab({ appt, hideFollowUp }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'.85rem' }}>
      {appt.diagnosis && (
        <div style={{ background:'#EDF2FF', border:'1px solid #BAC8FF', borderRadius:10, padding:'.85rem 1rem' }}>
          <div style={{ fontSize:'.65rem', fontWeight:700, color:'#3451B2', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>🔬 Diagnosis</div>
          <div style={{ fontSize:'.87rem', color:'#1A2155', lineHeight:1.8, whiteSpace:'pre-wrap' }}>{appt.diagnosis}</div>
        </div>
      )}
      {appt.prescription && (
        <div style={{ background:'#F0FFF4', border:`1px solid ${C.g300}`, borderRadius:10, padding:'.85rem 1rem' }}>
          <div style={{ fontSize:'.65rem', fontWeight:700, color:C.g600, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>💊 Prescription / Treatment Plan</div>
          <div style={{ fontSize:'.87rem', color:C.text, lineHeight:1.9, whiteSpace:'pre-wrap' }}>{appt.prescription}</div>
        </div>
      )}
      {appt.medication && (
        <div style={{ background:'#FFF8F0', border:'1px solid #FFCCAA', borderRadius:10, padding:'.85rem 1rem' }}>
          <div style={{ fontSize:'.65rem', fontWeight:700, color:'#C25A00', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'.4rem' }}>🏥 Medication</div>
          <div style={{ fontSize:'.87rem', color:C.text, lineHeight:1.9, whiteSpace:'pre-wrap' }}>{appt.medication}</div>
        </div>
      )}
      {!hideFollowUp && appt.followUpDate && (
        <div style={{ background:C.g50, border:`1px solid ${C.border}`, borderRadius:10, padding:'.85rem 1rem', display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:'1.3rem' }}>📅</span>
          <div>
            <div style={{ fontSize:'.65rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em' }}>Follow-up Date</div>
            <div style={{ fontSize:'.9rem', fontWeight:600, color:C.g800, marginTop:'.15rem' }}>{fmtDate(appt.followUpDate)}</div>
          </div>
        </div>
      )}
      {!appt.prescription && !appt.medication && (
        <div style={{ textAlign:'center', padding:'2rem', color:C.muted }}>
          <div style={{ fontSize:'1.8rem', opacity:.2 }}>💊</div>
          <div style={{ fontSize:'.82rem', marginTop:8 }}>No prescription added yet.</div>
        </div>
      )}
    </div>
  )
}


// ─── Reminder Tab ─────────────────────────────────────────────────────────────
// Slice 2: patient reports WHY they're not taking a dose. The server routes the
// reason to the ops or doctor lane (side_effect / want_to_stop → doctor).
function ReportProblem({ appointment }) {
  const { fetchWithAuth } = useAuth()
  const [open, setOpen] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [sent, setSent] = React.useState(null)
  const REASONS = [
    { code: 'forgot', label: 'I keep forgetting', icon: '🕐' },
    { code: 'cost', label: "Can't afford / can't get it", icon: '💰' },
    { code: 'confused', label: 'Confused how to use it', icon: '❓' },
    { code: 'side_effect', label: 'Side effect / reaction', icon: '⚠️' },
    { code: 'want_to_stop', label: 'I want to stop it', icon: '✋' },
  ]
  const submit = async (reason) => {
    setBusy(true)
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/med-issue`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ appointmentId: appointment?.id, reason }) })
      const d = await res.json().catch(() => ({}))
      setSent({ selfServe: !!d.selfServe, reply: d.reply || '', lane: d.lane || 'ops' })
    } catch { setSent({ lane: 'ops' }) } finally { setBusy(false) }
  }
  if (sent && sent.selfServe) return (
    <div style={{ background: '#F0F6FF', border: '1px solid #BBD6FF', borderRadius: 12, padding: '.9rem 1rem', margin: '0 0 1rem', fontSize: '.82rem', color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
      {sent.reply}
    </div>
  )
  if (sent) return (
    <div style={{ background: sent.lane === 'doctor' ? '#FFF0F0' : '#F0FFF4', border: `1px solid ${sent.lane === 'doctor' ? '#F5C6C6' : '#A3DFB8'}`, borderRadius: 12, padding: '.85rem 1rem', margin: '0 0 1rem', fontSize: '.82rem', color: C.text, lineHeight: 1.5 }}>
      {sent.lane === 'doctor'
        ? "✅ Thanks — we've flagged this for your doctor. They'll review and get back to you."
        : '✅ Thanks — the clinic team will help you with this shortly.'}
    </div>
  )
  return (
    <div style={{ margin: '0 0 1rem' }}>
      {!open ? (
        <button onClick={() => setOpen(true)} style={{ width: '100%', padding: '.7rem', borderRadius: 12, border: `1px solid ${C.border}`, background: 'white', color: C.muted, fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: MF }}>
          ⚠️ Having trouble taking your medicines?
        </button>
      ) : (
        <div style={{ background: C.g50, border: `1px solid ${C.border}`, borderRadius: 12, padding: '.85rem 1rem' }}>
          <div style={{ fontSize: '.82rem', fontWeight: 700, color: C.text, marginBottom: '.6rem' }}>What's the problem?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {REASONS.map(r => (
              <button key={r.code} disabled={busy} onClick={() => submit(r.code)} style={{ textAlign: 'left', padding: '.65rem .8rem', borderRadius: 10, border: `1px solid ${C.border}`, background: 'white', color: C.text, fontSize: '.82rem', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', fontFamily: MF, display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 16 }}>{r.icon}</span>{r.label}
              </button>
            ))}
          </div>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '.78rem', cursor: 'pointer', marginTop: 10, fontFamily: MF }}>Cancel</button>
        </div>
      )}
    </div>
  )
}

function ReminderTab({ appointment, premium = false, premiumPrice = 99, onSubscribe }) {
  const { fetchWithAuth } = useAuth()
  const [subscribing, setSubscribing] = useState(false)
  const allReminders = useMemo(() => generateReminders(appointment), [appointment?.id, appointment?.medication, appointment?.prescription])
  const [state, setState] = useState(() => loadState(appointment?.id))
  const [filterDate, setFilterDate] = useState(todayISO())
  const [voiceOn, setVoiceOnState] = useState(isVoiceOn)
  const [freq, setFreqState] = useState(getReminderFreq)
  const [, tick] = useState(0)
  const timerRef = useRef(null)
  const dateScrollRef = useRef(null)
  // Android 12+: exact-alarm access is what keeps daytime (morning/afternoon)
  // reminders from being dropped in Doze. 'denied' → show a one-tap fix banner.
  const [exactAlarm, setExactAlarm] = useState('granted')
  const [dbg, setDbg] = useState(null)
  const [testMsg, setTestMsg] = useState('')
  const isNative = !!_Cap2?.isNativePlatform?.()
  const refreshDbg = useCallback(() => {
    if (!isNative) return
    reminderDebugInfo().then(d => { setDbg(d); setExactAlarm(d.exact === 'denied' ? 'denied' : 'granted') }).catch(() => {})
  }, [isNative])
  useEffect(() => { refreshDbg() }, [refreshDbg])

  useEffect(() => {
    timerRef.current = setInterval(() => tick(n => n + 1), 30000)
    return () => clearInterval(timerRef.current)
  }, [])
  // Load dose marks: paint the local cache instantly, then merge server-persisted
  // marks so ticks sync across devices (website ↔ app), like the prescription flow.
  useEffect(() => {
    if (!appointment?.id) { setState({}); return }
    setState(loadState(appointment.id))
    let alive = true
    ;(async () => {
      try {
        const prefix = `${appointment.id}__`
        const res = await fetchWithAuth(`${API_BASE}/api/reminders/states?prefix=${encodeURIComponent(prefix)}`)
        if (!res.ok) return
        const { states } = await res.json()
        if (!alive || !states) return
        // Re-read local at merge time so marks made while the request was in flight aren't lost.
        const next = { ...loadState(appointment.id) }
        Object.entries(states).forEach(([remId, s]) => {
          if (!remId.startsWith(prefix)) return
          if (s.taken) next[remId] = { taken: true, takenAt: s.takenAt }
          else if (next[remId]?.taken) next[remId] = { taken: false, takenAt: null }
        })
        saveState(appointment.id, next)
        setState(next)
        syncReminderNotifications(allReminders, next)   // cancel/reschedule device alarms for cross-device marks
      } catch (_) { /* offline → keep local cache */ }
    })()
    return () => { alive = false }
  }, [appointment?.id])
  useEffect(() => {
    const container = dateScrollRef.current
    const el = container?.querySelector('[data-today]')
    if (container && el) {
      container.scrollLeft = el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2
    }
  }, [])

  const markTaken = useCallback(async (id, type, date) => {
    if (getSlotStatus(date, type, false) !== 'open') return
    const takenAt = new Date().toISOString()
    setState(prev => { const next = { ...prev, [id]: { taken: true, takenAt } }; saveState(appointment.id, next); syncReminderNotifications(allReminders, next); return next })
    try { await fetchWithAuth(`${API_BASE}/api/reminders/taken`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ remId: id, taken: true, takenAt }) }) } catch(e) {}
  }, [appointment?.id, allReminders])

  const undoTaken = useCallback(async (id) => {
    setState(prev => { const next = { ...prev, [id]: { taken: false, takenAt: null } }; saveState(appointment.id, next); syncReminderNotifications(allReminders, next); return next })
    try { await fetchWithAuth(`${API_BASE}/api/reminders/taken`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ remId: id, taken: false, takenAt: null }) }) } catch(e) {}
  }, [appointment?.id, allReminders])

  if (allReminders.length === 0) return (
    <div style={{ textAlign: 'center', padding: '2rem', color: C.muted }}>
      <div style={{ fontSize: '2rem', opacity: .2, marginBottom: '.6rem' }}>⏰</div>
      <div style={{ fontSize: '.88rem', fontWeight: 600, color: C.text, marginBottom: '.4rem' }}>No reminders found</div>
      <div style={{ fontSize: '.78rem', lineHeight: 1.7 }}>
        Reminders are auto-generated from medication text.<br/>
        Each medication must start with a number like:<br/>
        <code style={{ background: C.g50, padding: '.1rem .4rem', borderRadius: 4, fontSize: '.75rem', display: 'inline-block', marginTop: 4 }}>1) cap doxy 0-0-1 for 45 days</code>
      </div>
    </div>
  )

  const today = todayISO()
  const uniqueDates = [...new Set(allReminders.map(r => r.date))].sort()
  const validDate = uniqueDates.includes(filterDate) ? filterDate : (uniqueDates.includes(today) ? today : uniqueDates[0])
  const dayReminders = allReminders.filter(r => r.date === validDate)
  const todayRems   = allReminders.filter(r => r.date === today)
  const takenToday  = todayRems.filter(r => state[r.id]?.taken).length
  const missedToday = todayRems.filter(r => getSlotStatus(r.date, r.medicationType, state[r.id]?.taken) === 'missed').length

  const windowInfo = { morning: '9:00 AM – 12:00 PM', afternoon: '1:00 PM – 3:00 PM', night: '9:00 PM – 11:59 PM' }
  const typeIcon   = { morning: '🌅', afternoon: '☀️', night: '🌙' }
  const typeLabel  = { morning: 'Morning', afternoon: 'Afternoon', night: 'Night' }
  const typeTheme  = {
    morning:   { bg: '#FFFBEB', border: '#FCD34D', color: '#92400E', btnBg: '#78350F' },
    afternoon: { bg: '#FFF7ED', border: '#FDBA74', color: '#9A3412', btnBg: '#7C2D12' },
    night:     { bg: '#EEF2FF', border: '#A5B4FC', color: '#3730A3', btnBg: '#3730A3' },
  }

  return (
    <div>
      <ReportProblem appointment={appointment} />
      {isNative && dbg && (
        <div style={{ background: '#0F1713', border: '1px solid #223', borderRadius: 14, padding: '.85rem .9rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#8fb', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Reminder status</div>
          {[
            ['Notifications allowed', dbg.permission === 'granted'],
            ['Exact alarms allowed', dbg.exact === 'granted'],
            ['Reminders scheduled', dbg.pending > 0, `${dbg.pending}`],
          ].map(([label, ok, val]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.76rem', color: '#cfe', marginBottom: 4 }}>
              <span style={{ width: 14, flexShrink: 0, color: ok ? '#4ADE80' : '#F87171', fontWeight: 800 }}>{ok ? '✓' : '✕'}</span>
              <span style={{ flex: 1 }}>{label}</span>
              <span style={{ color: '#9fb' }}>{val || (ok ? 'Yes' : 'No')}</span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {dbg.permission !== 'granted' && (
              <button onClick={async () => { await ensureNotifPermission(); refreshDbg() }}
                style={{ background: '#1E6B3C', color: '#fff', border: 'none', borderRadius: 9, padding: '.45rem .8rem', fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Allow notifications</button>
            )}
            {dbg.exact !== 'granted' && (
              <button onClick={async () => { const r = await requestExactAlarms(); setExactAlarm(r); refreshDbg() }}
                style={{ background: '#C2410C', color: '#fff', border: 'none', borderRadius: 9, padding: '.45rem .8rem', fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Allow exact alarms</button>
            )}
            <button onClick={async () => { setTestMsg('Scheduling…'); let n = 0; try { n = await syncReminderNotifications(allReminders, state) } catch {} refreshDbg(); setTestMsg(`Scheduled ${n} reminders ✓${freq !== 'off' ? ` (repeats every ${freq} min)` : ''}`) }}
              style={{ background: '#235233', color: '#fff', border: 'none', borderRadius: 9, padding: '.45rem .8rem', fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Re-sync now</button>
            <button onClick={async () => { setTestMsg('Sending test…'); const r = await sendTestNotification(); if (premium) { try { await speakReminder('Test voice reminder. Time to take your medicines.') } catch {} } setTestMsg(r.ok ? (premium ? 'Test sent — you should hear a voice + get 3 notifications' : 'Test sent — 3 notifications in ~40 seconds') : `Test failed: ${r.reason}`); refreshDbg() }}
              style={{ background: 'transparent', color: '#8fe', border: '1px solid #35A05C', borderRadius: 9, padding: '.45rem .8rem', fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Send test{premium ? ' 🔊' : ''}</button>
          </div>
          {testMsg && <div style={{ fontSize: '.72rem', color: '#9fe', marginTop: 8 }}>{testMsg}</div>}
        </div>
      )}
      {exactAlarm === 'denied' && (
        <div style={{ background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 14, padding: '.8rem .9rem', marginBottom: '1rem', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ flexShrink: 0, marginTop: 1 }}><AlertTriangle size={18} strokeWidth={2} color="#C2410C" /></span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '.8rem', fontWeight: 700, color: '#9A3412' }}>Morning & afternoon reminders may not arrive</div>
            <div style={{ fontSize: '.72rem', color: '#9A3412', lineHeight: 1.5, marginTop: 2 }}>
              Your phone is blocking exact alarms, so daytime reminders can be dropped. Turn on “Alarms &amp; reminders” to fix it.
            </div>
            <button onClick={async () => { const r = await requestExactAlarms(); setExactAlarm(r) }}
              style={{ marginTop: 8, background: '#C2410C', color: '#fff', border: 'none', borderRadius: 9, padding: '.45rem .9rem', fontSize: '.74rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
              Turn on reliable reminders
            </button>
          </div>
        </div>
      )}
      {(() => {
        const pct = todayRems.length ? takenToday / todayRems.length : 0
        const R = 24, CIRC = 2 * Math.PI * R
        return (
          <div style={{ background: 'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)', borderRadius: 20, padding: '1.05rem 1.15rem', marginBottom: '1rem', position: 'relative', overflow: 'hidden', boxShadow: '0 12px 30px rgba(15,61,36,.3)' }}>
            <div style={{ position: 'absolute', top: -46, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle,rgba(133,201,154,.25),transparent 70%)' }}/>
            <div style={{ position: 'absolute', bottom: -60, left: -20, width: 130, height: 130, borderRadius: '50%', background: 'radial-gradient(circle,rgba(255,255,255,.07),transparent 70%)' }}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '.6rem', fontWeight: 700, color: 'rgba(255,255,255,.6)', textTransform: 'uppercase', letterSpacing: '.12em' }}>Today's Progress</div>
                <div style={{ fontSize: '1.55rem', fontWeight: 700, color: '#fff', marginTop: '.25rem', lineHeight: 1 }}>
                  {takenToday}<span style={{ fontSize: '.9rem', fontWeight: 500, color: 'rgba(255,255,255,.5)' }}>/{todayRems.length}</span>
                  <span style={{ fontSize: '.72rem', fontWeight: 500, color: 'rgba(255,255,255,.65)', marginLeft: 6 }}>doses taken</span>
                </div>
                <div style={{ marginTop: '.6rem' }}>
                  {missedToday > 0
                    ? <span style={{ fontSize: '.64rem', fontWeight: 700, color: '#FECACA', background: 'rgba(220,38,38,.28)', padding: '.18rem .55rem', borderRadius: 999 }}>{missedToday} missed today</span>
                    : todayRems.length > 0 && takenToday === todayRems.length
                    ? <span style={{ fontSize: '.64rem', fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,.16)', padding: '.18rem .55rem', borderRadius: 999 }}>All done for today ✨</span>
                    : <span style={{ fontSize: '.64rem', fontWeight: 600, color: 'rgba(255,255,255,.8)', background: 'rgba(255,255,255,.12)', padding: '.18rem .55rem', borderRadius: 999 }}>On track · {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                </div>
              </div>
              <div style={{ position: 'relative', width: 62, height: 62, flexShrink: 0, marginLeft: 10 }}>
                <svg width="62" height="62" style={{ transform: 'rotate(-90deg)', display: 'block' }}>
                  <defs>
                    <linearGradient id="remRing" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FFFFFF"/>
                      <stop offset="100%" stopColor="#8FE3AC"/>
                    </linearGradient>
                  </defs>
                  <circle cx="31" cy="31" r={R} fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="5"/>
                  <circle cx="31" cy="31" r={R} fill="none" stroke="url(#remRing)" strokeWidth="5" strokeLinecap="round" strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - pct)} style={{ transition: 'stroke-dashoffset .5s ease' }}/>
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.76rem', fontWeight: 700, color: '#fff' }}>{Math.round(pct * 100)}%</div>
              </div>
            </div>
            {/* Voice reminder controls — premium subscription; free users see an upsell */}
            {premium ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '.85rem', position: 'relative' }}>
                <button onClick={() => speakReminder(voiceLineForNow(allReminders, state, appointment?.name) || `You have no pending doses right now.`)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.25)', color: '#fff', borderRadius: 999, padding: '.35rem .8rem', fontSize: '.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                  🔊 Play voice reminder
                </button>
                <button onClick={() => { const nv = !voiceOn; setVoiceOn(nv); setVoiceOnState(nv) }}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'transparent', border: 'none', color: 'rgba(255,255,255,.75)', fontSize: '.66rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                  <span style={{ width: 30, height: 17, borderRadius: 999, background: voiceOn ? '#8FE3AC' : 'rgba(255,255,255,.25)', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
                    <span style={{ position: 'absolute', top: 2, left: voiceOn ? 15 : 2, width: 13, height: 13, borderRadius: '50%', background: '#fff', transition: 'left .2s' }}/>
                  </span>
                  Voice {voiceOn ? 'on' : 'off'}
                </button>
              </div>
            ) : (
              <button disabled={subscribing} onClick={async () => { setSubscribing(true); try { await onSubscribe?.() } finally { setSubscribing(false) } }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', marginTop: '.85rem', background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.28)', color: '#fff', borderRadius: 12, padding: '.55rem .8rem', cursor: subscribing ? 'wait' : 'pointer', fontFamily: "'DM Sans',sans-serif", position: 'relative' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                  <span style={{ fontSize: '.95rem' }}>🔒</span>
                  <span style={{ textAlign: 'left', minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '.72rem', fontWeight: 700 }}>Voice reminders</span>
                    <span style={{ display: 'block', fontSize: '.6rem', color: 'rgba(255,255,255,.7)' }}>Hear your medicines read aloud</span>
                  </span>
                </span>
                <span style={{ flexShrink: 0, fontSize: '.66rem', fontWeight: 800, background: '#8FE3AC', color: '#0F3D24', padding: '.28rem .6rem', borderRadius: 999 }}>
                  {subscribing ? 'Opening…' : `Unlock ₹${premiumPrice}/mo`}
                </span>
              </button>
            )}
          </div>
        )
      })()}

      {/* Repeat-nudge frequency — patient picks, notifications re-schedule automatically */}
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '.7rem .85rem', marginBottom: '.9rem', boxShadow: '0 1px 6px rgba(13,34,24,.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '.5rem' }}>
          <span style={{ fontSize: '.85rem' }}>🔔</span>
          <span style={{ fontSize: '.74rem', fontWeight: 700, color: C.text }}>Remind me until I mark taken</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['off', 'Once'], ['30', 'Every 30 min'], ['15', 'Every 15 min']].map(([val, label]) => {
            const sel = freq === val
            return (
              <button key={val} onClick={async () => { setReminderFreq(val); setFreqState(val); setTestMsg('Scheduling…'); let n = 0; try { n = await syncReminderNotifications(allReminders, state) } catch {} refreshDbg(); setTestMsg(`Scheduled ${n} reminders ✓${val !== 'off' ? ` (repeats every ${val} min)` : ''}`) }}
                style={{ flex: 1, padding: '.45rem .3rem', borderRadius: 9, border: `1.5px solid ${sel ? C.g500 : C.border}`, background: sel ? C.g50 : '#fff', color: sel ? C.g700 : C.muted, fontWeight: sel ? 800 : 600, fontSize: '.68rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", transition: 'all .15s' }}>
                {label}
              </button>
            )
          })}
        </div>
        <div style={{ fontSize: '.6rem', color: C.muted, marginTop: '.5rem', lineHeight: 1.4 }}>
          {freq === 'off' ? 'One reminder at each dose time.' : `Extra reminders every ${freq} min for an hour, stopping as soon as you mark the dose taken.`}
        </div>
      </div>

      <div style={{ marginBottom: '.9rem' }}>
        <div style={{ fontSize: '.62rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.4rem' }}>Select Date</div>
        <div ref={dateScrollRef} style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 4 }}>
          {uniqueDates.map(d => {
            const isToday = d === today, isSel = d === validDate
            const cnt  = allReminders.filter(r => r.date === d).length
            const done = allReminders.filter(r => r.date === d && state[r.id]?.taken).length
            const miss = allReminders.filter(r => r.date === d && getSlotStatus(r.date, r.medicationType, state[r.id]?.taken) === 'missed').length
            const [, mm, dd] = d.split('-')
            const wd = new Date(d + 'T00:00:00').toLocaleDateString('en', { weekday: 'short' })
            return (
              <button key={d} onClick={() => setFilterDate(d)}
                {...(isToday ? { 'data-today': 'true' } : {})}
                style={{ flexShrink: 0, minWidth: 58, padding: '.42rem .4rem', borderRadius: 13, border: `1px solid ${isSel ? 'transparent' : isToday ? C.g400 : C.border}`, background: isSel ? 'linear-gradient(150deg,#0F3D24,#2E8B4F)' : isToday ? C.g50 : 'white', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", transition: 'all .15s', boxShadow: isSel ? '0 4px 14px rgba(15,61,36,.32)' : '0 1px 3px rgba(13,34,24,.04)' }}>
                <div style={{ fontSize: '.55rem', color: isSel ? 'rgba(255,255,255,.6)' : C.muted }}>{dd}/{mm}</div>
                <div style={{ fontSize: '.68rem', fontWeight: 700, color: isSel ? 'white' : isToday ? C.g700 : C.text, margin: '2px 0' }}>{isToday ? 'Today' : wd}</div>
                <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                  {cnt === 0 ? <span style={{ fontSize: '.52rem', color: isSel ? 'rgba(255,255,255,.4)' : C.muted }}>—</span> : <>
                    {miss > 0 && <span style={{ fontSize: '.6rem', fontWeight: 700, color: isSel ? '#fca5a5' : '#dc2626' }}>✗{miss}</span>}
                    {done > 0 && <span style={{ fontSize: '.6rem', fontWeight: 700, color: isSel ? '#86efac' : '#16a34a' }}>✓{done}</span>}
                    {done === 0 && miss === 0 && <span style={{ fontSize: '.52rem', color: isSel ? 'rgba(255,255,255,.5)' : C.muted }}>{cnt} due</span>}
                  </>}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* One card per time slot; each medicine is a row inside it */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
        {['morning', 'afternoon', 'night'].filter(t => dayReminders.some(r => r.medicationType === t)).map(t => {
          const group = dayReminders.filter(r => r.medicationType === t)
          const tc = typeTheme[t] || typeTheme.morning
          const slotNow = getSlotStatus(validDate, t, false)   // open | missed | future (ignoring taken)
          const allTaken = group.every(r => state[r.id]?.taken)
          const headBg = allTaken ? '#F6FDF8' : slotNow === 'missed' ? '#FFF7F7' : slotNow === 'open' ? tc.bg : '#FBFBFA'
          return (
            <div key={t} style={{ borderRadius: 16, border: `1px solid ${allTaken ? C.g300 : slotNow === 'missed' ? '#FECACA' : C.border}`, background: '#fff', overflow: 'hidden', boxShadow: '0 2px 14px rgba(13,34,24,.06)', transition: 'all .25s' }}>
              {/* Slot header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.7rem .85rem', background: headBg, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', background: 'rgba(255,255,255,.85)', border: `1px solid ${slotNow === 'open' ? tc.border : C.border}`, boxShadow: '0 1px 4px rgba(13,34,24,.06)' }}>{typeIcon[t]}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.84rem', color: slotNow === 'missed' && !allTaken ? '#B91C1C' : C.text, letterSpacing: '-.01em' }}>{typeLabel[t]}</div>
                  <div style={{ fontSize: '.6rem', color: C.muted, marginTop: 1 }}>{windowInfo[t]} · {group.length} medicine{group.length > 1 ? 's' : ''}</div>
                </div>
                {allTaken
                  ? <span style={{ flexShrink: 0, fontSize: '.62rem', fontWeight: 700, color: C.g600, background: C.g100, padding: '.2rem .55rem', borderRadius: 999 }}>✓ All taken</span>
                  : slotNow === 'missed' ? <span style={{ flexShrink: 0, fontSize: '.62rem', fontWeight: 700, color: '#DC2626', background: '#FEE2E2', padding: '.2rem .55rem', borderRadius: 999 }}>Missed</span>
                  : slotNow === 'open'   ? <span style={{ flexShrink: 0, fontSize: '.62rem', fontWeight: 700, color: tc.color, border: `1px solid ${tc.border}`, background: 'rgba(255,255,255,.9)', padding: '.2rem .55rem', borderRadius: 999 }}>Open now</span>
                  : <span style={{ flexShrink: 0, fontSize: '.62rem', fontWeight: 600, color: C.muted, background: '#F3F4F6', padding: '.2rem .55rem', borderRadius: 999 }}>{validDate === today ? 'Later today' : 'Upcoming'}</span>}
              </div>
              {/* Medicine rows */}
              {group.map((r, idx) => {
                const s = state[r.id], taken = s?.taken || false
                const slot = getSlotStatus(r.date, r.medicationType, taken)
                const canAct = slot === 'open' && !taken
                return (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.65rem .85rem', borderTop: idx > 0 ? `1px solid ${C.border}` : 'none' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: taken || slot === 'missed' ? '.8rem' : '.85rem', fontWeight: 800, background: taken ? C.g500 : slot === 'missed' ? '#FEE2E2' : C.g50, border: taken ? 'none' : `1px solid ${slot === 'missed' ? '#FCA5A5' : C.border}`, color: taken ? '#fff' : slot === 'missed' ? '#DC2626' : C.muted, boxShadow: taken ? '0 2px 8px rgba(61,138,87,.35)' : 'none', transition: 'all .2s' }}>
                      {taken ? '✓' : slot === 'missed' ? '✗' : '💊'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: C.text, fontSize: '.83rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.medicationName}{r.doses > 1 && <span style={{ fontSize: '.66rem', color: C.muted, fontWeight: 500 }}> ×{r.doses}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <span style={{ fontSize: '.58rem', color: C.muted, flexShrink: 0 }}>Day {r.dayIndex + 1}/{r.durationDays}</span>
                        <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 999, maxWidth: 80 }}>
                          <div style={{ height: '100%', borderRadius: 999, background: taken ? C.g500 : slot === 'missed' ? C.error : C.g300, width: `${((r.dayIndex + 1) / r.durationDays) * 100}%` }}/>
                        </div>
                        {taken && s?.takenAt && <span style={{ fontSize: '.58rem', color: C.g600 }}>{new Date(s.takenAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                      </div>
                    </div>
                    {taken ? (
                      <button onClick={() => undoTaken(r.id)} style={{ flexShrink: 0, padding: '.35rem .8rem', borderRadius: 999, border: `1px solid ${C.g300}`, background: 'transparent', color: C.g600, fontSize: '.66rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Undo</button>
                    ) : canAct ? (
                      <button onClick={() => markTaken(r.id, r.medicationType, r.date)} style={{ flexShrink: 0, padding: '.4rem .85rem', borderRadius: 999, border: 'none', background: 'linear-gradient(135deg,#1E6B3C,#35A05C)', color: 'white', fontSize: '.66rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", boxShadow: '0 3px 12px rgba(30,107,60,.4)' }}>Mark Taken</button>
                    ) : (
                      <span style={{ flexShrink: 0, padding: '.35rem .8rem', borderRadius: 999, background: '#F6F7F6', color: C.muted, fontSize: '.64rem', fontWeight: 600 }}>{slot === 'missed' ? 'Missed' : 'Not yet'}</span>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Treatment Plan Tab (native app, opened from a home tile) ──────────────────
function PlanTab({ appt, onTrackDoses, onViewPrescription }) {
  const { topical, oral, count } = useMemo(() => structuredMeds(appt), [appt?.id, appt?.medication, appt?.prescription])
  const all = [...topical, ...oral]
  const planDays = all.length ? Math.max(20, ...all.map(m => m.durationDays || 0)) : 20
  const raw = appt?.date || todayISO()
  const startISO = raw.includes('T') ? raw.split('T')[0] : raw.split(' ')[0]
  const dayNum = Math.min(planDays, Math.max(1, Math.floor((Date.now() - new Date(startISO + 'T00:00:00').getTime()) / 86400000) + 1))
  const pct = Math.round((dayNum / planDays) * 100)
  const daysLeft = Math.max(0, planDays - dayNum)
  const weeks = Math.min(4, Math.ceil(planDays / 7))
  const currentWeek = Math.ceil(dayNum / 7)

  const MedGroup = ({ title, icon, items }) => items.length === 0 ? null : (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: '.8rem', boxShadow: '0 2px 14px rgba(13,34,24,.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.7rem .95rem', background: C.g50, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: '.95rem' }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: '.8rem', color: C.text }}>{title}</span>
        <span style={{ fontSize: '.62rem', color: C.muted }}>· {items.length}</span>
      </div>
      {items.map((m, i) => (
        <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.7rem .95rem', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', background: C.g50, border: `1px solid ${C.border}` }}>{m.icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '.84rem', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
            <div style={{ fontSize: '.66rem', color: C.muted, marginTop: 2 }}>{m.instruction} · {m.schedule}</div>
          </div>
          {m.durationDays > 0 && <span style={{ flexShrink: 0, fontSize: '.62rem', fontWeight: 700, color: C.g600, background: C.g100, padding: '.18rem .5rem', borderRadius: 999 }}>{m.durationDays} days</span>}
        </div>
      ))}
    </div>
  )

  return (
    <div>
      {/* Plan overview */}
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '1rem 1.1rem', marginBottom: '.8rem', boxShadow: '0 2px 14px rgba(13,34,24,.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.6rem' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '.6rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.1em' }}>Active Plan</div>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: C.g900, marginTop: 3 }}>{appt?.service || 'Treatment'}</div>
          </div>
          <span style={{ flexShrink: 0, fontSize: '.64rem', fontWeight: 700, color: C.g600, background: C.g100, padding: '.22rem .6rem', borderRadius: 999 }}>Day {dayNum} of {planDays}</span>
        </div>
        <div style={{ height: 7, background: '#E8F1EC', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg,#1E6B3C,#35A05C)', borderRadius: 999, transition: 'width .4s' }}/>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.66rem', color: C.muted, marginTop: 5 }}>
          <span>{pct}% complete</span>
          <span>{daysLeft} days left</span>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          {Array.from({ length: weeks }, (_, i) => i + 1).map(w => (
            <span key={w} style={{ fontSize: '.62rem', fontWeight: 700, padding: '.18rem .55rem', borderRadius: 999, background: dayNum >= w * 7 ? C.g100 : currentWeek === w ? '#FFF8E1' : '#F3F4F6', color: dayNum >= w * 7 ? C.g600 : currentWeek === w ? '#B7770D' : C.muted, border: `1px solid ${dayNum >= w * 7 ? C.g300 : currentWeek === w ? '#FFE082' : C.border}` }}>
              {dayNum >= w * 7 ? '✓ ' : currentWeek === w ? '→ ' : ''}Week {w}
            </span>
          ))}
        </div>
      </div>

      {count === 0 ? (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.2rem', marginBottom: '.8rem', textAlign: 'center', color: C.muted, fontSize: '.78rem' }}>
          No structured medicines found — see the full prescription below.
        </div>
      ) : (
        <>
          <MedGroup title="Topical — apply on skin" icon="🧴" items={topical}/>
          <MedGroup title="Oral — take by mouth" icon="💊" items={oral}/>
        </>
      )}

      {/* Doctor note */}
      {appt?.diagnosis && (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '.9rem 1rem', marginBottom: '.8rem', boxShadow: '0 2px 14px rgba(13,34,24,.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '.45rem' }}>
            <div style={{ width: 30, height: 30, borderRadius: 9, background: C.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.9rem' }}>👩‍⚕️</div>
            <span style={{ fontWeight: 700, fontSize: '.8rem', color: C.text }}>Doctor's Note</span>
          </div>
          <div style={{ fontSize: '.78rem', color: '#3a4a42', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{appt.diagnosis}</div>
        </div>
      )}

      {/* Follow-up */}
      {appt?.followUpDate && (
        <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 16, padding: '.85rem 1rem', marginBottom: '.8rem', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.1rem' }}>📅</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '.78rem', color: '#8D6E00' }}>Follow-up visit</div>
            <div style={{ fontSize: '.7rem', color: '#A07800', marginTop: 1 }}>{fmtDate(appt.followUpDate)}</div>
          </div>
        </div>
      )}

      <button onClick={onTrackDoses} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', background: 'linear-gradient(135deg,#1E6B3C,#35A05C)', color: '#fff', border: 'none', borderRadius: 14, padding: '.8rem', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", boxShadow: '0 4px 14px rgba(30,107,60,.35)', marginBottom: '.6rem' }}>
        ⏰ Track Today's Doses
      </button>
      <button onClick={onViewPrescription} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', background: 'none', color: C.g600, border: `1.5px solid ${C.g300}`, borderRadius: 14, padding: '.75rem', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
        📄 View Full Prescription
      </button>
    </div>
  )
}

// ─── Rx Upload Tab ─────────────────────────────────────────────────────────────
// Fetch helper that never fails on "body stream already read"
async function safeApiFetch(url, opts = {}) {
  const res = await fetch(url, opts)
  const text = await res.text().catch(() => '{}')
  let data = {}
  try { data = JSON.parse(text) } catch(_) {}
  return { ok: res.ok, status: res.status, data }
}

// ── Specialization validation (mirrors server logic) ─────────────────────────
const DERM_SPEC_KEYWORDS = [
  'dermatol','dvl','cosmetol','trichol','aesthetic physician',
  'dermatosurg','skin specialist','venereol','leprosy','skin care'
]
const DERM_MED_KEYWORDS = [
  'adapalene','tretinoin','retinol','clindamycin','doxycycline','minocycline',
  'benzoyl peroxide','salicylic acid','azelaic acid','hydroquinone','kojic',
  'alpha arbutin','glycolic','lactic acid','mandelic','isotretinoin',
  'tranexamic','calcipotriol','tacrolimus','pimecrolimus','clobetasol',
  'betamethasone','mometasone','fluocinolone','desonide','ketoconazole',
  'terbinafine','clotrimazole','miconazole','ivermectin','permethrin',
  'metronidazole','minoxidil','finasteride','mupirocin','fusidic acid',
  'non-comedogenic','oil free moisturizer','sunscreen','spf'
]

function resolveSpecializationValid(p) {
  // Use DB value if already computed
  if (p.specializationValid === 1) return 1
  if (p.specializationValid === 0) return 0
  // Fall back: check specialization text
  if (p.specialization && p.specialization.trim()) {
    const s = p.specialization.toLowerCase()
    if (DERM_SPEC_KEYWORDS.some(k => s.includes(k))) return 1
    return 0  // specialization text present but not derm
  }
  // Fall back: infer from medication names (2+ derm drug matches = derm prescription)
  if (p.medicationText) {
    const t = p.medicationText.toLowerCase()
    const matches = DERM_MED_KEYWORDS.filter(k => t.includes(k))
    if (matches.length >= 2) return 1
  }
  return null  // genuinely unknown
}

// Prescription cache — persists across server restarts so data never disappears
const RX_CACHE_KEY = email => `revive_rx_cache_${email}`
function saveRxCache(email, list) {
  try { localStorage.setItem(RX_CACHE_KEY(email), JSON.stringify(list)) } catch {}
}
function loadRxCache(email) {
  try { return JSON.parse(localStorage.getItem(RX_CACHE_KEY(email)) || 'null') } catch { return null }
}

// ── Unified Medication Calendar for all uploaded prescriptions ────────────────
function RxAllRemindersCalendar({ prescriptions, remStates, onMarkTaken, onUndoTaken }) {
  const [filterDate, setFilterDate] = React.useState(todayISO())
  const [, tick] = React.useState(0)
  const timerRef = React.useRef(null)
  const dateScrollRef = React.useRef(null)
  React.useEffect(() => {
    timerRef.current = setInterval(() => tick(n => n + 1), 30000)
    return () => clearInterval(timerRef.current)
  }, [])
  React.useEffect(() => {
    const container = dateScrollRef.current
    const el = container?.querySelector('[data-today]')
    if (container && el) {
      container.scrollLeft = el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2
    }
  }, [])

  const typeOrder = { morning: 0, afternoon: 1, night: 2 }

  const allReminders = React.useMemo(() => {
    const combined = []
    const order = { morning: 0, afternoon: 1, night: 2 }
    prescriptions.forEach(p => {
      const startDate = (p.createdAt || todayISO()).slice(0, 10)
      const rems = generateReminders({ id: p.id, date: startDate, medication: p.medicationText, prescription: '' })
      rems.forEach(r => combined.push({ ...r, prescriptionId: p.id }))
    })
    return combined.sort((a, b) => a.date !== b.date ? a.date.localeCompare(b.date) : (order[a.medicationType]??3)-(order[b.medicationType]??3))
  }, [prescriptions])
  const typeIcon  = { morning: '🌅', afternoon: '☀️', night: '🌙' }
  const typeLabel = { morning: 'Morning', afternoon: 'Afternoon', night: 'Night' }
  const winInfo   = { morning: '9am–12pm', afternoon: '1pm–3pm', night: '9pm–12am' }
  const typeTheme = {
    morning:   { bg:'#FFFBEB', border:'#FCD34D', color:'#92400E', btnBg:'#78350F' },
    afternoon: { bg:'#FFF7ED', border:'#FDBA74', color:'#9A3412', btnBg:'#7C2D12' },
    night:     { bg:'#EEF2FF', border:'#A5B4FC', color:'#3730A3', btnBg:'#3730A3' },
  }

  if (allReminders.length === 0) return null

  const today = todayISO()
  const uniqueDates = [...new Set(allReminders.map(r => r.date))].sort()
  const validDate = uniqueDates.includes(filterDate) ? filterDate : (uniqueDates.includes(today) ? today : uniqueDates[0])
  const dayRems   = allReminders.filter(r => r.date === validDate)
  const todayRems = allReminders.filter(r => r.date === today)
  const takenToday  = todayRems.filter(r => remStates[r.prescriptionId]?.[r.id]?.taken).length
  const missedToday = todayRems.filter(r => getSlotStatus(r.date, r.medicationType, remStates[r.prescriptionId]?.[r.id]?.taken) === 'missed').length

  return (
    <div style={{ marginTop: '1.5rem', border: '1.5px solid ' + C.border, borderRadius: 14, overflow: 'hidden', background: 'white', boxShadow: '0 2px 12px rgba(13,34,24,.07)' }}>
      {/* Header */}
      <div style={{ background: C.headerGrad, padding: '.85rem 1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: '1rem' }}>📅</div>
        <div>
          <div style={{ fontWeight: 700, color: 'white', fontSize: '.88rem' }}>Medication Calendar</div>
          <div style={{ fontSize: '.62rem', color: 'rgba(255,255,255,.6)', marginTop: 1 }}>{allReminders.length} total reminders across {uniqueDates.length} days</div>
        </div>
      </div>

      <div style={{ padding: '1rem' }}>
        {/* Today's progress */}
        <div style={{ background: `linear-gradient(135deg,${C.g100},${C.g50})`, border: '1px solid ' + C.border, borderRadius: 10, padding: '.75rem 1rem', marginBottom: '.85rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem' }}>
            <div>
              <div style={{ fontSize: '.58rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em' }}>Today's Progress</div>
              <div style={{ fontSize: '1.15rem', fontWeight: 700, color: C.g800, marginTop: '.1rem' }}>
                {takenToday}/{todayRems.length}<span style={{ fontSize: '.75rem', fontWeight: 400, color: C.muted, marginLeft: 5 }}>doses taken</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {missedToday > 0 && <div style={{ fontSize: '.68rem', fontWeight: 600, color: C.error }}>⚠ {missedToday} missed</div>}
              <div style={{ fontSize: '.64rem', color: C.muted, marginTop: 2 }}>{new Date().toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</div>
            </div>
          </div>
          {todayRems.length > 0 && (
            <div style={{ height: 5, background: C.border, borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 999, background: C.g500, width: `${(takenToday/todayRems.length)*100}%`, transition: 'width .4s ease' }}/>
            </div>
          )}
          <div style={{ display: 'flex', gap: 5, marginTop: '.6rem', flexWrap: 'wrap' }}>
            {[['morning','🌅 9am–12pm'],['afternoon','☀️ 1pm–3pm'],['night','🌙 9pm–12am']].map(([t,l]) => (
              <div key={t} style={{ fontSize: '.58rem', background: 'white', border: '1px solid '+C.border, borderRadius: 4, padding: '.15rem .45rem', color: C.muted }}>{l}</div>
            ))}
          </div>
        </div>

        {/* Date selector */}
        <div style={{ fontSize: '.58rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.4rem' }}>Select Date</div>
        <div ref={dateScrollRef} style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 6, marginBottom: '.85rem' }}>
          {uniqueDates.map(d => {
            const isToday = d === today, isSel = d === validDate
            const cnt  = allReminders.filter(r => r.date === d).length
            const done = allReminders.filter(r => r.date === d && remStates[r.prescriptionId]?.[r.id]?.taken).length
            const miss = allReminders.filter(r => r.date === d && getSlotStatus(r.date, r.medicationType, remStates[r.prescriptionId]?.[r.id]?.taken) === 'missed').length
            const [y,m,day] = d.split('-')
            const label = new Date(d+'T00:00:00').toLocaleDateString('en-IN', { day:'numeric', month:'short' })
            return (
              <button key={d} onClick={() => setFilterDate(d)}
                {...(isToday ? { 'data-today': 'true' } : {})}
                style={{ flexShrink: 0, minWidth: 54, padding: '.4rem .4rem', borderRadius: 8, border: '1.5px solid '+(isSel?C.g600:miss>0?'#FCA5A5':C.border), background: isSel?C.g700:miss>0?'#FEF2F2':isToday?C.g50:'white', color: isSel?'white':miss>0?C.error:C.text, cursor: 'pointer', fontFamily:"'DM Sans',sans-serif", textAlign:'center' }}>
                <div style={{ fontSize: '.58rem', fontWeight: 700 }}>{label}</div>
                <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                  {cnt === 0 ? <span style={{ fontSize: '.52rem', color: isSel?'rgba(255,255,255,.4)':C.muted }}>—</span> : <>
                    {miss > 0 && <span style={{ fontSize: '.6rem', fontWeight: 700, color: isSel?'#fca5a5':'#dc2626' }}>✗{miss}</span>}
                    {done > 0 && <span style={{ fontSize: '.6rem', fontWeight: 700, color: isSel?'#86efac':'#16a34a' }}>✓{done}</span>}
                    {done === 0 && miss === 0 && <span style={{ fontSize: '.52rem', color: isSel?'rgba(255,255,255,.5)':C.muted }}>{cnt} due</span>}
                  </>}
                </div>
                {isToday && <div style={{ fontSize: '.5rem', color: isSel?'rgba(255,255,255,.7)':C.g600, fontWeight: 600 }}>Today</div>}
              </button>
            )
          })}
        </div>

        {/* Reminders for selected date */}
        <div style={{ fontSize: '.58rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.5rem' }}>
          {validDate === today ? "Today's Reminders" : new Date(validDate+'T00:00:00').toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}
          <span style={{ marginLeft: 6, color: C.g600, textTransform:'none', fontWeight:400 }}>
            ({dayRems.filter(r => remStates[r.prescriptionId]?.[r.id]?.taken).length}/{dayRems.length} taken)
          </span>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap: '.4rem' }}>
          {dayRems.map(r => {
            const isTaken = !!remStates[r.prescriptionId]?.[r.id]?.taken
            const slot    = getSlotStatus(r.date, r.medicationType, isTaken)
            const tc      = typeTheme[r.medicationType] || typeTheme.morning
            const canAct  = slot === 'open' && !isTaken
            const takenAt = remStates[r.prescriptionId]?.[r.id]?.takenAt
            return (
              <div key={r.id} style={{ borderRadius: 10, border: '1.5px solid '+(isTaken?C.g300:slot==='missed'?'#FCA5A5':slot==='open'?tc.border:C.border), background: isTaken?'#F0FFF4':slot==='missed'?'#FEF2F2':slot==='open'?tc.bg:'#FAFAFA', padding:'.6rem .85rem', display:'flex', alignItems:'center', gap:9 }}>
                <div style={{ width:32, height:32, borderRadius:8, background: isTaken?C.g100:slot==='missed'?'#FEE2E2':'rgba(255,255,255,.8)', border:'1.5px solid '+(isTaken?C.g300:slot==='missed'?'#FCA5A5':tc.border), display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.85rem', flexShrink:0 }}>
                  {typeIcon[r.medicationType]}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, color:C.text, fontSize:'.82rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.medicationName}</div>
                  <div style={{ fontSize:'.62rem', color:C.muted }}>
                    {typeLabel[r.medicationType]} · {winInfo[r.medicationType]}
                    {isTaken && takenAt && ' · Taken ' + new Date(takenAt).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}
                  </div>
                </div>
                {isTaken
                  ? <button onClick={() => onUndoTaken(r.prescriptionId, r.id)} style={{ padding:'.25rem .65rem', borderRadius:6, border:'1px solid '+C.g400, background:C.g700, color:'white', fontSize:'.68rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Undo</button>
                  : canAct
                    ? <button onClick={() => onMarkTaken(r.prescriptionId, r.id, r.medicationType, r.date)} style={{ padding:'.25rem .65rem', borderRadius:6, border:'none', background:tc.btnBg, color:'white', fontSize:'.68rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Mark Taken</button>
                    : <span style={{ fontSize:'.65rem', color:slot==='missed'?C.error:C.muted, fontStyle:'italic', fontWeight:slot==='missed'?600:400 }}>{slot==='missed'?'Missed':'Upcoming'}</span>
                }
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function RxUploadTab({ onAdd }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { products } = useProducts()
  const fileRef = React.useRef(null)
  const cached = loadRxCache(user?.email)
  const [prescriptions, setPrescriptions] = React.useState(cached || [])
  const [loading, setLoading]   = React.useState(!cached) // skip spinner if cache exists
  const [serverDown, setServerDown] = React.useState(false)
  const [scanning, setScanning] = React.useState(false)
  const [scanMsg, setScanMsg]   = React.useState('')
  const [rxTab, setRxTab]             = React.useState({})  // {id: 'details'|'reminders'}
  const [rxFilterDate, setRxFilterDate] = React.useState({}) // {id: 'YYYY-MM-DD'}
  const [editingId, setEditingId] = React.useState(null)
  const [editText, setEditText]   = React.useState('')
  const [saving, setSaving]       = React.useState(false)
  const [reparsing, setReparsing] = React.useState(null)
  const [remStates, setRemStates] = React.useState({})
  const [waPref, setWaPref]       = React.useState({ enabled: false, phone: '' })
  const [waPhone, setWaPhone]     = React.useState('')
  const [waCountry, setWaCountry]   = React.useState('91')
  const [waSetup, setWaSetup]       = React.useState(false)
  const [waChangeNum, setWaChangeNum] = React.useState(false)
  const [waSaving, setWaSaving]     = React.useState(false)
  const [showUpload, setShowUpload] = React.useState(false) // Rx upload hidden by default; panel leads with reminders

  const lsKey  = id => `rx-rem-${id}`
  const loadRS = id => { try { return JSON.parse(localStorage.getItem(lsKey(id)) || '{}') } catch { return {} } }
  const saveRS = (id, s) => { try { localStorage.setItem(lsKey(id), JSON.stringify(s)) } catch {} }

  const typeIcon  = { morning: '🌅', afternoon: '☀️', night: '🌙' }
  const typeLabel = { morning: 'Morning', afternoon: 'Afternoon', night: 'Night' }
  const winInfo   = { morning: '9am–12pm', afternoon: '1pm–3pm', night: '9pm–12am' }
  const getActiveTab   = id => rxTab[id] || 'prescription'
  const setActiveTab   = (id, tab) => setRxTab(prev => ({ ...prev, [id]: tab }))
  const getFilterDate  = (id, reminders) => {
    const today = todayISO()
    const dates = [...new Set(reminders.map(r => r.date))].sort()
    const saved = rxFilterDate[id]
    return dates.includes(saved) ? saved : (dates.includes(today) ? today : (dates[0] || today))
  }
  const setFilter = (id, date) => setRxFilterDate(prev => ({ ...prev, [id]: date }))
  const parseMedTable = (text) => {
    if (!text) return []
    const lines = text.split('\n').filter(l => l.trim())
    return lines.map(line => {
      const m = line.match(/^(\d+)\)\s*(.+?)\s+(\d+[-]\d+[-]\d+)\s+for\s+(\d+\s*days?)\s*$/i)
      if (m) {
        const [, num, name, timing, duration] = m
        const [mo, af, ni] = timing.split('-').map(Number)
        const slots = [mo > 0 && 'Morning', af > 0 && 'Afternoon', ni > 0 && 'Night'].filter(Boolean)
        return { num, name, timing, duration, slots: slots.join(' + ') || '—' }
      }
      const numM = line.match(/^(\d+)\)/)
      const timM = line.match(/(\d+[-]\d+[-]\d+)/)
      const durM = line.match(/for\s+(\d+\s*days?)/i)
      if (!numM) return null
      const rawName = line.replace(/^\d+\)\s*/, '').replace(/\s+\d+-\d+-\d+.*$/, '').trim()
      let slots = '—'
      if (timM) {
        const [mo, af, ni] = timM[1].split('-').map(Number)
        slots = [mo > 0 && 'Morning', af > 0 && 'Afternoon', ni > 0 && 'Night'].filter(Boolean).join(' + ') || '—'
      }
      return { num: numM[1], name: rawName, timing: timM ? timM[1] : '—', duration: durM ? durM[1] : '—', slots }
    }).filter(Boolean)
  }
  const typeTheme = {
    morning:   { bg: '#FFFBEB', border: '#FCD34D', color: '#92400E', btnBg: '#78350F' },
    afternoon: { bg: '#FFF7ED', border: '#FDBA74', color: '#9A3412', btnBg: '#7C2D12' },
    night:     { bg: '#EEF2FF', border: '#A5B4FC', color: '#3730A3', btnBg: '#3730A3' },
  }

  React.useEffect(() => { loadList(); loadWaPref() }, [])

  const authHdr = () => ({ Authorization: `Bearer ${user?.token || ''}` })

  const loadWaPref = async () => {
    try {
      const { ok, data } = await safeApiFetch(`${API_BASE}/api/reminders/whatsapp-pref`, { headers: authHdr() })
      if (ok) {
        setWaPref(data)
        // Try to split saved phone into country code + number
        const saved = data.phone || ''
        const knownCodes = ['971','966','91','44','65','60','61','49','33','81','86','1']
        let matched = '91', number = saved
        for (const code of knownCodes) {
          if (saved.startsWith(code)) { matched = code; number = saved.slice(code.length); break }
        }
        setWaCountry(matched)
        setWaPhone(number)
      }
    } catch(e) {}
  }

  const saveWaPref = async (enabled) => {
    setWaSaving(true)
    try {
      const digits = waPhone.replace(/[^0-9]/g, '')
      const phone = waCountry + digits
      if (enabled && digits.length < 7) { alert('Please enter a valid WhatsApp number'); return }
      const { ok, data } = await safeApiFetch(`${API_BASE}/api/reminders/whatsapp-pref`, {
        method: 'POST', headers: { ...authHdr(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, phone })
      })
      if (ok) {
        setWaPref({ enabled: data.enabled, phone: data.phone })
        setWaSetup(false)
        setWaChangeNum(false)
        if (enabled) alert('✅ WhatsApp reminders enabled! You\'ll get medication reminders at 8am, 1pm and 9pm.')
        else alert('WhatsApp reminders stopped.')
      } else {
        alert('Could not save preference. Please make sure the server is running and try again.\n\nError: ' + (data?.error || data?.message || JSON.stringify(data)))
      }
    } catch(e) { alert('Could not save preference: ' + e.message) }
    finally { setWaSaving(false) }
  }

  const loadList = async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const { ok, status, data } = await safeApiFetch(`${API_BASE}/api/prescriptions`, { headers: authHdr() })
      if (status === 401) { navigate('/login'); return }
      if (!ok) throw new Error('Server error')
      const list = data.prescriptions || []
      setPrescriptions(list)
      saveRxCache(user?.email, list)
      setServerDown(false)
      const rs = {}
      list.forEach(p => { rs[p.id] = loadRS(p.id) })
      // Merge with server-persisted taken states (survives domain/device changes)
      try {
        const { ok: sok, data: sdata } = await safeApiFetch(`${API_BASE}/api/prescription-reminder/states`, { headers: authHdr() })
        if (sok && sdata.states) {
          Object.entries(sdata.states).forEach(([remId, s]) => {
            // remId format: ${prescriptionId}__${name}__${type}__${i}
            const prescriptionId = remId.split('__')[0]
            if (!rs[prescriptionId]) rs[prescriptionId] = {}
            if (s.taken) rs[prescriptionId][remId] = { taken: true, takenAt: s.takenAt }
            else if (rs[prescriptionId][remId]?.taken) rs[prescriptionId][remId] = { taken: false }
          })
        }
      } catch(_) {}
      setRemStates(rs)
    } catch(e) {
      // Network error = server restarting — keep showing cached data, retry in 4s
      console.warn('[RxUploadTab] Server unreachable, retrying...', e.message)
      setServerDown(true)
      setTimeout(() => loadList(true), 4000)
    }
    finally { if (!silent) setLoading(false) }
  }

  const scan = async (file) => {
    setScanning(true)
    setScanMsg('Uploading...')
    try {
      const fd = new FormData()
      fd.append('prescription', file)
      setScanMsg('Reading prescription... this takes about 15 seconds')
      const { ok, data } = await safeApiFetch(`${API_BASE}/api/prescriptions/scan`, { method: 'POST', body: fd, headers: authHdr() })
      if (!ok) throw new Error(data.message || 'Scan failed')
      await loadList()
      setActiveTab(data.id, 'prescription')
      if (data.ocrError) {
        alert('Prescription uploaded! OCR could not read the image automatically. Please click "Edit / Correct" to enter medication details manually.')
      }
      // Prompt to enable WhatsApp reminders if not already on
      if (!waPref.enabled) {
        setWaSetup(true)
      }
    } catch(e) { alert('Scan failed: ' + e.message) }
    finally { setScanning(false); setScanMsg('') }
  }

  const del = async (id) => {
    if (!window.confirm('Delete this prescription?')) return
    await safeApiFetch(`${API_BASE}/api/prescriptions/${id}`, { method: 'DELETE', headers: authHdr() })
    setPrescriptions(prev => prev.filter(p => p.id !== id))
  }

  const reparse = async (id) => {
    setReparsing(id)
    try {
      const { ok, data } = await safeApiFetch(`${API_BASE}/api/prescriptions/${id}/reparse`, { method: 'POST', headers: authHdr() })
      if (ok) {
        setPrescriptions(prev => prev.map(p => p.id === id ? {
          ...p,
          medicationText:       data.medicationText       || p.medicationText,
          doctorName:           data.doctorName           !== undefined ? data.doctorName           : p.doctorName,
          clinicName:           data.clinicName           !== undefined ? data.clinicName           : p.clinicName,
          specialization:       data.specialization       !== undefined ? data.specialization       : p.specialization,
          specializationValid:  data.specializationValid  !== undefined ? data.specializationValid  : p.specializationValid,
          diagnosis:            data.diagnosis            !== undefined ? data.diagnosis            : p.diagnosis,
          rxNotes:              data.rxNotes              !== undefined ? data.rxNotes              : p.rxNotes
        } : p))
        const gotDetails = data.doctorName || data.diagnosis || data.clinicName
        if (data.visionParsed) alert('✅ AI read your prescription image — medications and details extracted!')
        else if (data.aiParsed) alert('✅ AI formatted your medications successfully!')
        else if (data.localParsed) alert('✅ Medications extracted!')
        else if (gotDetails) alert('✅ Details extracted (Doctor, Specialization, Diagnosis, Notes filled in)!')
        else alert('⚠️ Could not re-read image. Existing medications kept. Use Edit to correct manually.')
      } else {
        alert('⚠️ Reparse failed: ' + (data.message || 'Server error'))
      }
    } catch(e) { alert('Reparse error: ' + e.message) }
    finally { setReparsing(null) }
  }

  const saveEdit = async (id) => {
    setSaving(true)
    try {
      const { ok, data } = await safeApiFetch(`${API_BASE}/api/prescriptions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ medicationText: editText }),
        headers: { ...authHdr(), 'Content-Type': 'application/json' }
      })
      if (ok) {
        setPrescriptions(prev => prev.map(p => p.id === id ? { ...p, medicationText: editText } : p))
        setEditingId(null)
      } else {
        alert('Save failed: ' + (data.message || 'Unknown error'))
      }
    } catch(e) { alert('Save error: ' + e.message) }
    finally { setSaving(false) }
  }

  const markTaken = (prescriptionId, remId, medicationType, date) => {
    const takenAt = new Date().toISOString()
    setRemStates(prev => {
      const ps = prev[prescriptionId] || {}
      const next = { ...prev, [prescriptionId]: { ...ps, [remId]: { taken: true, takenAt } } }
      saveRS(prescriptionId, next[prescriptionId])
      return next
    })
    safeApiFetch(`${API_BASE}/api/prescription-reminder/taken`, {
      method: 'POST', headers: { ...authHdr(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ remId, taken: true, takenAt })
    }).catch(() => {})
  }

  const undoTaken = (prescriptionId, remId) => {
    setRemStates(prev => {
      const ps = prev[prescriptionId] || {}
      const next = { ...prev, [prescriptionId]: { ...ps, [remId]: { taken: false } } }
      saveRS(prescriptionId, next[prescriptionId])
      return next
    })
    safeApiFetch(`${API_BASE}/api/prescription-reminder/taken`, {
      method: 'POST', headers: { ...authHdr(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ remId, taken: false })
    }).catch(() => {})
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Prescription upload is hidden by default so the panel leads with reminders.
         The "My Prescription" button reveals the upload box on demand. */}
      <button onClick={() => setShowUpload(v => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '.6rem 1rem', borderRadius: 10, background: showUpload ? C.g50 : C.g700, color: showUpload ? C.text : 'white', border: showUpload ? '1px solid ' + C.border : 'none', fontSize: '.83rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
        💊 My Prescription <span style={{ fontSize: '.7rem' }}>{showUpload ? '▲' : '▼'}</span>
      </button>

      {showUpload && (
        <div style={{ background: C.g50, border: '1.5px dashed ' + C.border, borderRadius: 12, padding: '1.4rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '.35rem' }}>💊</div>
          <div style={{ fontWeight: 700, color: C.text, fontSize: '.9rem', marginBottom: '.25rem' }}>Upload Your Prescription</div>
          <div style={{ fontSize: '.75rem', color: C.muted, lineHeight: 1.6, marginBottom: '.9rem' }}>
            Take a photo of your prescription slip. Our bot reads it and sets up daily medication reminders automatically.
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) { scan(e.target.files[0]); e.target.value = '' } }} />
          <button onClick={() => fileRef.current && fileRef.current.click()} disabled={scanning}
            style={{ padding: '.55rem 1.5rem', borderRadius: 9, background: scanning ? C.muted : C.g700, color: 'white', border: 'none', fontSize: '.83rem', fontWeight: 700, cursor: scanning ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
            {scanning ? 'Scanning...' : 'Upload and Scan'}
          </button>
          {scanning && <div style={{ marginTop: '.6rem', fontSize: '.72rem', color: C.g600, fontStyle: 'italic' }}>{scanMsg}</div>}
        </div>
      )}

      {/* WhatsApp Reminder Toggle */}
      <div id="wa-reminder-toggle" style={{ margin: '0 0 .85rem', background: waPref.enabled ? '#F0FFF4' : (waSetup ? '#F0FFF4' : 'white'), border: '1px solid ' + (waPref.enabled || waSetup ? '#6EE7B7' : C.border), borderRadius: 12, padding: '.8rem 1rem' }}>
        {!waSetup ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div style={{ fontSize: '.82rem', fontWeight: 700, color: C.text }}>
                💬 WhatsApp Medication Reminders
              </div>
              <div style={{ fontSize: '.72rem', color: C.muted, marginTop: '.1rem' }}>
                {waPref.enabled ? `Sending to +${waPref.phone} — reminders at 8am, 1pm & 9pm` : 'Get WhatsApp reminders for your medications (8am, 1pm, 9pm)'}
              </div>
            </div>
            {waPref.enabled ? (
              <button onClick={() => saveWaPref(false)} disabled={waSaving}
                style={{ flexShrink: 0, padding: '.3rem .75rem', borderRadius: 7, background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FECACA', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer' }}>
                {waSaving ? 'Stopping…' : 'Stop Reminders'}
              </button>
            ) : (
              <button onClick={() => { setWaSetup(true); setWaChangeNum(false) }}
                style={{ flexShrink: 0, padding: '.3rem .75rem', borderRadius: 7, background: '#25D366', color: 'white', border: 'none', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer' }}>
                Enable
              </button>
            )}
          </div>
        ) : (
          /* waSetup=true: either confirm existing number or enter new one */
          (waPref.phone && !waChangeNum) ? (
            /* Existing number on file — just confirm or change */
            <div>
              <div style={{ fontSize: '.82rem', fontWeight: 700, color: C.text, marginBottom: '.5rem' }}>💬 Enable WhatsApp Reminders</div>
              <div style={{ fontSize: '.78rem', color: C.muted, marginBottom: '.75rem' }}>
                Send reminders to <strong style={{ color: C.text }}>+{waPref.phone}</strong>?
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => saveWaPref(true)} disabled={waSaving}
                  style={{ padding: '.4rem .9rem', borderRadius: 7, background: '#25D366', color: 'white', border: 'none', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                  {waSaving ? 'Enabling…' : 'Yes, use this number'}
                </button>
                <button onClick={() => setWaChangeNum(true)}
                  style={{ padding: '.4rem .7rem', borderRadius: 7, background: C.g50, color: C.text, border: '1px solid ' + C.border, fontSize: '.78rem', cursor: 'pointer' }}>
                  Use a different number
                </button>
                <button onClick={() => setWaSetup(false)}
                  style={{ padding: '.4rem .7rem', borderRadius: 7, background: 'white', color: C.muted, border: '1px solid ' + C.border, fontSize: '.78rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* No saved number, or patient chose to change */
            <div>
              <div style={{ fontSize: '.82rem', fontWeight: 700, color: C.text, marginBottom: '.5rem' }}>💬 Enter your WhatsApp number</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={waCountry} onChange={e => setWaCountry(e.target.value)}
                  style={{ padding: '.4rem .5rem', borderRadius: 7, border: '1px solid ' + C.border, fontSize: '.8rem', fontFamily: "'DM Sans',sans-serif", background: 'white', minWidth: 130 }}>
                  <option value="91">🇮🇳 India (+91)</option>
                  <option value="1">🇺🇸 USA (+1)</option>
                  <option value="44">🇬🇧 UK (+44)</option>
                  <option value="971">🇦🇪 UAE (+971)</option>
                  <option value="966">🇸🇦 Saudi Arabia (+966)</option>
                  <option value="65">🇸🇬 Singapore (+65)</option>
                  <option value="60">🇲🇾 Malaysia (+60)</option>
                  <option value="61">🇦🇺 Australia (+61)</option>
                  <option value="49">🇩🇪 Germany (+49)</option>
                  <option value="33">🇫🇷 France (+33)</option>
                  <option value="81">🇯🇵 Japan (+81)</option>
                  <option value="86">🇨🇳 China (+86)</option>
                </select>
                <input value={waPhone} onChange={e => setWaPhone(e.target.value)} placeholder="Phone number"
                  style={{ flex: 1, minWidth: 140, padding: '.4rem .7rem', borderRadius: 7, border: '1px solid ' + C.border, fontSize: '.8rem', fontFamily: "'DM Sans',sans-serif" }} />
                <button onClick={() => saveWaPref(true)} disabled={waSaving}
                  style={{ padding: '.4rem .9rem', borderRadius: 7, background: '#25D366', color: 'white', border: 'none', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                  {waSaving ? 'Saving…' : 'Confirm'}
                </button>
                <button onClick={() => { setWaChangeNum(false); setWaSetup(false) }}
                  style={{ padding: '.4rem .7rem', borderRadius: 7, background: C.g50, color: C.muted, border: '1px solid ' + C.border, fontSize: '.78rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
              <div style={{ fontSize: '.68rem', color: C.muted, marginTop: '.4rem' }}>Reminders sent via WhatsApp at 8am, 1pm and 9pm daily.</div>
            </div>
          )
        )}
      </div>

      {serverDown && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '.5rem 1rem', marginBottom: '.75rem', fontSize: '.78rem', color: '#92400E', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <span>⏳</span> Server restarting — showing saved data. Reconnecting automatically...
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: C.muted, padding: '1.5rem', fontSize: '.85rem' }}>Loading...</div>
      ) : prescriptions.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.muted, padding: '.5rem', fontSize: '.82rem' }}>No prescriptions uploaded yet.</div>
      ) : prescriptions.map(p => {
        const isEditing   = editingId === p.id
        const startDate   = (p.createdAt || todayISO()).slice(0, 10)
        const reminders   = generateReminders({ id: p.id, date: startDate, medication: p.medicationText, prescription: '' })
        const rxSt        = remStates[p.id] || {}
        const today       = todayISO()
        const todayRems   = reminders.filter(r => r.date === today)
        const takenCnt    = todayRems.filter(r => rxSt[r.id]?.taken).length
        const activeTab   = getActiveTab(p.id)
        const filterDate  = getFilterDate(p.id, reminders)
        const uniqueDates = [...new Set(reminders.map(r => r.date))].sort()
        const dayRems     = reminders.filter(r => r.date === filterDate)
        const missedToday = todayRems.filter(r => getSlotStatus(r.date, r.medicationType, rxSt[r.id]?.taken) === 'missed').length

        return (
          <div key={p.id} style={{ border: '1px solid ' + C.border, borderRadius: 12, overflow: 'hidden', background: 'white', boxShadow: '0 1px 4px rgba(13,34,24,.05)' }}>

            {/* Card Header */}
            <div style={{ padding: '.85rem 1rem', background: C.g50, borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {p.imageFilename && (
                  <img src={photoUrl(p.imageFilename)} alt="rx"
                    style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none' }} />
                )}
                <div>
                  <div style={{ fontWeight: 600, color: C.text, fontSize: '.85rem' }}>
                    Prescription — {new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  <div style={{ fontSize: '.68rem', color: C.muted, marginTop: '.1rem' }}>
                    {reminders.length > 0
                      ? takenCnt + '/' + todayRems.length + ' taken today · ' + reminders.length + ' reminder' + (reminders.length !== 1 ? 's' : '') + ' across ' + uniqueDates.length + ' days'
                      : 'No reminders — check Details tab'}
                  </div>
                </div>
              </div>
              <button onClick={() => del(p.id)}
                style={{ padding: '.2rem .55rem', borderRadius: 6, background: '#FFF0F0', color: C.error, border: '1px solid #FFC9C9', fontSize: '.68rem', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
            </div>

            {/* Tab Bar - matches consultation view */}
            <div style={{ display: 'flex', borderBottom: '1px solid ' + C.border, background: C.g50 }}>
              {[['details','📋 Details'],['prescription','💊 Prescription'],['reminders','⏰ Reminders']].map(([t, label]) => (
                <button key={t} onClick={() => setActiveTab(p.id, t)}
                  style={{ flex: 1, padding: '.65rem .3rem', fontSize: '.72rem', fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", background: 'none', borderBottom: activeTab === t ? '2.5px solid ' + C.g600 : '2.5px solid transparent', color: activeTab === t ? C.g700 : C.muted, transition: 'all .15s' }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Disclaimer — always visible regardless of active tab */}
            <div style={{ background: '#FFF8F0', borderBottom: '1px solid #FBBF7C', padding: '.5rem .85rem', display: 'flex', gap: '.45rem', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '.8rem', flexShrink: 0 }}>⚠️</span>
              <div style={{ fontSize: '.7rem', color: '#92400E', lineHeight: 1.5 }}>
                <strong>Disclaimer:</strong> This prescription was uploaded by the patient from outside Revive. Revive Skin &amp; Hair Experts is not responsible for its accuracy, authenticity, or the medical advice it contains. Please consult your doctor before taking any medication.
              </div>
            </div>

            {/* Details Tab - 3 fields filled by AI from image */}
            {activeTab === 'details' && (
              <div style={{ padding: '1.1rem 1.25rem' }}>
                {/* If all 3 fields are empty, show a prompt to run AI Parse */}
                {!p.doctorName && !p.diagnosis && !p.rxNotes && (
                  <div style={{ background: '#FFF9E6', border: '1px solid #F5CC73', borderRadius: 10, padding: '.7rem 1rem', marginBottom: '.85rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ fontSize: '.78rem', color: '#92400E' }}>Details not extracted yet — click AI Parse to auto-fill Doctor, Specialization, Diagnosis &amp; Notes from your prescription image.</div>
                    <button onClick={() => { reparse(p.id); setActiveTab(p.id, 'details') }} disabled={reparsing === p.id}
                      style={{ flexShrink: 0, padding: '.3rem .75rem', borderRadius: 7, background: reparsing === p.id ? C.g100 : '#78350F', color: 'white', border: 'none', fontSize: '.72rem', fontWeight: 600, cursor: reparsing === p.id ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
                      {reparsing === p.id ? '⏳ Reading…' : '🤖 AI Parse'}
                    </button>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '.85rem', marginBottom: '1rem' }}>
                  {[['Doctor / Clinic', (p.doctorName && p.clinicName) ? p.doctorName + ' · ' + p.clinicName : p.doctorName || p.clinicName || ''], ['Specialization', p.specialization || ''], ['Diagnosis', p.diagnosis || ''], ['Notes', p.rxNotes || '']].map(([label, val]) => (
                    <div key={label} style={{ background: C.g50, border: '1px solid ' + C.border, borderRadius: 10, padding: '.7rem .85rem' }}>
                      <div style={{ fontSize: '.58rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: '.3rem' }}>{label}</div>
                      <div style={{ fontSize: '.82rem', color: val ? C.text : C.muted, fontStyle: val ? 'normal' : 'italic', opacity: val ? 1 : .5 }}>{val || '—'}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200, background: C.g50, border: '1px solid ' + C.border, borderRadius: 10, padding: '.7rem .85rem' }}>
                    <div style={{ fontSize: '.58rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: '.2rem' }}>Source</div>
                    <div style={{ fontSize: '.8rem', color: C.text }}>Self-uploaded · {new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                  </div>
                  {(p.doctorName || p.diagnosis) && (
                    <button onClick={() => { reparse(p.id); setActiveTab(p.id, 'details') }} disabled={reparsing === p.id}
                      style={{ alignSelf: 'center', padding: '.3rem .75rem', borderRadius: 7, background: '#FFF9E6', color: '#B7770D', border: '1px solid #F5CC73', fontSize: '.72rem', fontWeight: 600, cursor: reparsing === p.id ? 'not-allowed' : 'pointer' }}>
                      {reparsing === p.id ? '⏳ Re-reading…' : '🤖 Re-parse'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Prescription Tab - 6 medications as table */}
            {activeTab === 'prescription' && (
              <div style={{ padding: '.85rem 1rem' }}>
                {/* Outside specialization — show disclaimer, block pharmacy */}
                {resolveSpecializationValid(p) === 0 && (
                  <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '.85rem 1rem', marginBottom: '.85rem', display: 'flex', gap: '.6rem', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>🚫</span>
                    <div>
                      <div style={{ fontWeight: 700, color: '#991B1B', fontSize: '.82rem', marginBottom: '.25rem' }}>Outside Our Specialization</div>
                      <div style={{ fontSize: '.75rem', color: '#B91C1C', lineHeight: 1.6 }}>
                        This prescription is from a doctor outside Dermatology, Venereology &amp; Leprosy (DVL). We cannot fulfil pharmacy orders for this prescription at this time. Your medication reminders are still active. Please consult a dermatologist for skin &amp; hair related concerns.
                      </div>
                    </div>
                  </div>
                )}
                {(() => {
                  const rows = parseMedTable(p.medicationText)
                  return rows.length > 0 ? (
                    <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid ' + C.border }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
                        <thead>
                          <tr style={{ background: C.g700 }}>
                            {['#', 'Medication', 'Dosage Pattern', 'Time of Day', 'Duration'].map(h => (
                              <th key={h} style={{ padding: '.5rem .75rem', textAlign: 'left', color: 'white', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? 'white' : C.g50, borderTop: '1px solid ' + C.border }}>
                              <td style={{ padding: '.55rem .75rem', color: C.muted, fontSize: '.72rem', fontWeight: 700 }}>{row.num}</td>
                              <td style={{ padding: '.55rem .75rem', color: C.text, fontWeight: 600, fontSize: '.82rem' }}>{row.name}</td>
                              <td style={{ padding: '.55rem .75rem', color: C.g600, fontFamily: 'monospace', fontSize: '.78rem' }}>{row.timing}</td>
                              <td style={{ padding: '.55rem .75rem', color: C.text, fontSize: '.75rem' }}>{row.slots}</td>
                              <td style={{ padding: '.55rem .75rem', color: C.muted, fontSize: '.75rem', whiteSpace: 'nowrap' }}>{row.duration}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <pre style={{ margin: 0, fontSize: '.8rem', color: p.medicationText ? C.text : C.muted, whiteSpace: 'pre-wrap', lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif", fontStyle: p.medicationText ? 'normal' : 'italic', background: C.g50, border: '1px solid ' + C.border, borderRadius: 8, padding: '.75rem 1rem' }}>
                      {p.medicationText || 'No medication text yet. Click AI Parse or Edit below.'}
                    </pre>
                  )
                })()}
                <div style={{ display: 'flex', gap: 6, marginTop: '.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button onClick={() => reparse(p.id)} disabled={reparsing === p.id}
                    style={{ padding: '.3rem .75rem', borderRadius: 7, background: reparsing === p.id ? C.g100 : '#FFF9E6', color: '#B7770D', border: '1px solid #F5CC73', fontSize: '.72rem', cursor: reparsing === p.id ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                    {reparsing === p.id ? '⏳ Parsing…' : '🤖 AI Parse'}
                  </button>
                  <button onClick={() => { setEditingId(p.id); setEditText(p.medicationText || '') }}
                    style={{ padding: '.3rem .75rem', borderRadius: 7, background: C.g50, color: C.g700, border: '1px solid ' + C.border, fontSize: '.72rem', cursor: 'pointer', fontWeight: 600 }}>✏️ Edit / Correct</button>
                  {(() => {
                    const sv = resolveSpecializationValid(p)
                    if (sv === 0) return <span style={{ fontSize: '.72rem', color: '#B91C1C', fontWeight: 600, marginLeft: 4 }}>🚫 Pharmacy unavailable for this prescription</span>
                    if (sv === 1) return (
                      <button style={{ padding: '.3rem .75rem', borderRadius: 7, background: '#028090', color: 'white', border: 'none', fontSize: '.72rem', cursor: 'pointer', fontWeight: 600 }}
                        onClick={() => {
                          const rows = parseMedTable(p.medicationText)
                          if (p.createdAt) {
                            const start = new Date(p.createdAt); start.setHours(0,0,0,0)
                            const maxDays = rows.reduce((mx, r) => { const m = r.duration && r.duration.match(/(\d+)/); return m ? Math.max(mx, parseInt(m[1])) : mx }, 30)
                            const end = new Date(start); end.setDate(end.getDate() + maxDays)
                            const today = new Date(); today.setHours(0,0,0,0)
                            const fmt = d => d.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
                            if (today > end) { alert(`This prescription expired on ${fmt(end)}.\nPlease consult your doctor for a renewed prescription.`); return }
                            if (today < start) { alert(`This prescription is valid from ${fmt(start)}.\nYou cannot add to cart before the prescription start date.`); return }
                          }
                          const medNames = rows.map(r => r.name).filter(Boolean)
                          const prods = products || []
                          const added = []
                          const unmatched = []
                          medNames.forEach(med => {
                            const kws = medKeywords(med)
                            const match = prods.find(prod => matchesMedication(prod, kws))
                            if (match) {
                              if (!added.find(a => a.id === match.id)) {
                                added.push(match)
                                onAdd && onAdd(match)
                              }
                            } else {
                              unmatched.push(med)
                            }
                          })
                          if (added.length === 0) {
                            navigate('/shop', { state: { rxMeds: medNames } })
                          } else if (unmatched.length > 0) {
                            alert(`Added ${added.length} product(s) to cart.\n\nNot available in our pharmacy:\n• ${unmatched.join('\n• ')}`)
                          }
                        }}>🛒 Add to Cart</button>
                    )
                    return <span style={{ fontSize: '.72rem', color: '#92400E', fontWeight: 600, marginLeft: 4 }}>⚠️ Specialization unverified — pharmacy unavailable</span>
                  })()}
                </div>
                {isEditing && (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
                    onClick={e => { if (e.target === e.currentTarget) setEditingId(null) }}>
                    <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 680, height: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.35)', overflow: 'hidden' }}>
                      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid ' + C.border, background: C.g50, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div>
                          <div style={{ fontWeight: 700, color: C.g900, fontSize: '.9rem' }}>Edit Medication Text</div>
                          <div style={{ fontSize: '.68rem', color: C.muted, marginTop: '.1rem' }}>Format: 1) Medicine Name 1-0-1 for 7 days (morning-afternoon-night)</div>
                        </div>
                        <button onClick={() => setEditingId(null)}
                          style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid ' + C.border, background: 'white', color: C.muted, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                      </div>
                      <textarea value={editText} onChange={e => setEditText(e.target.value)} autoFocus
                        style={{ flex: 1, padding: '1rem 1.25rem', border: 'none', outline: 'none', fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif", color: C.text, lineHeight: 1.8, resize: 'none' }} />
                      <div style={{ padding: '.85rem 1.25rem', borderTop: '1px solid ' + C.border, display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0, background: C.g50 }}>
                        <button onClick={() => setEditingId(null)}
                          style={{ padding: '.5rem 1.1rem', borderRadius: 8, background: 'white', color: C.muted, border: '1px solid ' + C.border, fontSize: '.83rem', cursor: 'pointer' }}>Cancel</button>
                        <button onClick={() => saveEdit(p.id)} disabled={saving}
                          style={{ padding: '.5rem 1.4rem', borderRadius: 8, background: saving ? C.muted : C.g700, color: 'white', border: 'none', fontSize: '.83rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                          {saving ? 'Saving…' : '💾 Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reminders Tab */}
            {activeTab === 'reminders' && (
              <div style={{ padding: '.85rem 1rem' }}>
                {reminders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem', color: C.muted, fontSize: '.82rem' }}>
                    No reminders yet. Go to the <strong>Details</strong> tab and click <strong>AI Parse</strong>, or edit medication text so each line starts with a number (e.g. "1) Paracetamol 1-0-1 for 5 days").
                  </div>
                ) : (
                  <div>
                    <div style={{ background: 'linear-gradient(135deg,' + C.g100 + ',' + C.g50 + ')', border: '1px solid ' + C.border, borderRadius: 10, padding: '.7rem .9rem', marginBottom: '.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.35rem' }}>
                        <div>
                          <div style={{ fontSize: '.56rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em' }}>Today Progress</div>
                          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: C.g800, marginTop: '.1rem' }}>
                            {takenCnt}/{todayRems.length}<span style={{ fontSize: '.7rem', fontWeight: 400, color: C.muted, marginLeft: 5 }}>doses taken</span>
                          </div>
                        </div>
                        {missedToday > 0 && <div style={{ fontSize: '.65rem', fontWeight: 600, color: C.error }}>missed: {missedToday}</div>}
                      </div>
                      {todayRems.length > 0 && (
                        <div style={{ height: 5, background: C.border, borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 999, background: C.g500, width: (takenCnt / todayRems.length * 100) + '%', transition: 'width .4s ease' }} />
                        </div>
                      )}
                    </div>

                    <div style={{ fontSize: '.56rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.35rem' }}>Select Date</div>
                    <div style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 6, marginBottom: '.75rem' }}>
                      {uniqueDates.map(d => {
                        const isToday = d === today, isSel = d === filterDate
                        const cnt  = reminders.filter(r => r.date === d).length
                        const done = reminders.filter(r => r.date === d && rxSt[r.id] && rxSt[r.id].taken).length
                        const miss = reminders.filter(r => r.date === d && getSlotStatus(r.date, r.medicationType, rxSt[r.id] && rxSt[r.id].taken) === 'missed').length
                        const label = new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                        return (
                          <button key={d} onClick={() => setFilter(p.id, d)}
                            style={{ flexShrink: 0, minWidth: 52, padding: '.4rem .35rem', borderRadius: 8, border: '1.5px solid ' + (isSel ? C.g600 : miss > 0 ? '#FCA5A5' : C.border), background: isSel ? C.g700 : miss > 0 ? '#FEF2F2' : isToday ? C.g50 : 'white', color: isSel ? 'white' : miss > 0 ? C.error : C.text, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", textAlign: 'center' }}>
                            <div style={{ fontSize: '.55rem', fontWeight: 700 }}>{label}</div>
                            <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                              {cnt === 0 ? <span style={{ fontSize: '.52rem', color: isSel?'rgba(255,255,255,.4)':C.muted }}>—</span> : <>
                                {miss > 0 && <span style={{ fontSize: '.6rem', fontWeight: 700, color: isSel?'#fca5a5':'#dc2626' }}>✗{miss}</span>}
                                {done > 0 && <span style={{ fontSize: '.6rem', fontWeight: 700, color: isSel?'#86efac':'#16a34a' }}>✓{done}</span>}
                                {done === 0 && miss === 0 && <span style={{ fontSize: '.52rem', color: isSel?'rgba(255,255,255,.5)':C.muted }}>{cnt} due</span>}
                              </>}
                            </div>
                            {isToday && <div style={{ fontSize: '.48rem', color: isSel ? 'rgba(255,255,255,.7)' : C.g600, fontWeight: 600 }}>Today</div>}
                          </button>
                        )
                      })}
                    </div>

                    <div style={{ fontSize: '.58rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.45rem' }}>
                      {filterDate === today ? "Today Reminders" : new Date(filterDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                      <span style={{ marginLeft: 6, color: C.g600, textTransform: 'none', fontWeight: 400, fontSize: '.62rem' }}>
                        ({dayRems.filter(r => rxSt[r.id] && rxSt[r.id].taken).length}/{dayRems.length} taken)
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                      {dayRems.map(r => {
                        const isTaken = !!(rxSt[r.id] && rxSt[r.id].taken)
                        const slot    = getSlotStatus(r.date, r.medicationType, isTaken)
                        const tc      = typeTheme[r.medicationType] || typeTheme.morning
                        const canAct  = slot === 'open' && !isTaken
                        return (
                          <div key={r.id} style={{ borderRadius: 10, border: '1.5px solid ' + (isTaken ? C.g300 : slot === 'missed' ? '#FCA5A5' : slot === 'open' ? tc.border : C.border), background: isTaken ? '#F0FFF4' : slot === 'missed' ? '#FEF2F2' : slot === 'open' ? tc.bg : '#FAFAFA', padding: '.6rem .85rem', display: 'flex', alignItems: 'center', gap: 9 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: isTaken ? C.g100 : slot === 'missed' ? '#FEE2E2' : 'rgba(255,255,255,.8)', border: '1.5px solid ' + (isTaken ? C.g300 : slot === 'missed' ? '#FCA5A5' : tc.border), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.85rem', flexShrink: 0 }}>
                              {typeIcon[r.medicationType]}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: C.text, fontSize: '.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.medicationName}</div>
                              <div style={{ fontSize: '.62rem', color: C.muted }}>
                                {typeLabel[r.medicationType]} - {winInfo[r.medicationType]}
                                {isTaken && rxSt[r.id] && rxSt[r.id].takenAt ? ' - Taken ' + new Date(rxSt[r.id].takenAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              </div>
                            </div>
                            {isTaken
                              ? <button onClick={() => undoTaken(p.id, r.id)} style={{ padding: '.25rem .65rem', borderRadius: 6, border: '1px solid ' + C.g400, background: C.g700, color: 'white', fontSize: '.68rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Undo</button>
                              : canAct
                                ? <button onClick={() => markTaken(p.id, r.id, r.medicationType, r.date)} style={{ padding: '.25rem .65rem', borderRadius: 6, border: 'none', background: tc.btnBg, color: 'white', fontSize: '.68rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>Mark Taken</button>
                                : <span style={{ fontSize: '.65rem', color: slot === 'missed' ? C.error : C.muted, fontStyle: 'italic' }}>{slot === 'missed' ? 'Missed' : 'Upcoming'}</span>
                            }
                          </div>
                        )
                      })}
                    </div>
                    <div style={{ marginTop: '.5rem', fontSize: '.6rem', color: C.muted }}>
                      {reminders.length} total reminders - started {startDate}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
